// components/tabs/DraftTab.jsx
import React, { useState, useRef, useEffect } from 'react';

// Load PDF.js from CDN
async function loadPdfJs() {
  if (window.pdfjsLib) return window.pdfjsLib;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  return window.pdfjsLib;
}

// Extract specific pages from a base64 PDF as images
async function extractPdfPages(base64, startPage, endPage) {
  try {
    const lib = await loadPdfJs();
    const pdfDoc = await lib.getDocument({ data: atob(base64) }).promise;
    const totalPages = pdfDoc.numPages;
    const start = Math.max(1, startPage || 1);
    const end = Math.min(totalPages, endPage || totalPages);
    const pageImages = [];
    for (let p = start; p <= end; p++) {
      const page = await pdfDoc.getPage(p);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width; canvas.height = viewport.height;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      pageImages.push(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
    }
    return { pageImages, pageCount: end - start + 1, isImages: true, totalPages };
  } catch (e) {
    console.error('PDF extraction failed:', e);
    return null;
  }
}

// Extract plain text from a base64 PDF using pdf.js's text layer. Returns
// null if the PDF has little/no extractable text (e.g. a scanned image with
// no OCR text layer) so callers can fall back to keeping the base64 for the
// document/vision API path instead. Used to avoid storing multi-MB base64
// blobs in Supabase for text-based PDFs like proprietary letters, which
// otherwise bloat the penny_drafts JSONB column and can cause save timeouts.
async function extractPdfText(base64) {
  try {
    const lib = await loadPdfJs();
    const pdfDoc = await lib.getDocument({ data: atob(base64) }).promise;
    let text = '';
    for (let p = 1; p <= pdfDoc.numPages; p++) {
      const page = await pdfDoc.getPage(p);
      const content = await page.getTextContent();
      text += content.items.map(i => i.str).join(' ') + '\n\n';
    }
    text = text.trim();
    // Fewer than ~30 characters per page on average almost certainly means
    // this is a scanned/image-only PDF with no real text layer.
    if (text.length < pdfDoc.numPages * 30) return null;
    return text;
  } catch (e) {
    console.error('PDF text extraction failed:', e);
    return null;
  }
}

// Converts a Blob (e.g. from supabase.storage download()) to a data URL
// string via FileReader. Note: this was already called elsewhere in this
// file (generateCmasBrief's storage-download fallback) but was never
// actually defined anywhere — a pre-existing latent bug that would throw
// ReferenceError if that fallback path was ever hit. Defining it here fixes
// both that and the new NCB letter storage path below.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Get total page count of a base64 PDF
async function getPdfPageCount(base64) {
  try {
    const lib = await loadPdfJs();
    const pdfDoc = await lib.getDocument({ data: atob(base64) }).promise;
    return pdfDoc.numPages;
  } catch (e) { return null; }
}

// Auto-split a large PDF into 100-page chunks and return as document blocks
async function splitPdfIntoChunks(base64, name) {
  try {
    const lib = await loadPdfJs();
    const pdfDoc = await lib.getDocument({ data: atob(base64) }).promise;
    const totalPages = pdfDoc.numPages;
    const chunks = [];
    for (let start = 1; start <= totalPages; start += 100) {
      const end = Math.min(start + 99, totalPages);
      const pageImages = [];
      for (let p = start; p <= end; p++) {
        const page = await pdfDoc.getPage(p);
        const viewport = page.getViewport({ scale: 1.2 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width; canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        pageImages.push(canvas.toDataURL('image/jpeg', 0.75).split(',')[1]);
      }
      chunks.push({ start, end, pageImages, label: name + ' (pp. ' + start + '–' + end + ')' });
    }
    return { chunks, totalPages };
  } catch (e) {
    console.error('PDF split failed:', e);
    return null;
  }
}
import { Field, Select, Callout, OutputPanel, UploadZone, LoadRow, renderMd } from '../UI';
import CalEProcurePanel from '../CalEProcurePanel';
import { fmtMoney, LPA_METHODS, LPA_SKIPPED_STAGES, CONTRACT_TERMS } from '../../utils/constants';
import { templatesDb } from '../../utils/store';
import {
  getEffectiveProfile,
  inchesToTwips,
  ptToHalfPt,
  ptToTwips,
} from '../../utils/docTemplate';
import {
  Document, Packer, Paragraph, TextRun, AlignmentType,
  Header, PageNumber,
  Table, TableRow, TableCell, WidthType, ShadingType, VerticalAlign,
  BorderStyle,
} from 'docx';
import supabase from '../../utils/supabaseClient';

// ── Exhibit A .docx builder — uses JSZip (loaded via CDN, no npm needed) ──
// A .docx is a ZIP containing XML files. We build the XML directly.

// ── Exhibit A/B/D .docx builder — uses the `docx` npm library ────────────
// Generates valid Word documents through a typed API instead of hand-rolling
// OOXML strings. All visual formatting (margins, fonts, indents, spacing,
// table colors, page headers, section heading styles) is driven by docProfile
// — the resolved profile from getEffectiveProfile (template → agency → default).

// ── Extract plain text from a .docx file using JSZip (CDN-loaded) ──────────
// Used for the "Upload revised draft" feature — extracts paragraph text from
// the docx, which then becomes the new working draft text. The docx builder
// can regenerate a formatted document from this text + s215 data.
async function extractDocxText(file) {
  const JSZip = window.JSZip;
  if (!JSZip) throw new Error('JSZip not loaded — refresh the page and try again.');
  const buf = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buf);
  const docXml = zip.file('word/document.xml');
  if (!docXml) throw new Error('Invalid .docx file (no word/document.xml).');
  const xmlStr = await docXml.async('string');
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlStr, 'application/xml');
  const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const pNodes = xmlDoc.getElementsByTagNameNS(W_NS, 'p');
  const lines = [];
  for (let i = 0; i < pNodes.length; i++) {
    const tNodes = pNodes[i].getElementsByTagNameNS(W_NS, 't');
    let text = '';
    for (let j = 0; j < tNodes.length; j++) text += tNodes[j].textContent || '';
    lines.push(text.trim());
  }
  // Join paragraphs with newlines, collapse runs of 3+ blank lines to 2
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// Pure helper: parse Penny's body text into a structured paragraph tree.
// Handles BOTH clean hierarchy (1. / A. / 1) / a)) AND concatenated forms
// (1.A / 2.A.1 / 1.A.i). Output is an array of { level, marker, text } objects.
//   level: 'section' | 'subletter' | 'numitem' | 'lowletter' | 'romanitem' | 'body' | 'blank'
//   marker: the visible numbering token ('1.', 'A.', '1)', 'a)', 'ii)', '')
//   text: the prose content
// Builds a "LANGUAGE PREFERENCES" instruction block from an agency's
// docProfile.drafting_voice settings (avoid_phrases / preferred_phrases,
// configured in Agency Admin > Doc Profile). Returns '' when neither list
// has entries, so it's safe to always append to a system prompt.
// Parses JSON out of a model response, tolerating markdown fences and
// trailing prose after the closing brace/bracket (a model adding a stray
// note after otherwise-valid JSON, despite being told not to, is a common
// enough failure mode that a plain JSON.parse is too fragile on its own).
// Handles both object ({...}) and array ([...]) root JSON.
function safeParseJson(raw) {
  const clean = raw.replace(/```json|```/g, '').trim();
  try {
    return JSON.parse(clean);
  } catch (e) {
    const firstBrace = clean.indexOf('{');
    const firstBracket = clean.indexOf('[');
    const usesArray = firstBracket !== -1 && (firstBrace === -1 || firstBracket < firstBrace);
    const start = usesArray ? firstBracket : firstBrace;
    const end = usesArray ? clean.lastIndexOf(']') : clean.lastIndexOf('}');
    if (start !== -1 && end > start) {
      return JSON.parse(clean.slice(start, end + 1));
    }
    throw e;
  }
}

function languageRuleText(dp) {
  const voice = dp?.drafting_voice || {};
  const avoid = Array.isArray(voice.avoid_phrases) ? voice.avoid_phrases.filter(Boolean) : [];
  const prefer = Array.isArray(voice.preferred_phrases) ? voice.preferred_phrases.filter(Boolean) : [];
  let rule = '\n\nWRITING STYLE — apply throughout the ENTIRE draft:' +
    '\n- Write natural, grammatically varied prose, the way an experienced human contract writer would — not the way an AI tends to default to.' +
    '\n- Do NOT overuse em dashes or hyphens as a substitute for commas, periods, or properly restructured sentences. Use a dash only on the rare occasion it genuinely reads better than the alternative — most sentences should use ordinary punctuation (commas, periods, semicolons) instead.' +
    '\n- Prefer complete, well-structured sentences over strings of short dash-separated clauses or fragments.';
  if (avoid.length > 0 || prefer.length > 0) {
    rule += '\n\nLANGUAGE PREFERENCES — apply throughout the ENTIRE draft, every section, no exceptions:';
    if (avoid.length > 0) rule += '\n- NEVER use these words/phrases, even in boilerplate or standard legal phrasing: ' + avoid.join(', ') + '.';
    if (prefer.length > 0) rule += '\n- Use these instead wherever grammatically applicable: ' + prefer.join(', ') + '.';
  }
  return rule;
}

function parseBodyToParagraphs(text, contractTitle = '') {
  const out = [];
  const cleanInline = (s) => String(s || '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
  // Lower-cased contract title for case-insensitive comparison
  const titleLower = String(contractTitle || '').trim().toLowerCase();

  // Standard SOW section titles, indexed by section number as Penny outputs them.
  // Used as a fallback when Penny emits a bare section marker like "1." without
  // a title — happens when the model splits the marker onto its own line.
  // Maps 1→Purpose, 2→Scope, etc. (matches the prompt's required section list.)
  const STANDARD_SECTION_TITLES = {
    1: 'PURPOSE AND BACKGROUND',
    2: 'SCOPE OF SERVICES',
    3: 'DELIVERABLES',
    4: 'PERFORMANCE STANDARDS',
    5: 'ACCEPTANCE CRITERIA',
    6: 'CONTRACTOR RESPONSIBILITIES',
    7: 'STATE RESPONSIBILITIES',
    8: 'TIMELINE AND MILESTONES',
  };

  // ── Lone-marker pre-pass ──────────────────────────────────────────────
  // Penny sometimes emits each numbering marker on its own line, followed
  // by blank lines, with content further below. e.g.:
  //   1.
  //   <blank>
  //   A.
  //   <blank>
  //   The Office of Energy Infrastructure Safety requires...
  //
  // The downstream regex expects "A. text" on a single line. We pre-merge
  // each lone marker line with the next non-blank line, EXCEPT when the next
  // non-blank line is itself a lone marker (so "1." followed by "A." doesn't
  // collapse into "1. A." — those are correctly separate hierarchy levels).
  // The result is a cleaner line list the main parser can handle as if Penny
  // had written things correctly to start with.
  const lonePattern = /^(\d+\.|[A-Z]\.|\d+\)|[a-z]\)|[ivxlcdm]{2,7}\))\s*$/i;
  function preprocessLones(rawLines) {
    const result = [];
    let i = 0;
    while (i < rawLines.length) {
      const t = rawLines[i].trim();
      if (lonePattern.test(t)) {
        // Look ahead to next non-blank line
        let j = i + 1;
        while (j < rawLines.length && !rawLines[j].trim()) j++;
        if (j < rawLines.length) {
          const nextT = rawLines[j].trim();
          if (!lonePattern.test(nextT)) {
            // Merge: marker + space + next-line content
            result.push(t + '  ' + nextT);
            i = j + 1;
            continue;
          }
        }
        // Either no next line, or next line is also a lone marker — pass through
      }
      result.push(rawLines[i]);
      i++;
    }
    return result;
  }

  const lines = preprocessLones(String(text || '').split('\n'));
  // Track whether we've seen the first numbered section yet. Before that point,
  // we aggressively strip echoed title-block content (some templates emit the
  // contract title in ALL CAPS as a subtitle line, for example). After we hit
  // section 1, we let everything through to the regular parser branches.
  let seenFirstSection = false;
  for (let raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    // Collapse consecutive blank lines — at most one blank between content
    // paragraphs. Multiple blanks in Penny's output compound spacing and
    // create visibly large gaps in Exhibit B/D documents.
    if (!trimmed) {
      if (out.length === 0 || out[out.length - 1].level !== 'blank') {
        out.push({ level: 'blank' });
      }
      continue;
    }

    // Skip echoed title-block lines (Penny sometimes still emits these even
    // when told not to). We strip them out so the docx builder's own title
    // block isn't duplicated by Penny's preamble.
    if (/^EXHIBIT [A-D]$/i.test(trimmed)) continue;
    if (/^SCOPE OF WORK$/i.test(trimmed)) continue;
    if (/^BUDGET DETAIL AND PAYMENT PROVISIONS$/i.test(trimmed)) continue;
    if (/^SPECIAL TERMS AND CONDITIONS$/i.test(trimmed)) continue;
    if (/^Agreement No\.?:/i.test(trimmed)) continue;
    if (/^Term:/i.test(trimmed)) continue;

    // Before the first numbered section, strip any standalone ALL-CAPS line
    // (likely Penny echoing the contract title as an unnecessary subtitle).
    // Lets through legitimate content like "1. PURPOSE AND BACKGROUND" because
    // that matches the section_heading regex which has its own ALL-CAPS check
    // AND starts with a number.
    if (!seenFirstSection) {
      // A bare section marker "1." (after preprocessLones couldn't find content
      // to merge it with — e.g. last line of input) still counts as first section.
      if (/^\d+\.\s*$/.test(trimmed) || /^\d+\.\s+[A-Z]/.test(trimmed)) {
        seenFirstSection = true;
        // Fall through to the section-heading parser below
      } else if (/^[A-Z][A-Z\s,&\-/()]+$/.test(trimmed) && trimmed.length < 120) {
        // Pure ALL-CAPS line with no number marker — preamble noise, skip.
        continue;
      } else if (titleLower && trimmed.toLowerCase() === titleLower) {
        // Penny echoed the contract title verbatim as a preamble line — skip,
        // since the docx builder's own title block already includes it.
        continue;
      }
    }

    // ── Bare section marker (no title on same line) ─────────────────────
    // After preprocessLones, this should be rare — only happens if "1." was
    // followed by "A." with no content between. Use the standard-title lookup.
    let bare = trimmed.match(/^(\d+)\.\s*$/);
    if (bare) {
      const n = parseInt(bare[1], 10);
      const title = STANDARD_SECTION_TITLES[n] || '';
      out.push({ level: 'section', marker: bare[1] + '.', text: title });
      continue;
    }

    // ── Concatenated markers (Penny's quirky output) ────────────────────
    // "1.A.i text" or "2.A.1) text" or "1.A The Office..." — split into the
    // deepest level marker, emit any missing parent levels above on demand.

    // Pattern: N.L.D where N is digit, L is capital, D is digit, with text following
    // (e.g. "2.A.1 Contractor shall...")
    let m = trimmed.match(/^(\d+)\.([A-Z])\.(\d+)\.?\s+(.+)$/);
    if (m) {
      out.push({ level: 'numitem', marker: m[3] + ')', text: cleanInline(m[4]) });
      continue;
    }
    // Pattern: N.L (e.g. "1.A The Office..." or "2.B.")
    m = trimmed.match(/^(\d+)\.([A-Z])\.?\s+(.+)$/);
    if (m) {
      out.push({ level: 'subletter', marker: m[2] + '.', text: cleanInline(m[3]) });
      continue;
    }

    // ── Clean hierarchy markers ─────────────────────────────────────────
    // Section heading: "1. PURPOSE AND BACKGROUND" — digit-period, uppercase title, short
    m = trimmed.match(/^(\d+)\.\s+([A-Z][A-Z\s,&\-/()]+)$/);
    if (m && m[2].length < 80) {
      out.push({ level: 'section', marker: m[1] + '.', text: m[2].trim() });
      continue;
    }

    // Sub-letter: "A. text" or "A) text"
    m = trimmed.match(/^([A-Z])[.)]\s+(.+)$/);
    if (m) {
      out.push({ level: 'subletter', marker: m[1] + '.', text: cleanInline(m[2]) });
      continue;
    }

    // Roman numeral item: "ii) text" or "iv. text" (must come BEFORE lower-letter check)
    m = trimmed.match(/^([ivxlcdm]{2,7})[.)]\s+(.+)$/i);
    if (m) {
      out.push({ level: 'romanitem', marker: m[1].toLowerCase() + ')', text: cleanInline(m[2]) });
      continue;
    }

    // Numbered item: "1) text" or "1. text" — but only if title isn't ALL-CAPS
    // (because "1. PURPOSE..." was already matched as section_heading above)
    m = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
    if (m) {
      out.push({ level: 'numitem', marker: m[1] + ')', text: cleanInline(m[2]) });
      continue;
    }

    // Lower-letter item: "a) text" or "a. text"
    m = trimmed.match(/^([a-z])[.)]\s+(.+)$/);
    if (m) {
      out.push({ level: 'lowletter', marker: m[1] + ')', text: cleanInline(m[2]) });
      continue;
    }

    // Bullet point
    m = trimmed.match(/^[-•*]\s+(.+)$/);
    if (m) {
      out.push({ level: 'bullet', marker: '•', text: cleanInline(m[1]) });
      continue;
    }

    // Fallback: plain body paragraph
    out.push({ level: 'body', marker: '', text: cleanInline(trimmed) });
  }
  // Strip leading/trailing blank paragraphs — they add unwanted space
  // at the start/end of the document body.
  while (out.length > 0 && out[0].level === 'blank') out.shift();
  while (out.length > 0 && out[out.length - 1].level === 'blank') out.pop();
  return out;
}

// Exact tokens Penny is instructed to insert (never write the content
// herself) wherever the DVBE incentive scale or SB preference statement
// belongs in the solicitation — these get swapped for deterministic,
// code-generated content so the numbers are always guaranteed correct.
const DVBE_INCENTIVE_TOKEN = '[[DVBE_INCENTIVE_TABLE]]';
const SB_PREFERENCE_TOKEN = '[[SB_PREFERENCE_STATEMENT]]';

// Builds the real Word table for the Standard DGS DVBE incentive scale —
// fixed, deterministic content (SCM Vol 2 DVBE Incentive requirements).
// unitLabel differs by award type: percentage of bid price for low-price
// awards, or percentage of total points for high-score awards — the
// underlying scale (1%-5% tied to proposed DVBE participation) is the same.
function buildDvbeIncentiveTable(isHighScore) {
  const rightHeader = isHighScore ? 'DVBE Incentive (% of Total Points)' : 'DVBE Incentive (% of Bid Price)';
  const rows = [
    ['5% or Over', '5%'],
    ['4% to 4.99%', '4%'],
    ['3% to 3.99%', '3%'],
    ['2% to 2.99%', '2%'],
    ['1% to 1.99%', '1%'],
  ];
  const dvbeCellBorder = { style: BorderStyle.SINGLE, size: 4, color: '000000' };
  const cell = (text, bold) => new TableCell({
    children: [new Paragraph({ children: [new TextRun({ text, bold: !!bold, size: 20 })] })],
    verticalAlign: VerticalAlign.CENTER,
    borders: { top: dvbeCellBorder, bottom: dvbeCellBorder, left: dvbeCellBorder, right: dvbeCellBorder },
    margins: { top: 60, bottom: 60, left: 100, right: 100 },
  });
  const headerRow = new TableRow({
    children: [cell('Confirmed DVBE Participation of:', true), cell(rightHeader, true)],
    tableHeader: true,
  });
  const dataRows = rows.map(([participation, incentive]) => new TableRow({ children: [cell(participation), cell(incentive)] }));
  return [
    new Paragraph({ children: [new TextRun({ text: 'DVBE Incentive Schedule (Standard DGS Scale)', bold: true, size: 22 })], spacing: { before: 120, after: 80 } }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows],
    }),
    new Paragraph({ children: [new TextRun({ text: '', size: 20 })], spacing: { before: 120 } }),
  ];
}

// Builds the fixed, correctly-worded SB preference statement — 5% is set
// by statute (GC §14838(b), 2 CCR §1896.8), never agency-discretionary, so
// this is generated once, consistently, rather than left to the model.
function buildSbPreferenceParagraphs(isHighScore) {
  const text = isHighScore
    ? 'For evaluation purposes, five percent (5%) of the total score awarded to the highest-ranked responsive, responsible bidder not eligible for the Small Business preference shall be added to the total score of each qualified California-certified Small Business (or Microbusiness) bidder, in accordance with Government Code Section 14838(b) and California Code of Regulations, Title 2, Section 1896.8.'
    : 'For evaluation purposes, the net bid price of each qualified California-certified Small Business (or Microbusiness) bidder shall be reduced by five percent (5%) of the lowest responsive, responsible bid price, not to exceed $50,000 per bid, in accordance with Government Code Section 14838(b) and California Code of Regulations, Title 2, Section 1896.8. This preference is used solely for evaluation and award purposes and does not alter the actual price paid to the Contractor.';
  return [
    new Paragraph({ children: [new TextRun({ text: 'Small Business Preference', bold: true, size: 22 })], spacing: { before: 120, after: 60 } }),
    new Paragraph({ children: [new TextRun({ text, size: 20 })], spacing: { after: 120 } }),
  ];
}

async function buildAndDownloadDocx(title, bodyText, profile, s215, contract, assignedCm, exhibit = 'A', docProfile) {
  // ── Profile fallbacks (defensive) ────────────────────────────────────────
  const dp = docProfile || {};
  const dpPage    = dp.page    || { margins_inches: { top: 1, right: 0.6, bottom: 1, left: 0.6 } };
  const dpFont    = dp.font    || { family: 'Arial', body_size_pt: 12, table_size_pt: 10 };
  const dpSpacing = dp.spacing || { paragraph_after_pt: 6 };
  const dpIndents = dp.indents_twips || {};
  const dpHeader  = dp.page_header || { enabled: true, align: 'right', lines: [] };
  const dpTitles  = dp.exhibit_titles || {};
  const dpSecStyle = dp.section_heading_style || {};
  const dpCmTable = dp.cm_table || { enabled: true };
  const dpVoice   = dp.drafting_voice || {};
  const agencyShortName = dpVoice.agency_name_in_text || 'Energy Safety';
  const subLetterBold = dp.subsection_letter_bold === true;

  const ind = {
    section_heading:    dpIndents.section_heading    ?? 36,
    body_under_section: dpIndents.body_under_section ?? 396,
    subsection_letter:  dpIndents.subsection_letter  ?? 396,
    sub_sub_item:       dpIndents.sub_sub_item       ?? 756,
    sub_sub_sub_item:   dpIndents.sub_sub_sub_item   ?? 1116,
    roman_numeral_item: dpIndents.roman_numeral_item ?? 1476,
    bullet:             dpIndents.bullet             ?? 396,
  };

  // Hanging-indent amounts for marker-based paragraph types (lettered/numbered
  // items and bullets). `hanging` pulls the first line back by this amount so
  // the marker (e.g. "a.", "1.", "iv.") sits at ind.X while wrapped
  // continuation lines align under the start of the text, not under the
  // marker. Word's own default hanging indent is 360 twips (0.25in); roman
  // numerals get a touch more room since markers like "iv." run wider.
  const hang = {
    subsection_letter:  dpIndents.hanging_subsection_letter  ?? 360,
    sub_sub_item:       dpIndents.hanging_sub_sub_item       ?? 360,
    sub_sub_sub_item:   dpIndents.hanging_sub_sub_sub_item   ?? 360,
    roman_numeral_item: dpIndents.hanging_roman_numeral_item ?? 450,
    bullet:             dpIndents.hanging_bullet             ?? 270,
  };

  const bodySzHalfPt  = ptToHalfPt(dpFont.body_size_pt  || 12);
  const tableSzHalfPt = ptToHalfPt(dpFont.table_size_pt || 10);
  const fontFamily    = dpFont.family || 'Arial';
  const paraAfterTwips = ptToTwips(dpSpacing.paragraph_after_pt || 6);

  // ── Contact data from contract / s215 / user profile ────────────────────
  const agency  = profile.agencyName     || 'Office of Energy Infrastructure Safety (Energy Safety)';
  const div     = profile.agencyDivision || 'Administrative Services Division';
  const ag      = s215?.agreementNum     || '[AGREEMENT NO.]';
  const st      = s215?.startDate        || '[START DATE]';
  const en      = s215?.endDate          || '[END DATE]';

  const cmName  = assignedCm?.name  || s215?.contactName  || profile.contactName  || '[CM NAME]';
  const cmAddr  = assignedCm?.address || '715 P Street, 15th Floor';
  const cmCity  = 'Sacramento, CA 95814';
  const cmPhone = assignedCm?.phone || s215?.contactPhone || profile.contactPhone || '[PHONE]';
  const cmEmail = assignedCm?.email || s215?.contactEmail || profile.contactEmail || '[EMAIL]';

  // Contractor display name — includes DBA if set on the s215 tab.
  // Format: "Legal Company Name dba DBA Name" when DBA is present.
  // When contractor info hasn't been filled in yet, use "Contractor" as a
  // clean generic stand-in rather than bracket-placeholder text like
  // "[CONTRACTOR]" — those look unprofessional if the document
  // is shared before the contractor is finalized.
  const ctDba = s215?.contractorDba || '';
  const ctOrgName = s215?.contractorName
    ? (ctDba ? s215.contractorName + ' dba ' + ctDba : s215.contractorName)
    : 'Contractor';
  const ctName  = s215?.contractorContactName || s215?.contractorName || 'Contractor Representative';
  const ctAddr  = s215?.contractorAddress || '';
  const ctCity  = [s215?.contractorCity, s215?.contractorState, s215?.contractorZip].filter(Boolean).join(', ') || '';
  const ctPhone = s215?.contractorContactPhone || s215?.contractorPhone || '';
  const ctEmail = s215?.contractorContactEmail || s215?.contractorEmail || '';

  // ── Helper: TextRun with profile defaults ────────────────────────────────
  const run = (text, opts = {}) => new TextRun({
    text: String(text),
    bold: opts.bold,
    italics: opts.italic,
    underline: opts.underline ? {} : undefined,
    size: opts.size || bodySzHalfPt,
    font: fontFamily,
    color: opts.color,
    highlight: opts.highlight,
  });

  // Splits text by [PLACEHOLDER] tokens and returns mixed-styled runs
  function placeholderRuns(text, opts = {}) {
    const parts = String(text).split(/(\[[^\]]+\])/g);
    const runs = [];
    for (const part of parts) {
      if (!part) continue;
      if (/^\[[^\]]+\]$/.test(part)) {
        runs.push(run(part, { ...opts, bold: true, color: 'C07000', highlight: 'yellow' }));
      } else {
        runs.push(run(part, opts));
      }
    }
    return runs;
  }

  // ── Build the docx Paragraph for each parsed item ────────────────────────
  function makeParagraph(item) {
    const after = paraAfterTwips;
    const before = 0;
    switch (item.level) {
      case 'blank':
        return new Paragraph({ children: [run('')], spacing: { before: 0, after: Math.round(after * 0.5) } });

      case 'section': {
        const style = dpSecStyle[exhibit] || { bold: true, underline: false };
        return new Paragraph({
          children: [run(item.marker + ' ' + item.text, {
            bold: style.bold !== false,
            underline: style.underline === true,
          })],
          indent: { left: ind.section_heading },
          spacing: { before: 240, after },
        });
      }

      case 'subletter': {
        const children = [
          run(item.marker, { bold: subLetterBold }),
          run('  '),
          ...placeholderRuns(item.text),
        ];
        return new Paragraph({
          children,
          indent: { left: ind.subsection_letter + hang.subsection_letter, hanging: hang.subsection_letter },
          spacing: { before: 80, after },
        });
      }

      case 'numitem':
        return new Paragraph({
          children: [run(item.marker + '  '), ...placeholderRuns(item.text)],
          indent: { left: ind.sub_sub_item + hang.sub_sub_item, hanging: hang.sub_sub_item },
          spacing: { before: 60, after },
        });

      case 'lowletter':
        return new Paragraph({
          children: [run(item.marker + '  '), ...placeholderRuns(item.text)],
          indent: { left: ind.sub_sub_sub_item + hang.sub_sub_sub_item, hanging: hang.sub_sub_sub_item },
          spacing: { before: 40, after },
        });

      case 'romanitem':
        return new Paragraph({
          children: [run(item.marker + '  '), ...placeholderRuns(item.text)],
          indent: { left: ind.roman_numeral_item + hang.roman_numeral_item, hanging: hang.roman_numeral_item },
          spacing: { before: 40, after },
        });

      case 'bullet':
        return new Paragraph({
          children: [run('•  '), ...placeholderRuns(item.text)],
          indent: { left: ind.bullet + hang.bullet, hanging: hang.bullet },
          spacing: { before: 40, after },
        });

      case 'body':
      default:
        return new Paragraph({
          children: placeholderRuns(item.text),
          indent: { left: ind.body_under_section },
          spacing: { before, after },
        });
    }
  }

  // ── CM Table ─────────────────────────────────────────────────────────────
  const cmFill = dpCmTable.fill_color_hex || 'C5E0B3';
  const cmCols = (dpCmTable.col_widths_twips && dpCmTable.col_widths_twips.length === 3)
    ? dpCmTable.col_widths_twips
    : [2300, 3530, 3530];
  const cmTotal = cmCols.reduce((a, b) => a + b, 0);
  const headerAllFilled   = dpCmTable.header_all_filled !== false;
  const bodyLeftColFilled = dpCmTable.body_left_col_filled !== false;
  const cmAlign = dpCmTable.text_align === 'left' ? AlignmentType.LEFT
                 : dpCmTable.text_align === 'right' ? AlignmentType.RIGHT
                 : AlignmentType.CENTER;
  const cmLabels = dpCmTable.labels || {};
  const cmHeaderLeft  = cmLabels.header_left  || 'Representatives';
  const cmHeaderState = cmLabels.header_state || agencyShortName;
  const cmRowLabels = (cmLabels.rows && cmLabels.rows.length >= 5) ? cmLabels.rows : [
    'Contract Manager:', 'Address:', 'City, State, Zip:', 'Phone Number:', 'E-mail Address:',
  ];

  const cellBorder = { style: BorderStyle.SINGLE, size: 4, color: '000000' };
  function cmCell(text, opts = {}) {
    return new TableCell({
      width: { size: opts.width, type: WidthType.DXA },
      shading: opts.fill ? { type: ShadingType.CLEAR, color: 'auto', fill: opts.fill } : undefined,
      margins: { top: 80, left: 120, bottom: 80, right: 120 },
      verticalAlign: VerticalAlign.CENTER,
      borders: { top: cellBorder, bottom: cellBorder, left: cellBorder, right: cellBorder },
      children: [new Paragraph({
        alignment: cmAlign,
        spacing: { before: 0, after: 0 },
        children: [new TextRun({ text: String(text || ''), bold: opts.bold, size: tableSzHalfPt, font: fontFamily })],
      })],
    });
  }
  const cmHeaderRow = new TableRow({ children: [
    cmCell(cmHeaderLeft,  { width: cmCols[0], bold: true, fill: headerAllFilled ? cmFill : null }),
    cmCell(cmHeaderState, { width: cmCols[1], bold: true, fill: headerAllFilled ? cmFill : null }),
    cmCell(ctOrgName,     { width: cmCols[2], bold: true, fill: headerAllFilled ? cmFill : null }),
  ]});
  function cmBodyRow(label, stateVal, ctVal) {
    return new TableRow({ children: [
      cmCell(label,    { width: cmCols[0], bold: true, fill: bodyLeftColFilled ? cmFill : null }),
      cmCell(stateVal, { width: cmCols[1] }),
      cmCell(ctVal,    { width: cmCols[2] }),
    ]});
  }
  const cmTableObj = new Table({
    width: { size: cmTotal, type: WidthType.DXA },
    columnWidths: cmCols,
    rows: [
      cmHeaderRow,
      cmBodyRow(cmRowLabels[0], cmName,  ctName),
      cmBodyRow(cmRowLabels[1], cmAddr,  ctAddr),
      cmBodyRow(cmRowLabels[2], cmCity,  ctCity),
      cmBodyRow(cmRowLabels[3], cmPhone, ctPhone),
      cmBodyRow(cmRowLabels[4], cmEmail, ctEmail),
    ],
  });

  // ── Exhibit title block ──────────────────────────────────────────────────
  function exhibitTitleBlock() {
    if (exhibit === 'SOL') {
      return [
        new Paragraph({ alignment: AlignmentType.CENTER, children: [run(agency,  { bold: true, size: 26 })], spacing: { after: 0 } }),
        new Paragraph({ alignment: AlignmentType.CENTER, children: [run(div,     { size: 22 })],            spacing: { after: 160 } }),
        new Paragraph({ alignment: AlignmentType.CENTER, children: [run(contract.procMethod || 'SOLICITATION', { bold: true, size: 28 })], spacing: { after: 0 } }),
        new Paragraph({ alignment: AlignmentType.CENTER, children: [run(title, { bold: true, size: 24 })], spacing: { after: 160 } }),
        new Paragraph({ children: [run('Issued: ', { bold: true }), run(new Date().toLocaleDateString('en-US', {year:'numeric',month:'long',day:'numeric'}))], spacing: { after: 0 } }),
        new Paragraph({ children: [run('Due Date: ', { bold: true }), run('[SUBMISSION DEADLINE]')], spacing: { after: 160 } }),
        new Paragraph({ children: [run('')], spacing: { after: 80 } }),
      ];
    }
    const tobj = dpTitles[exhibit] || {};
    const titleText = tobj.title || ('EXHIBIT ' + exhibit);
    const subtitleText = tobj.subtitle || '';
    const titleStyle = dpSecStyle[exhibit] || { bold: true, underline: false };
    // Title block: just "EXHIBIT A" and "SCOPE OF WORK" (or B/D equivalents).
    // The contract title and Agreement No./Term details are intentionally
    // omitted from the title block — they appear in the page header and
    // throughout the body, so duplicating them at the top is unnecessary.
    return [
      new Paragraph({ alignment: AlignmentType.CENTER, children: [run(titleText, { bold: true, size: 28 })], spacing: { after: 0 } }),
      new Paragraph({ alignment: AlignmentType.CENTER, children: [run(subtitleText, { bold: titleStyle.bold !== false, underline: titleStyle.underline === true, size: 24 })], spacing: { after: 240 } }),
    ];
  }

  // ── Exhibit A: Section 2 (Term) + Section 3 (Contract Representatives) ──
  // For Exhibit A, the app inserts two fixed sections between Penny's
  // section 1 (Purpose) and her remaining sections (which get renumbered
  // starting at 4). This ensures every Energy Safety SOW has a Term clause
  // and a Contract Reps table at consistent section numbers.
  //
  // Section 2 — TERM OF PERFORMANCE: hardcoded with conditional approval text.
  //   Above PAAL threshold (Non-IT > $50K, IT > $200K) → requires DGS approval.
  //   At or below threshold → agency self-approves.
  //
  // Section 3 — CONTRACT REPRESENTATIVES: the green CM table plus four
  //   sub-letters of standing language. Agency name (Energy Safety / Sac County
  //   / etc.) flows from agency profile so the language is multi-jurisdiction.
  function exhibitASections23() {
    if (exhibit !== 'A') return [];

    // Determine DGS-approval requirement from agency PAAL thresholds.
    // PAAL values live on doc_template.paal (extracted from the uploaded PAAL
    // document and confirmed by the agency admin). If no PAAL is set, fall back
    // to the standard California Tier 1 defaults: $50K Non-IT Services, $200K IT,
    // DGS as the approver above threshold.
    const budget = parseFloat(contract.budget) || 0;
    const isIT = contract.isIT === 'yes';
    const paal = dp.paal || {};
    const itMax    = Number(paal.it_self_approve_max);
    const nonItMax = Number(paal.non_it_services_self_approve_max);
    // If a field is missing (NaN), use the standard CA default.
    const itThreshold    = Number.isFinite(itMax)    ? itMax    : 200000;
    const nonItThreshold = Number.isFinite(nonItMax) ? nonItMax : 50000;
    const requiresExternalApproval = isIT
      ? budget > itThreshold
      : budget > nonItThreshold;
    const externalApprover = paal.external_approver || 'Department of General Services (DGS)';
    const approver = requiresExternalApproval ? externalApprover : agencyShortName;

    // ── Term clause sub-letters (profile-driven) ────────────────────────
    // The Term section's sub-letters come from doc_template.term_clause.subletters
    // when set (after admin runs the "Review Term Clause" feature in Agency Admin).
    // Otherwise we fall back to the built-in defaults below — same wording the
    // app shipped with originally.
    //
    // Each sub-letter has: { id, text, active }. Active is the only thing the
    // admin toggles UI-side; sub-letters with active=false are skipped.
    // The label (A./B./C.) is re-derived at render time from the position in
    // the filtered list so admins can disable a sub-letter without renumbering.
    //
    // Placeholders supported in `text`:
    //   {start_date}, {end_date}, {approver}, {agency_short_name},
    //   {contract_title}, {contractor_name}
    // {start_date} and {end_date} render with yellow-highlight (placeholder style).
    const DEFAULT_TERM_SUBLETTERS = [
      {
        id: 'commencement',
        text: 'The term of this Contract will commence on {start_date}, or upon approval by {approver}, whichever is later, and will continue through {end_date}.',
        active: true,
      },
      {
        id: 'amendment',
        text: 'Any changes to this Contract will require consent of both parties and may require an amendment to be approved by {approver}.',
        active: true,
      },
    ];
    const termSubletters = (
      Array.isArray(dp.term_clause?.subletters) && dp.term_clause.subletters.length > 0
        ? dp.term_clause.subletters
        : DEFAULT_TERM_SUBLETTERS
    ).filter(s => s.active !== false);

    // Render one sub-letter as a Paragraph with placeholder substitution.
    // {start_date} and {end_date} are highlighted yellow (placeholder style);
    // all other placeholders are substituted as plain text inline.
    function renderTermSubletter(text, label) {
      const tokenRe = /\{(start_date|end_date|approver|agency_short_name|contract_title|contractor_name)\}/g;
      const children = [run(label + '.', { bold: subLetterBold }), run('  ')];
      let lastIdx = 0;
      const src = String(text || '');
      let m;
      while ((m = tokenRe.exec(src)) !== null) {
        if (m.index > lastIdx) children.push(run(src.slice(lastIdx, m.index)));
        const token = m[1];
        if (token === 'start_date')    children.push(run(st, { bold: true, color: 'C07000', highlight: 'yellow' }));
        else if (token === 'end_date') children.push(run(en, { bold: true, color: 'C07000', highlight: 'yellow' }));
        else if (token === 'approver')             children.push(run(approver));
        else if (token === 'agency_short_name')    children.push(run(agencyShortName));
        else if (token === 'contract_title')       children.push(run(contract.title || ''));
        else if (token === 'contractor_name')      children.push(run(ctOrgName));
        lastIdx = tokenRe.lastIndex;
      }
      if (lastIdx < src.length) children.push(run(src.slice(lastIdx)));
      return new Paragraph({
        children,
        indent: { left: ind.subsection_letter },
        spacing: { after: 60 },
      });
    }

    const termSubletterParas = termSubletters.map((s, i) =>
      renderTermSubletter(s.text, String.fromCharCode(65 + i)) // 65 = 'A'
    );

    return [
      // ── Section 2: TERM OF PERFORMANCE ─────────────────────────────────
      new Paragraph({
        children: [run('2. TERM OF PERFORMANCE', { bold: true })],
        indent: { left: ind.section_heading },
        spacing: { before: 240, after: 80 },
      }),
      ...termSubletterParas,

      // ── Section 3: CONTRACT REPRESENTATIVES ────────────────────────────
      new Paragraph({
        children: [run('3. CONTRACT REPRESENTATIVES', { bold: true })],
        indent: { left: ind.section_heading },
        spacing: { before: 240, after: 80 },
      }),
      new Paragraph({
        children: [
          run('A.', { bold: subLetterBold }),
          run('  All inquiries during the term of this Contract will be directed to the Contract Representatives listed below:'),
        ],
        indent: { left: ind.subsection_letter },
        spacing: { after: 120 },
      }),
      cmTableObj,
      new Paragraph({ children: [run('')], spacing: { after: 80 } }),
      new Paragraph({
        children: [
          run('B.', { bold: subLetterBold }),
          run('  ' + agencyShortName + ' reserves the right to identify a Designated Representative in the absence of the ' + agencyShortName + ' Contract Manager.'),
        ],
        indent: { left: ind.subsection_letter },
        spacing: { after: 60 },
      }),
      new Paragraph({
        children: [
          run('C.', { bold: subLetterBold }),
          run('  Any changes in Contractor\u2019s representative must be immediately reported in writing to the ' + agencyShortName + ' Contract Manager.'),
        ],
        indent: { left: ind.subsection_letter },
        spacing: { after: 60 },
      }),
      new Paragraph({
        children: [
          run('D.', { bold: subLetterBold }),
          run('  Changes to Contract Representatives can be made without a formal amendment to this Contract.'),
        ],
        indent: { left: ind.subsection_letter },
        spacing: { after: 160 },
      }),
    ];
  }

  // ── Page Header ──────────────────────────────────────────────────────────
  // Profile may define right-aligned lines including the page count.
  // For B/D, profile inserts an "Exhibit B" / "Exhibit D" line before page count.
  let headerLines = [];
  if (dpHeader.enabled && Array.isArray(dpHeader.lines)) headerLines = [...dpHeader.lines];
  if (headerLines.length === 0) {
    // Sensible default if profile omits the header
    headerLines = ['{contractor_name}', 'Contract: {agreement_num}', 'Page {page} of {total}'];
  }
  // Inject per-exhibit lines just before the page-count line
  {
    const inserts = exhibit === 'B' ? (dpHeader.exhibit_b_inserts || [])
                   : exhibit === 'D' ? (dpHeader.exhibit_d_inserts || [])
                   : [];
    if (inserts.length > 0) {
      const idx = headerLines.findIndex(l => /\{page\}/.test(l));
      if (idx >= 0) headerLines.splice(idx, 0, ...inserts);
    }
  }
  const headerAlign = dpHeader.align === 'center' ? AlignmentType.CENTER
                    : dpHeader.align === 'left'   ? AlignmentType.LEFT
                    : AlignmentType.RIGHT;
  function buildHeaderParagraph(template) {
    // Substitute simple text placeholders; handle {page}/{total} as field codes
    const vars = {
      contractor_name: ctOrgName,
      agreement_num:   ag,
      exhibit:         (exhibit === 'SOL' ? 'Solicitation' : 'Exhibit ' + exhibit),
      exhibit_title:   (dpTitles[exhibit] && dpTitles[exhibit].subtitle) || '',
    };
    let textTmpl = String(template || '')
      .replace(/\{contractor_name\}/g, vars.contractor_name)
      .replace(/\{agreement_num\}/g,   vars.agreement_num)
      .replace(/\{exhibit\}/g,         vars.exhibit)
      .replace(/\{exhibit_title\}/g,   vars.exhibit_title);
    const hasPage = /\{page\}/.test(textTmpl);
    const hasTotal = /\{total\}/.test(textTmpl);
    if (!hasPage && !hasTotal) {
      return new Paragraph({
        alignment: headerAlign,
        spacing: { before: 0, after: 0 },
        children: [new TextRun({ text: textTmpl, size: bodySzHalfPt, font: fontFamily })],
      });
    }
    // Split around the field-code placeholders
    const parts = textTmpl.split(/(\{page\}|\{total\})/g);
    const children = parts.map(part => {
      if (part === '{page}')  return new TextRun({ children: [PageNumber.CURRENT],     size: bodySzHalfPt, font: fontFamily });
      if (part === '{total}') return new TextRun({ children: [PageNumber.TOTAL_PAGES], size: bodySzHalfPt, font: fontFamily });
      if (!part) return null;
      return new TextRun({ text: part, size: bodySzHalfPt, font: fontFamily });
    }).filter(Boolean);
    return new Paragraph({ alignment: headerAlign, spacing: { before: 0, after: 0 }, children });
  }
  const headerParas = headerLines.map(buildHeaderParagraph);
  const pageHeader = new Header({ children: headerParas });

  // ── Assemble document ───────────────────────────────────────────────────
  // Substitute common placeholder tokens in the body text before parsing.
  // These tokens come from agency baseline templates (Exhibit B/D) and from
  // Penny's output when she uses placeholders instead of resolved values.
  // The docx builder resolves them here so the final document has real data.
  let resolvedBody = String(bodyText || '')
    .replace(/\{contractor_name\}/gi, ctOrgName)
    .replace(/\{agency_short_name\}/gi, agencyShortName)
    .replace(/\{agreement_num\}/gi, ag)
    .replace(/\{start_date\}/gi, st)
    .replace(/\{end_date\}/gi, en)
    .replace(/\{contract_title\}/gi, title || '');
  // For Exhibit B/D: agency baselines often use "TBD" as a stand-in for the
  // contractor. Replace any remaining standalone "TBD" with "Contractor".
  // This does NOT apply to Exhibit A (which has its own structure and doesn't
  // use baseline TBDs).
  if (exhibit !== 'A') {
    resolvedBody = resolvedBody.replace(/\bTBD\b/g, 'Contractor');
  }
  const parsed = parseBodyToParagraphs(resolvedBody, title);

  // For Exhibit A, sections 2 (Term) and 3 (Contract Representatives) are
  // inserted by the app between Penny's section 1 and the rest. Penny's
  // sections 2, 3, 4… get renumbered to 4, 5, 6… so the final document has
  // a clean continuous numbering: 1 (Penny: Purpose), 2 (app: Term),
  // 3 (app: Contract Reps), 4 (Penny: Scope), 5 (Penny: Deliverables), etc.
  //
  // We also split Penny's parsed items at the boundary between her section 1
  // and her section 2 so the app's sections 2 and 3 land in the right spot.
  let pennySection1 = parsed;
  let pennySectionsAfter = [];
  let renumberedAfter = [];

  if (exhibit === 'A') {
    // Find index of Penny's first "section" item with marker "2." or later
    let splitIdx = parsed.length;
    for (let i = 0; i < parsed.length; i++) {
      const it = parsed[i];
      if (it.level === 'section') {
        const n = parseInt(String(it.marker).replace('.', ''), 10);
        if (Number.isFinite(n) && n >= 2) { splitIdx = i; break; }
      }
    }
    pennySection1 = parsed.slice(0, splitIdx);
    pennySectionsAfter = parsed.slice(splitIdx);

    // Renumber Penny's section markers by +2
    renumberedAfter = pennySectionsAfter.map(it => {
      if (it.level !== 'section') return it;
      const n = parseInt(String(it.marker).replace('.', ''), 10);
      if (!Number.isFinite(n)) return it;
      return { ...it, marker: (n + 2) + '.' };
    });
  }

  // Solicitation-only: swap Penny's placeholder tokens for deterministic,
  // code-generated content (a real Word table for the DVBE incentive scale,
  // fixed correctly-worded paragraphs for the SB preference) instead of
  // rendering them as regular text — these values never vary, so they
  // shouldn't be freehanded by the model each time.
  const isHighScoreAward = contract.procMethod === 'RFP-Secondary';
  function expandItem(item) {
    const t = (item.text || '').trim();
    if (exhibit === 'SOL' && t.includes(DVBE_INCENTIVE_TOKEN)) return buildDvbeIncentiveTable(isHighScoreAward);
    if (exhibit === 'SOL' && t.includes(SB_PREFERENCE_TOKEN)) return buildSbPreferenceParagraphs(isHighScoreAward);
    return [makeParagraph(item)];
  }

  const section1Paragraphs = pennySection1.flatMap(expandItem);
  // For Exhibit A: afterParagraphs = renumbered sections (4, 5, 6…).
  // For non-A exhibits: afterParagraphs is EMPTY because section1Paragraphs
  // already contains the full parsed body. Using `parsed` here would
  // duplicate the entire document content.
  const afterParagraphs    = (exhibit === 'A' ? renumberedAfter : []).flatMap(expandItem);

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: fontFamily, size: bodySzHalfPt },
          paragraph: { spacing: { after: paraAfterTwips, line: 276 } },
        },
      },
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 }, // 8.5 x 11 inches
          margin: {
            top:    inchesToTwips(dpPage.margins_inches.top    ?? 1),
            right:  inchesToTwips(dpPage.margins_inches.right  ?? 0.6),
            bottom: inchesToTwips(dpPage.margins_inches.bottom ?? 1),
            left:   inchesToTwips(dpPage.margins_inches.left   ?? 0.6),
            header: 720,
            footer: 720,
          },
        },
      },
      headers: dpHeader.enabled ? { default: pageHeader } : undefined,
      children: exhibit === 'A'
        ? [
            ...exhibitTitleBlock(),
            ...section1Paragraphs,        // Penny's section 1 (Purpose)
            ...exhibitASections23(),       // App-inserted sections 2 + 3
            ...afterParagraphs,            // Penny's renumbered sections 4+
          ]
        : [
            ...exhibitTitleBlock(),
            ...section1Paragraphs,        // (same as full body for non-A exhibits)
            ...afterParagraphs,
          ],
    }],
  });

  // ── Pack into blob and trigger download ─────────────────────────────────
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (title || 'Exhibit').replace(/[^a-zA-Z0-9\s-]/g, '').trim().replace(/\s+/g, '_').slice(0, 50) + '.docx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function memoFilename(title, ext) {
  return (title || 'Document').replace(/[^a-zA-Z0-9\s-]/g, '').trim().replace(/\s+/g, '_').slice(0, 60) + '.' + ext;
}

// Lightweight docx builder for simple procurement-file memos (NCB
// Justification, Cost-Reasonableness Documentation) — unlike
// buildAndDownloadDocx, this doesn't need exhibit-specific TBD/contractor-
// token/term-insertion logic, just a title, contract context, and the
// labeled-section body text Penny already produces in plain text.
async function downloadMemoAsDocx(title, bodyText, contract) {
  const paragraphs = [
    new Paragraph({ children: [new TextRun({ text: title, bold: true, size: 28 })], spacing: { after: 160 } }),
    new Paragraph({ children: [new TextRun({ text: 'Contract: ' + (contract?.title || 'N/A'), size: 20 })], spacing: { after: 40 } }),
    new Paragraph({ children: [new TextRun({ text: 'Budget: ' + fmtMoney(contract?.budget), size: 20 })], spacing: { after: 40 } }),
    new Paragraph({ children: [new TextRun({ text: 'Generated: ' + new Date().toLocaleDateString(), size: 20 })], spacing: { after: 240 } }),
  ];
  const blocks = (bodyText || '').split(/\n\s*\n/);
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    // Bold the leading numbered/all-caps section label if present (e.g.
    // "1. BASIS OF COMPARISON — ..."), rest of the paragraph as plain text.
    const headingMatch = trimmed.match(/^(\d+\.\s+[A-Z][A-Z\s/\-]+)([\s\S]*)$/);
    if (headingMatch) {
      paragraphs.push(new Paragraph({ children: [new TextRun({ text: headingMatch[1].trim(), bold: true, size: 22 })], spacing: { before: 160, after: 60 } }));
      const rest = headingMatch[2].trim();
      if (rest) paragraphs.push(new Paragraph({ children: [new TextRun({ text: rest, size: 22 })], spacing: { after: 100 } }));
    } else {
      paragraphs.push(new Paragraph({ children: [new TextRun({ text: trimmed, size: 22 })], spacing: { after: 100 } }));
    }
  }
  const doc = new Document({ sections: [{ children: paragraphs }] });
  const blob = await Packer.toBlob(doc);
  triggerDownload(blob, memoFilename(title, 'docx'));
}

// Lightweight PDF builder for the same memo content, using jsPDF (already
// loaded globally via index.html) with basic text wrapping and pagination.
async function downloadMemoAsPdf(title, bodyText, contract) {
  const jsPDFLib = window.jspdf?.jsPDF || window.jsPDF;
  if (!jsPDFLib) { throw new Error('PDF library not loaded — try refreshing the page.'); }
  const doc = new jsPDFLib({ unit: 'pt', format: 'letter', orientation: 'portrait' });
  const marginX = 54, marginTop = 60, marginBottom = 60, pageWidth = 612, pageHeight = 792;
  const maxWidth = pageWidth - marginX * 2;
  let y = marginTop;

  function checkPageBreak(lineHeight) {
    if (y + lineHeight > pageHeight - marginBottom) { doc.addPage(); y = marginTop; }
  }

  doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text(title, marginX, y); y += 22;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text('Contract: ' + (contract?.title || 'N/A'), marginX, y); y += 14;
  doc.text('Budget: ' + fmtMoney(contract?.budget), marginX, y); y += 14;
  doc.text('Generated: ' + new Date().toLocaleDateString(), marginX, y); y += 26;

  const blocks = (bodyText || '').split(/\n\s*\n/);
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    const headingMatch = trimmed.match(/^(\d+\.\s+[A-Z][A-Z\s/\-]+)([\s\S]*)$/);
    const heading = headingMatch ? headingMatch[1].trim() : null;
    const rest = headingMatch ? headingMatch[2].trim() : trimmed;

    if (heading) {
      checkPageBreak(16);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
      const headingLines = doc.splitTextToSize(heading, maxWidth);
      headingLines.forEach(line => { checkPageBreak(14); doc.text(line, marginX, y); y += 14; });
      y += 4;
    }
    if (rest) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5);
      const bodyLines = doc.splitTextToSize(rest, maxWidth);
      bodyLines.forEach(line => { checkPageBreak(13); doc.text(line, marginX, y); y += 13; });
      y += 10;
    }
  }
  doc.save(memoFilename(title, 'pdf'));
}

// ── Collapsible Card ─────────────────────────────────────────────────────────
function CollapsibleCard({ id, title, subtitle, icon, collapsed, onToggle, children, style, titleStyle, badge }) {
  return (
    <div className="card" style={style}>
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', margin: collapsed ? 0 : '0 0 12px 0' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <div style={{ flex: 1 }}>
            <div className="card-title" style={{ margin: 0, ...titleStyle }}>{title}</div>
            {subtitle && !collapsed && <p style={{ fontSize: 12, color: 'var(--slate-500)', margin: '4px 0 0', lineHeight: 1.5 }}>{subtitle}</p>}
          </div>
          {badge && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--green-100)', color: 'var(--green-700)', border: '1px solid var(--green-200)', flexShrink: 0 }}>{badge}</span>}
        </div>
        <div style={{ fontSize: 14, color: 'var(--slate-400)', marginLeft: 12, transition: 'transform .2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', flexShrink: 0 }}>▼</div>
      </div>
      {!collapsed && children}
    </div>
  );
}

// ── Exhibit Status System ─────────────────────────────────────────────────────
const EXHIBIT_STATUSES = {
  draft:            { label: 'Draft',            color: 'var(--slate-500)', bg: 'var(--slate-50)',    border: 'var(--slate-200)', icon: '📝' },
  ready_for_review: { label: 'Ready for Review', color: 'var(--blue-600)',  bg: 'var(--blue-50)',     border: 'var(--blue-200)',  icon: '📋' },
  in_review:        { label: 'In Review',        color: 'var(--purple-600)',bg: 'var(--purple-50)',   border: 'var(--purple-200)',icon: '⏳' },
  approved:         { label: 'Approved',         color: 'var(--green-700)', bg: 'var(--green-50)',    border: 'var(--green-200)', icon: '✅' },
  needs_revision:   { label: 'Needs Revision',   color: 'var(--orange-700)',bg: 'var(--orange-50)',   border: 'var(--orange-200)',icon: '⚠' },
};

function ExhibitStatusBadge({ status }) {
  const s = EXHIBIT_STATUSES[status || 'draft'];
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: s.bg, color: s.color, border: '1px solid ' + s.border, whiteSpace: 'nowrap' }}>
      {s.icon} {s.label}
    </span>
  );
}

export default function DraftTab({
  contract, updateC, s215, profile, library, loadedRef, loadedSam, loadedTotal,
  apiKey, callAI, uploadToAnthropicFiles, saveDoc, toast, cms = [], templates = [], onScmRefreshReady,
  agencyDocTemplate = null, currentUserId = null,
}) {
  // Resolve assigned CM from roster
  const assignedCm = contract.assignedCmId ? cms.find(c => c.id === contract.assignedCmId) : null;
  // SOW Studio state — new 2-step flow
  const sowStep = contract.pennyDrafts?.sowStep || 1;
  const setSowStep = (v) => updateC({ pennyDrafts: { sowStep: v } });
  // Which candidate ('penny' draft or an 'upload') is currently the one
  // moving forward through the review/finalize workflow. Both candidates
  // can exist at once — this is just a pointer to which one is active,
  // never a destructive choice. Switching it never discards the other.
  const sowActiveSource = contract.pennyDrafts?.sowActiveSource || null;
  const setSowActiveSource = (v) => updateC({ pennyDrafts: { sowActiveSource: v } });
  const [sowShowUpload, setSowShowUpload] = useState(false);

  // Called whenever a new candidate (a fresh Penny draft, or a fresh
  // upload) becomes available. If the other candidate already exists and
  // is currently active, this asks before switching — nothing is ever
  // silently discarded or silently left stale either.
  function offerSwitchToNewCandidate(newSource, newLabel) {
    const otherActive = sowActiveSource && sowActiveSource !== newSource;
    if (!otherActive) {
      setSowActiveSource(newSource);
      return;
    }
    const proceed = window.confirm(
      'You\'re currently working from ' + (sowActiveSource === 'penny' ? 'Penny\'s draft' : 'your uploaded document') +
      '. Switch to the ' + newLabel + ' you just created?\n\nNothing is lost either way — you can switch back at any time.'
    );
    if (proceed) setSowActiveSource(newSource);
  }

  // Resolve the effective doc-formatting profile for this draft. Precedence:
  // matched template > agency doc_template > APP_DEFAULT_PROFILE.
  const resolveProfile = (exhibit) => getEffectiveProfile({
    contractType: contract?.type || null,
    exhibit,
    templates,
    agencyProfile: agencyDocTemplate,
    currentUserId,
  }).profile;

  // Which exhibit's Current Working Versions row currently has the
  // "select from existing documents" picker open (null = none open).
  const [docPickerFor, setDocPickerFor] = useState(null);

  // Documents available to pick as a Final Version directly, without
  // re-uploading — pulled from general attachments and reference docs
  // already on the contract.
  function getPickableDocuments() {
    const fromAttachments = (contract.general_attachments || []).map(d => ({
      id: 'att_' + d.id, name: d.name, source: 'general_attachments',
      base64: d.base64 || null, content: d.isText ? d.content : null, size: d.size,
    }));
    const fromRefDocs = (contract.referenceDocs || []).map(d => ({
      id: 'ref_' + d.id, name: d.name, source: 'referenceDocs',
      base64: d.base64 || null, content: null, size: d.size,
    }));
    return [...fromAttachments, ...fromRefDocs].filter(d => d.base64 || d.content);
  }
  function promotePickedDocument(exLabel, doc) {
    const final = doc.content
      ? { source: 'user_uploaded', content: doc.content, fileName: doc.name, uploadedAt: new Date().toISOString() }
      : { source: 'user_uploaded', fileName: doc.name, size: doc.size, base64: doc.base64, uploadedAt: new Date().toISOString() };
    updateC({ pennyDrafts: { ['ex' + exLabel + 'Final']: final } });
    setDocPickerFor(null);
    toast.success('"' + doc.name + '" set as the Final Version for Exhibit ' + exLabel + '.');
  }

  const [collapsed, setCollapsed] = useState({
    refDocs: true,
    clarifying: true,
    cmas: !contract.pennyDrafts?.cmasDoc, // open if no doc yet, collapsed if already uploaded
    cmasQuotes: true,
    finalVersions: true,
    pennyDrafts: true,
    procMethodRec: true,
    exhibitA: true,
    exhibitB: true,
    exhibitD: true,
    completeReview: true,
    postReview: true,
    soleSourceNcb: false,
  });
  const toggleCard = (key) => setCollapsed(p => ({ ...p, [key]: !p[key] }));
  const [clarifyLoading, setClarifyLoading] = useState(false);

  // Per-session cache of Anthropic Files API IDs for templates.
  // Keyed by template.id → { fileId, uploadedAt }. Avoids re-uploading the same
  // PDF for B/D analyses run back-to-back. Files API IDs are valid for 28 days
  // server-side; we only re-upload if the in-memory entry is missing.
  const templateFileIdCacheRef = useRef({});

  // triggerScmRefresh is handled in App.jsx so it works from any tab
  // clarifyingQuestions is stored as { requestor: [{id,text,checked}],
  // analyst: [{id,text,status,response}] } — itemized so the UI can support
  // per-question selection (requestor tab) and per-note status/response
  // (analyst tab) without asking Penny to track numbering across edits;
  // the app renumbers whatever's checked/relevant in plain JS instead.
  // Backward compatible with both older plain-string formats.
  const rawClarify = contract.pennyDrafts?.clarifyingQuestions;
  let clarifyQuestions;
  if (!rawClarify) {
    clarifyQuestions = { requestor: [], analyst: [] };
  } else if (typeof rawClarify === 'string') {
    clarifyQuestions = { requestor: rawClarify ? [{ id: 'legacy-r', text: rawClarify, checked: true }] : [], analyst: [] };
  } else if (typeof rawClarify.requestor === 'string' || typeof rawClarify.analyst === 'string') {
    clarifyQuestions = {
      requestor: rawClarify.requestor ? [{ id: 'legacy-r', text: rawClarify.requestor, checked: true }] : [],
      analyst: rawClarify.analyst ? [{ id: 'legacy-a', text: rawClarify.analyst, status: 'not_reviewed', response: '' }] : [],
    };
  } else {
    clarifyQuestions = { requestor: rawClarify.requestor || [], analyst: rawClarify.analyst || [] };
  }
  const setClarifyQuestions = (v) => updateC({ pennyDrafts: { clarifyingQuestions: v } });
  const [clarifyCopied, setClarifyCopied] = useState(false);
  const [clarifyTab, setClarifyTab] = useState('requestor');
  const [scmLoading, setScmLoading] = useState(false);

  function toggleRequestorChecked(id) {
    setClarifyQuestions({ ...clarifyQuestions, requestor: clarifyQuestions.requestor.map(q => q.id === id ? { ...q, checked: !q.checked } : q) });
  }
  function setAnalystStatus(id, status) {
    setClarifyQuestions({ ...clarifyQuestions, analyst: clarifyQuestions.analyst.map(n => n.id === id ? { ...n, status } : n) });
  }
  function setAnalystResponse(id, response) {
    setClarifyQuestions({ ...clarifyQuestions, analyst: clarifyQuestions.analyst.map(n => n.id === id ? { ...n, response, status: response.trim() ? 'responded' : (n.status === 'responded' ? 'not_reviewed' : n.status) } : n) });
  }

  // ── Replace a single requestor question — cascading reveal state ──────
  const [replacingId, setReplacingId] = useState(null);   // which question row has its replace controls open
  const [replaceMode, setReplaceMode] = useState(null);    // 'importance' | 'topic' | null
  const [replaceTopic, setReplaceTopic] = useState('');
  const [replaceLoading, setReplaceLoading] = useState(false);

  function openReplace(id) {
    // Toggle: clicking the same row's replace control again closes it
    if (replacingId === id) { setReplacingId(null); setReplaceMode(null); setReplaceTopic(''); }
    else { setReplacingId(id); setReplaceMode(null); setReplaceTopic(''); }
  }

  async function regenerateRequestorQuestion(id, mode, topic) {
    setReplaceLoading(true);
    try {
      const scmBrief = await getScmContext();
      const otherQuestions = clarifyQuestions.requestor.filter(q => q.id !== id).map(q => q.text);
      const existingList = otherQuestions.length ? otherQuestions.map((t, i) => (i + 1) + '. ' + t).join('\n') : '(none yet)';
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Budget: ' + fmtMoney(contract.budget),
        'Category: ' + (contract.category || 'Professional Services'),
        'Description: ' + (contract.description || 'Not provided'),
      ].join('\n');

      const sys = mode === 'topic'
        ? 'You are Penny, the GoldenScope contracting assistant. Draft ONE new clarifying question to send to the contract requestor, focused specifically on this topic/subject: "' + topic + '". Base it on the contract context and SCM brief below, and do NOT duplicate any question already in the list. Plain, non-technical language — no SCM citations, this is for a non-expert requestor.\n\nSCM BRIEF:\n' + (scmBrief || 'Apply standard SCM requirements.') + '\n\nALREADY-ASKED QUESTIONS (do not duplicate):\n' + existingList + languageRuleText(resolveProfile('CLARIFY')) + '\n\nReturn ONLY the question text itself — no numbering, no quotes, no preamble.'
        : 'You are Penny, the GoldenScope contracting assistant. Review the contract context and SCM brief below, and identify the SINGLE MOST IMPORTANT clarifying question that should be asked of the requestor but is NOT already covered by the existing questions list. Plain, non-technical language — no SCM citations.\n\nSCM BRIEF:\n' + (scmBrief || 'Apply standard SCM requirements.') + '\n\nALREADY-ASKED QUESTIONS (do not duplicate):\n' + existingList + languageRuleText(resolveProfile('CLARIFY')) + '\n\nReturn ONLY the question text itself — no numbering, no quotes, no preamble.';

      const text = await callAI(apiKey, {
        system: sys, model: 'claude-haiku-4-5-20251001', max_tokens: 2000,
        messages: [{ role: 'user', content: [{ type: 'text', text: contractCtx }] }],
        tag: 'clarify_replace_question',
        contract_id: contract.id,
      });
      const newText = text.trim().replace(/^["']|["']$/g, '');
      setClarifyQuestions({ ...clarifyQuestions, requestor: clarifyQuestions.requestor.map(q => q.id === id ? { ...q, text: newText, checked: true } : q) });
      setReplacingId(null); setReplaceMode(null); setReplaceTopic('');
      toast.success('Question replaced');
    } catch (e) {
      toast.error('Could not generate replacement: ' + e.message);
    } finally {
      setReplaceLoading(false);
    }
  }

  // Parses Penny's JSON output into the itemized shape above.
  function parseClarifyOutput(raw) {
    const parsed = safeParseJson(raw);
    return {
      requestor: (parsed.requestorQuestions || []).map((text, i) => ({ id: 'r' + i, text, checked: true })),
      analyst: (parsed.analystNotes || []).map((text, i) => ({ id: 'a' + i, text, status: 'not_reviewed', response: '' })),
    };
  }

  // Formats the itemized clarify data into prompt-ready text — only checked
  // requestor questions are included (unchecked ones were deliberately
  // excluded by the analyst), and analyst notes show their status/response
  // so Penny knows what's already resolved vs. still open.
  function formatClarifyForPrompt() {
    const reqLines = clarifyQuestions.requestor.filter(q => q.checked).map((q, i) => (i + 1) + '. ' + q.text);
    const analystLines = clarifyQuestions.analyst.map((n, i) => {
      const statusLabel = n.status === 'not_applicable' ? '[Marked not applicable by analyst]'
        : n.status === 'responded' ? '[Analyst response: ' + (n.response || '').trim() + ']'
        : '[Not yet reviewed by analyst]';
      return (i + 1) + '. ' + n.text + ' ' + statusLabel;
    });
    let out = '';
    if (reqLines.length) out += 'Requestor Questions (checked/selected only):\n' + reqLines.join('\n') + '\n';
    if (analystLines.length) out += (out ? '\n' : '') + 'Analyst Notes (with review status):\n' + analystLines.join('\n');
    return out;
  }

  // SCM context cache — keyed by procMethod+isIT, never expires (analyst can manually refresh)
  const scmContextCache = contract.pennyDrafts?.scmContext || {};
  const scmCacheKey = (contract.procMethod || 'unspecified') + '_' + (contract.isIT || 'no');
  const cachedScm = scmContextCache[scmCacheKey];
  const scmCacheAge = cachedScm?.generatedAt
    ? Math.floor((Date.now() - new Date(cachedScm.generatedAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const scmCacheValid = !!cachedScm;

  // ── CMAS document and brief ──────────────────────────────────────────────
  const isCMAS = LPA_METHODS.has(contract.procMethod);
  const lpaLabel = contract.procMethod === 'CALNET' ? 'CALNET' : contract.procMethod === 'SLP' ? 'SLP' : contract.procMethod === 'LPA-Other' ? 'LPA' : 'CMAS';
  const lpaGtcLabel = contract.procMethod === 'CALNET' ? 'CALNET Master Agreement Terms' : contract.procMethod === 'SLP' ? 'SLP License Terms' : contract.procMethod === 'LPA-Other' ? 'Master Agreement Terms' : 'CMAS GTC';
  const cmasDoc = contract.pennyDrafts?.cmasDoc || null;
  const cmasContext = contract.pennyDrafts?.cmasContext || null;
  const [cmasLoading, setCmasLoading] = useState(false);
  const [quoteAnalyzing, setQuoteAnalyzing] = useState(false);
  const cmasFileRef = useRef(null);
  const quoteFileRef = useRef(null);

  function setCmasDoc(v) { updateC({ pennyDrafts: { cmasDoc: v } }); }
  function setCmasContext(v) { updateC({ pennyDrafts: { cmasContext: v } }); }

  async function uploadCmasDoc(file) {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      let storagePath = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && contract?.id) {
          const path = user.id + '/' + contract.id + '/cmas/' + file.name.replace(/\s+/g, '_');
          const bytes = atob(base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          const { error } = await supabase.storage.from('exhibit-files').upload(path, blob, { upsert: true });
          if (!error) storagePath = path;
          else console.warn('CMAS storage upload failed, using base64 fallback');
        }
      } catch (e) { console.warn('CMAS upload error:', e.message); }
      setCmasDoc({
        fileName: file.name, size: file.size,
        storagePath, base64: storagePath ? null : base64,
        uploadedAt: new Date().toISOString(),
      });
      toast.success(file.name + ' uploaded — run "Analyze Document" to generate Penny\'s brief');
    };
    reader.readAsDataURL(file);
  }

  async function generateCmasBrief() {
    if (!cmasDoc) return;
    setCmasLoading(true);
    try {
      let base64 = cmasDoc.base64;
      if (!base64 && cmasDoc.storagePath) {
        const { data } = await supabase.storage.from('exhibit-files').download(cmasDoc.storagePath);
        if (data) { const full = await blobToBase64(data); base64 = full.split(',')[1]; }
      }
      if (!base64) { toast.error('Could not load LPA document'); return; }

      const pages = await getPdfPageCount(base64);
      const userContent = [];
      if (pages <= 60) {
        userContent.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } });
      } else {
        const chunks = await splitPdfIntoChunks(base64, cmasDoc.fileName);
        chunks.slice(0, 3).forEach(chunk => chunk.pageImages.forEach(img => userContent.push({ type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: img } })));
      }
      const lpaType = contract.procMethod === 'CALNET' ? 'CALNET (California Network)' : contract.procMethod === 'SLP' ? 'SLP (Software Licensing Program)' : contract.procMethod === 'LPA-Other' ? 'Leveraged Procurement Agreement (LPA)' : 'CMAS (California Multiple Award Schedules)';
      userContent.push({ type: 'text', text: 'Analyze this ' + lpaType + ' master agreement / schedule document. Return a structured brief covering:\n1. Vehicle type, name, number, and contract holder (confirm whether this is CMAS, CALNET, SLP, or another LPA)\n2. Allowable service/product categories (and SINs or schedule categories if applicable)\n3. Contract period of performance and any renewal options\n4. Ordering procedures specific to this vehicle\n5. Price list basis and how to document price reasonableness\n6. Minimum/maximum order thresholds\n7. Required documentation for an order (quote requirements, vendor count, SB/DVBE, STD 213/215)\n8. Any restrictions, special conditions, or mandatory flow-down clauses\n\nBe specific and cite page numbers where possible. This brief will be used by a California state procurement analyst to guide a ' + lpaType + ' procurement order.' });

      const raw = await callAI(apiKey, {
        system: 'You are Penny, a California state procurement specialist built into GoldenScope. You are analyzing a Leveraged Procurement Agreement (LPA) or master agreement document — this could be a CMAS schedule, CALNET contract, SLP agreement, or other statewide vehicle. Help the analyst understand the ordering requirements, allowable categories, and procedures specific to this vehicle. Be specific, practical, and cite page/section numbers. Write in a professional but approachable tone. This brief will persist with the contract and be referenced throughout the procurement.',
        messages: [{ role: 'user', content: userContent }],
        tag: 'lpa_brief',
        contract_id: contract.id,
        max_tokens: 8000,
      });
      setCmasContext({ brief: raw, generatedAt: new Date().toISOString(), fileName: cmasDoc.fileName });
      toast.success('✦ ' + lpaLabel + ' brief generated — Penny is ready to guide this procurement');
    } catch (e) {
      toast.error('CMAS analysis failed: ' + e.message);
    } finally {
      setCmasLoading(false);
    }
  }

  async function analyzeQuoteDocs(files) {
    if (!files.length) return;
    if (files.length > 10) {
      toast.warn('Maximum 10 quote documents at a time — only the first 10 will be analyzed.');
    }
    setQuoteAnalyzing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const cmasBrief = cmasContext?.brief ? '\n\nCMAS Schedule Brief:\n' + cmasContext.brief.slice(0, 800) : '';

      // Process each quote PDF INDIVIDUALLY — one Penny call per file.
      // This avoids the combined page limit entirely (each call has one
      // document, typically 5-20 pages) and gives Penny better focus on
      // each vendor's quote. Results are aggregated at the end.
      const allParsed = [];
      const skipped = [];

      const fileList = Array.from(files).slice(0, 10);
      const fileTotal = fileList.length;
      for (let fi = 0; fi < fileTotal; fi++) {
        const file = fileList[fi];
        const fileIndex = fi + 1;
        toast.info('📄 Analyzing quote ' + fileIndex + '/' + fileTotal + ': ' + file.name + '…');
        console.log('[CMAS per-file] Processing file', fileIndex, 'of', fileTotal, ':', file.name);
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result.split(',')[1]);
          r.onerror = rej;
          r.readAsDataURL(file);
        });

        // Upload to Supabase Storage for persistence
        if (user && contract?.id) {
          try {
            const path = user.id + '/' + contract.id + '/cmas/quotes/' + file.name.replace(/\s+/g, '_');
            const bytes = atob(b64);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            const blob = new Blob([arr], { type: 'application/pdf' });
            await supabase.storage.from('exhibit-files').upload(path, blob, { upsert: true });
          } catch (e) { console.warn('Quote file upload error:', e.message); }
        }

        // Send this single PDF inline as base64 — since each quote is
        // processed in its own Penny call, there's only one document per
        // request, so the 100-page limit applies per-file, not combined.
        // Check page count first and handle over-100-page files gracefully.
        try {
          const pageCount = await getPdfPageCount(b64);
          console.log('[CMAS per-file]', file.name, ':', pageCount, 'pages');

          if (pageCount && pageCount > 100) {
            // Over 100 pages — convert first 80 pages to images so Penny
            // can still read the pricing sections. Most 100+ page "quotes"
            // are full proposal packages; the pricing is usually in the
            // first 20-40 pages. We send 80 to be safe.
            toast.info(file.name + ' is ' + pageCount + ' pages — extracting first 80 pages as images…');
            const extracted = await extractPdfPages(b64, 1, Math.min(80, pageCount));
            if (!extracted?.pageImages?.length) {
              skipped.push(file.name + ' (' + pageCount + ' pages — could not extract images)');
            } else {
              const imgContent = extracted.pageImages.map(img => ({
                type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: img },
              }));
              const raw = await callAI(apiKey, {
                system: 'You are Penny, a California state procurement analyst. Extract vendor quote data from the provided document pages and return structured JSON only.',
                messages: [{ role: 'user', content: [
                  ...imgContent,
                  { type: 'text', text: `Extract quote information from these page images of a vendor quote document for a ${lpaLabel} procurement. Return ONLY a JSON array.

Each object must have these fields:
- vendor: vendor company name
- cmasNum: CMAS or LPA contract number if found (format like 4-XX-XX-XXXX), empty string if not found
- amount: quoted total amount as a string (e.g. "$12,500.00"), empty string if not clear
- sb: true if the vendor is identified as a Small Business, false otherwise
- dvbe: true if the vendor is identified as DVBE certified, false otherwise
- notes: 1 sentence of key notes (delivery timeframe, hourly rate, conditions, etc.), empty string if nothing notable

Document: ${file.name} (${pageCount} pages, first ${extracted.pageImages.length} shown)${cmasBrief}

Return ONLY a valid JSON array. No explanation, no markdown.` },
                ] }],
                tag: 'cmas_quote_extraction',
                contract_id: contract.id,
                max_tokens: 3000,
              });
              const parsed = safeParseJson(raw);
              if (Array.isArray(parsed)) allParsed.push(...parsed);
            }
          } else {
            // Under 100 pages (or page count unknown) — send as inline PDF
            const raw = await callAI(apiKey, {
              system: 'You are Penny, a California state procurement analyst. Extract vendor quote data from the provided document and return structured JSON only.',
              messages: [{ role: 'user', content: [
                { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: b64 } },
                { type: 'text', text: `Extract quote information from this vendor quote document for a ${lpaLabel} procurement. Return ONLY a JSON array (usually 1 object, but could be multiple if the document contains quotes from multiple vendors).

Each object must have these fields:
- vendor: vendor company name
- cmasNum: CMAS or LPA contract number if found (format like 4-XX-XX-XXXX), empty string if not found
- amount: quoted total amount as a string (e.g. "$12,500.00"), empty string if not clear
- sb: true if the vendor is identified as a Small Business, false otherwise
- dvbe: true if the vendor is identified as DVBE certified, false otherwise
- notes: 1 sentence of key notes (delivery timeframe, hourly rate, conditions, etc.), empty string if nothing notable

Document: ${file.name}${pageCount ? ' (' + pageCount + ' pages)' : ''}${cmasBrief}

Return ONLY a valid JSON array. No explanation, no markdown. If the document is an email chain, extract the most recent vendor quote from it.` },
              ] }],
              tag: 'cmas_quote_extraction',
              contract_id: contract.id,
              max_tokens: 3000,
            });
            const parsed = safeParseJson(raw);
            if (Array.isArray(parsed)) allParsed.push(...parsed);
          }
        } catch (e) {
          console.warn('Quote extraction failed for ' + file.name + ':', e.message);
          skipped.push(file.name);
        }
      }

      if (skipped.length > 0) {
        toast.warn(skipped.length + ' file' + (skipped.length > 1 ? 's' : '') + ' could not be analyzed: ' + skipped.join(', '));
      }

      if (allParsed.length > 0) {
        // Merge with existing quotes — fill empty rows first, then append
        const currentQuotes = [...(contract.pennyDrafts?.cmasQuotes || [
          { vendor: '', cmasNum: '', amount: '', sb: false, dvbe: false, notes: '', selected: false },
          { vendor: '', cmasNum: '', amount: '', sb: false, dvbe: false, notes: '', selected: false },
          { vendor: '', cmasNum: '', amount: '', sb: false, dvbe: false, notes: '', selected: false },
        ])];

        let qi = 0;
        for (const q of allParsed) {
          const newEntry = { vendor: q.vendor || '', cmasNum: q.cmasNum || '', amount: q.amount || '', sb: !!q.sb, dvbe: !!q.dvbe, notes: q.notes || '', selected: false };
          const emptyIdx = currentQuotes.findIndex((r, i) => i >= qi && !r.vendor?.trim());
          if (emptyIdx !== -1) {
            currentQuotes[emptyIdx] = newEntry;
            qi = emptyIdx + 1;
          } else {
            currentQuotes.push(newEntry);
          }
        }

        updateC({ pennyDrafts: { cmasQuotes: currentQuotes } });
        toast.success('✦ Penny extracted ' + allParsed.length + ' quote' + (allParsed.length !== 1 ? 's' : '') + ' from ' + (files.length - skipped.length) + ' document' + ((files.length - skipped.length) !== 1 ? 's' : '') + ' — review and confirm');
      } else {
        toast.error('Penny could not extract quote data from any document — try cleaner PDFs');
      }
    } catch (e) {
      toast.error('Quote analysis failed: ' + e.message);
    } finally {
      setQuoteAnalyzing(false);
    }
  }

  function setScmContext(brief) {
    const updated = { ...scmContextCache, [scmCacheKey]: { brief, generatedAt: new Date().toISOString() } };
    updateC({ pennyDrafts: { scmContext: updated } });
  }

  function clearScmContext() {
    const updated = { ...scmContextCache };
    delete updated[scmCacheKey];
    updateC({ pennyDrafts: { scmContext: updated } });
  }

  // Call 1 — Generate SCM compliance brief from uploaded SCM PDF
  async function generateScmContext() {
    const isIT = contract.isIT === 'yes';
    const scmKey = isIT ? 'scm_vol2' : 'scm_vol1';
    const scmDoc = library[scmKey];
    if (!scmDoc?.base64) {
      const fallback = isIT
        ? 'SCM Vol 2 (IT) — not uploaded to Library, using verified SCM Vol 2 section references. Key requirements: IT procurement is defined per §1000; formal vs. informal competitive thresholds per §1400-1406 (informal below $1M for IT, formal above; RFQ format for informal, RFP/IFB for formal); Fair and Reasonable (F&R) acquisitions for goods (IT or non-IT) under $20,000 per §1510; NCB process per §1500-1508; SB/DVBE Option (GC §14838.5, $5,000.01-$249,999.99, two or more matching-type quotes) per §1405.3; LPA basics per §1602; SB participation (25% agency-wide annual goal, GC §14838) and DVBE (3% agency-wide annual goal) per §1200-1207; CDT review of IT RFPs required before release per PCC §12104(c).'
        : 'SCM Vol 1 (Non-IT) — not uploaded to Library, using verified SCM Vol 1 chapter references. Key requirements: procurement method selection per Chapter 5 (IFB §5.11, RFP-Primary §5.20, RFP-Secondary §5.25, exemptions/PriceQuote/NCB/SB-DVBE Option §5.80); required contract structure and exhibits (STD 213, Exhibits A-D) per §4.08; elements of a valid contract, scope and deliverables per §2.05; contractor performance monitoring per §9.11; SB (25% agency-wide annual goal, GC §14838) and DVBE (3% agency-wide annual goal, MVC §999 et seq.) participation per Chapter 8; DGS/OLS routing above PAAL threshold per §4.03 (PCC §§10295, 10297, 10335; GC §11256); union notification for personal-services contracting per GC §19131, §7.05; contract award protest procedures (5 working days\' notice to low bidder) per Chapter 6 (§6.10).';
      setScmContext(fallback);
      return fallback;
    }

    setScmLoading(true);
    try {
      const procMethod = contract.procMethod || 'RFQ';
      const scmLabel = isIT ? 'SCM Vol 2 (IT Services)' : 'SCM Vol 1 (Non-IT Services)';
      const procType = isIT ? 'IT services' : 'non-IT services';

      const sys = 'You are a California state contracting compliance expert. Read this ' + scmLabel + ' document and extract ALL compliance requirements relevant to a ' + procMethod + ' procurement for ' + procType + '.\n\n' +
        'Structure your response as follows — be specific, cite section numbers:\n\n' +
        '1. PROCUREMENT METHOD REQUIREMENTS\n' +
        'What this specific method (' + procMethod + ') requires per the SCM — advertising, minimum bid periods, mandatory forms.\n\n' +
        '2. SOW/SPECIFICATION REQUIREMENTS\n' +
        'What the SOW must include — required sections, deliverable standards, acceptance criteria.\n\n' +
        '3. SOLICITATION REQUIREMENTS\n' +
        'What the solicitation document must contain — evaluation criteria, mandatory clauses, prohibited provisions.\n\n' +
        '4. SB/DVBE COMPLIANCE\n' +
        'Small Business and DVBE requirements — goals, documentation, waiver procedures.\n\n' +
        '5. DGS ROUTING & APPROVAL\n' +
        'When DGS/OTA and DGS/OLS approval is required — thresholds, exemptions, routing procedures.\n\n' +
        '6. AWARD & EXECUTION REQUIREMENTS\n' +
        'Union notification, protest period, signature authority, contract execution sequence.\n\n' +
        '7. KEY PROHIBITED PRACTICES\n' +
        'Common compliance failures for this procurement type — cite specific SCM sections.\n\n' +
        'Be exhaustive. Cite specific section numbers. This brief will be used by Penny to ensure complete SCM compliance on every draft.';

      const actualPages = await getPdfPageCount(scmDoc.base64);
      const userContent = [];

      if (actualPages && actualPages <= 95) {
        userContent.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: scmDoc.base64 } });
      } else {
        const split = await splitPdfIntoChunks(scmDoc.base64, scmLabel);
        if (split?.chunks) {
          split.chunks.forEach(chunk => {
            chunk.pageImages.forEach(img => userContent.push({ type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: img } }));
          });
        }
      }

      userContent.push({ type: 'text', text: 'Extract compliance requirements for a ' + procMethod + ' procurement from this ' + scmLabel + '.' });

      const brief = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'scm_brief',
        contract_id: contract.id,
      });

      setScmContext(brief);
      toast.success('✒ SCM Compliance Brief ready for ' + procMethod + ' — Penny will use this for all drafts on this contract.');
      return brief;
    } catch (e) {
      toast.error('SCM brief failed: ' + e.message);
      return null;
    } finally {
      setScmLoading(false);
    }
  }

    // Get SCM context — from cache or generate fresh
  async function getScmContext() {
    if (scmCacheValid) return cachedScm.brief;
    return await generateScmContext();
  }

  // Expose generateScmContext via prop callback so App.jsx can trigger background refresh
  useEffect(() => {
    if (onScmRefreshReady) onScmRefreshReady(generateScmContext);
  }, [contract.procMethod, contract.isIT]); // eslint-disable-line
  // Persist supplement doc and notes to contract so they survive sessions
  const sowSupplementDoc = contract.pennyDrafts?.supplementDoc || null;
  const setSowSupplementDoc = (doc) => updateC({ pennyDrafts: { supplementDoc: doc } });
  async function handleSowSupplementUpload({ name, size, base64 }) {
    // Validate the PDF is actually readable BEFORE storing it — the draft
    // flow below sends this file's base64 directly to Anthropic as a
    // document block with no further checks, so an invalid/corrupted file
    // stored here would previously get shipped straight to the AI, causing
    // Anthropic to reject the whole request and — until the Worker's error
    // handling was hardened — crash it in a way that looked like a CORS
    // failure in the browser console.
    const pages = await getPdfPageCount(base64);
    if (!pages) {
      toast.error('"' + name + '" doesn\'t look like a valid, readable PDF — please check the file and try again.');
      return;
    }
    setSowSupplementDoc({ name, size, base64 });
  }
  const sowNotes = contract.pennyDrafts?.sowNotes || '';
  const setSowNotes = (text) => updateC({ pennyDrafts: { sowNotes: text } });
  const [sowLoading, setSowLoading] = useState(false);

  // ── Final Version state per exhibit ──────────────────────────────
  // Final versions live in pennyDrafts — Penny tab is source of truth
  const exAFinal = contract.pennyDrafts?.exAFinal || null;
  const setExAFinal = (v) => updateC({ pennyDrafts: { exAFinal: v } });
  const exBFinal = contract.pennyDrafts?.exBFinal || null;
  const setExBFinal = (v) => updateC({ pennyDrafts: { exBFinal: v } });
  const exDFinal = contract.pennyDrafts?.exDFinal || null;
  const setExDFinal = (v) => updateC({ pennyDrafts: { exDFinal: v } });

  // ── Contractor Finalization state ───────────────────────────────────
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeResults, setFinalizeResults] = useState(null); // null=not run, array=results

  // Stored draft text for each exhibit — used by the finalization button
  // to regenerate docx files with current s215 data (contractor info).
  const exBDraftText = contract.pennyDrafts?.exBDraft || '';
  const exDDraftText = contract.pennyDrafts?.exDDraft || '';
  const exBDraftAt = contract.pennyDrafts?.exBDraftAt || null;
  const exDDraftAt = contract.pennyDrafts?.exDDraftAt || null;
  const [exBUploadLoading, setExBUploadLoading] = useState(false);
  const [exDUploadLoading, setExDUploadLoading] = useState(false);

  // "Finalize with Contractor Info" — batch-regenerate all exhibits with
  // current s215 data. The docx builder handles headers, rep table, and
  // all {contractor_name} tokens. No AI call needed — purely mechanical.
  async function runContractorFinalize() {
    if (!s215?.contractorName?.trim()) {
      toast.warn('Fill in the contractor\'s name on the STD 215 tab before finalizing.');
      return;
    }
    setFinalizing(true);
    setFinalizeResults(null);
    const results = [];

    const exhibits = [
      { label: 'A', name: 'Scope of Work', title: contract.title || 'Professional Services', text: sowDraftText, final: exAFinal },
      { label: 'B', name: 'Budget Detail & Payment Provisions', title: 'Exhibit B — Budget Detail and Payment Provisions', text: exBDraftText, final: exBFinal },
      { label: 'D', name: 'Special Terms and Conditions', title: 'Exhibit D — Special Terms and Conditions', text: exDDraftText, final: exDFinal },
    ];

    for (const ex of exhibits) {
      // Determine the best text source:
      // 1. Stored draft text (from last Penny generation)
      // 2. penny_draft working version content
      // 3. Nothing → skip or flag
      const text = ex.text || (ex.final?.source === 'penny_draft' && ex.final?.content) || null;

      if (text) {
        try {
          await buildAndDownloadDocx(ex.title, text, profile, s215, contract, assignedCm, ex.label, resolveProfile(ex.label));
          results.push({ label: ex.label, name: ex.name, status: 'downloaded', message: 'Regenerated with ' + s215.contractorName });
        } catch (e) {
          results.push({ label: ex.label, name: ex.name, status: 'error', message: e.message });
        }
      } else if (ex.final?.source === 'user_uploaded') {
        results.push({ label: ex.label, name: ex.name, status: 'flagged', message: 'User-uploaded document — download and update contractor info manually in Word, then re-upload.' });
      } else {
        results.push({ label: ex.label, name: ex.name, status: 'skipped', message: 'No draft text available. Draft this exhibit first from the Penny tab.' });
      }

      // Small delay between downloads so the browser doesn't block them
      if (text) await new Promise(r => setTimeout(r, 500));
    }

    setFinalizeResults(results);
    setFinalizing(false);
    const downloaded = results.filter(r => r.status === 'downloaded').length;
    if (downloaded > 0) {
      toast.success('🏆 ' + downloaded + ' exhibit' + (downloaded === 1 ? '' : 's') + ' regenerated with contractor info for ' + s215.contractorName);
    }
  }

  // ── Penny Review toggles ──────────────────────────────────────────
  // Per-exhibit status
  const exAStatus = contract.pennyDrafts?.exAStatus || 'draft';
  const exBStatus = contract.pennyDrafts?.exBStatus || 'draft';
  const exDStatus = contract.pennyDrafts?.exDStatus || 'draft';
  const solStatus = contract.pennyDrafts?.solStatus || 'draft';
  const setExStatus = (key, val) => updateC({ pennyDrafts: { [key]: val } });

  const exAPennyReview = contract.pennyDrafts?.exAPennyReview !== false; // default ON
  const setExAPennyReview = (v) => updateC({ pennyDrafts: { exAPennyReview: v } });
  const exBPennyReview = contract.pennyDrafts?.exBPennyReview !== false;
  const setExBPennyReview = (v) => updateC({ pennyDrafts: { exBPennyReview: v } });
  const exDPennyReview = contract.pennyDrafts?.exDPennyReview !== false;
  const setExDPennyReview = (v) => updateC({ pennyDrafts: { exDPennyReview: v } });

  // ── Complete Contract Review state ────────────────────────────────
  const [contractReviewLoading, setContractReviewLoading] = useState(false);
  const contractReview = contract.pennyDrafts?.contractReview || '';
  const setContractReview = (v) => updateC({ pennyDrafts: { contractReview: v } });
  const [postReviewLoading, setPostReviewLoading] = useState(false);
  const postReview = contract.pennyDrafts?.postReview || '';
  const setPostReview = (v) => updateC({ pennyDrafts: { postReview: v } });

  // ── Final Version upload helper — stores in Supabase Storage ────────
  async function uploadExhibitFinal(file, setFn, sourceTag) {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      let storagePath = null;

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && contract?.id) {
          const path = user.id + '/' + contract.id + '/' + file.name.replace(/\s+/g, '_');
          const bytes = atob(base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          const { error } = await supabase.storage
            .from('exhibit-files')
            .upload(path, blob, { upsert: true });
          if (!error) {
            storagePath = path;
          } else {
            console.warn('Storage upload failed, falling back to base64:', error.message);
          }
        }
      } catch (e) {
        console.warn('Storage upload error:', e.message);
      }

      // Store path if uploaded to Storage, otherwise fall back to base64
      setFn({
        source: sourceTag || 'user_uploaded',
        fileName: file.name,
        size: file.size,
        storagePath,
        base64: storagePath ? null : base64, // only keep base64 if storage failed
        uploadedAt: new Date().toISOString(),
      });
      toast.success(file.name + ' set as Final Version');
    };
    reader.readAsDataURL(file);
  }

  // ── Download exhibit final from storage ───────────────────────────
  async function downloadExhibitFinal(final) {
    if (!final) return;
    if (final.storagePath) {
      const { data, error } = await supabase.storage
        .from('exhibit-files')
        .download(final.storagePath);
      if (data) {
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url; a.download = final.fileName || 'exhibit.pdf';
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
        return;
      }
      if (error) console.warn('Storage download failed:', error.message);
    }
    // Fallback to base64
    if (final.base64) {
      const bytes = atob(final.base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = final.fileName || 'exhibit.pdf';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    }
  }
  // Persist Penny drafts to contract so they survive sessions
  const sowDraftText = contract.pennyDrafts?.sow || '';
  const setSowDraftText = (text) => {
    // Save current draft to version history before overwriting
    const existing = contract.pennyDrafts?.sow;
    const history = contract.pennyDrafts?.sowHistory || [];
    const updates = { sow: text };
    if (existing && existing.trim() && existing !== text) {
      updates.sowHistory = [
        { text: existing, savedAt: new Date().toISOString(), label: 'Version ' + (history.length + 1) },
        ...history.slice(0, 9), // keep last 10 versions
      ];
    }
    updateC({ pennyDrafts: { ...updates } });
  };
  const [showSowHistory, setShowSowHistory] = useState(false);
  const sowHistory = contract.pennyDrafts?.sowHistory || [];
  const sowSupplementRef = useRef(null);

  // Step 2 — Review & Improve state
  const sowReviewFile = contract.pennyDrafts?.sowReviewFile || null;
  const setSowReviewFile = (v) => updateC({ pennyDrafts: { sowReviewFile: v } });
  const [sowReviewFileLoading, setSowReviewFileLoading] = useState(false);
  // Analyst's free-text description of what they changed in the uploaded
  // revision, or what they need Penny to check/incorporate — since Penny
  // can't reliably infer this from a diff, explicit instruction is the
  // reliable path (vs. generic re-analysis of the raw document alone).
  const sowReviewNotes = contract.pennyDrafts?.sowReviewNotes || '';
  const setSowReviewNotes = (text) => updateC({ pennyDrafts: { sowReviewNotes: text } });

  // Handles both PDF (base64, sent as a document block) and Word .docx
  // (text extracted client-side via extractDocxText/JSZip) — the upload
  // zone's label promises Word support, so this actually delivers it
  // instead of silently mislabeling docx bytes as application/pdf.
  async function handleSowReviewUpload(payload) {
    const { name, base64, size } = payload;
    if (/\.doc$/i.test(name) && !/\.docx$/i.test(name)) {
      toast.error('".doc" (old Word format) isn\'t supported — please save this file as ".docx" or export it as a PDF, then upload again.');
      return false;
    }
    // Detect .docx by filename alone and reconstruct a Blob from the base64
    // we always have, rather than depending on a raw File object being
    // present in the payload. Some upload zones only ever pass
    // {name, size, base64} — relying on a `file` field that isn't always
    // there silently fell through to treating .docx bytes as if they were
    // a PDF, which is exactly why that produced "InvalidPDFException"
    // instead of correctly extracting the Word document's text.
    if (base64 && /\.docx$/i.test(name)) {
      setSowReviewFileLoading(true);
      try {
        const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes]);
        const text = await extractDocxText(blob);
        setSowReviewFile({ name, size, base64: null, extractedText: text });
        toast.success('"' + name + '" uploaded — text extracted, ready to review');
        offerSwitchToNewCandidate('upload', 'uploaded document');
        return true;
      } catch (e) {
        toast.error('Could not read this Word document: ' + e.message);
        return false;
      } finally {
        setSowReviewFileLoading(false);
      }
    } else if (base64) {
      setSowReviewFileLoading(true);
      try {
        let text = null;
        try {
          text = await extractPdfText(base64);
        } catch (extractErr) {
          // extractPdfText threw (invalid/corrupt PDF, or a non-PDF file that
          // slipped through) — don't abandon the upload, fall back to storing
          // the raw file below, same as "no text layer found."
          console.warn('PDF text extraction threw, falling back to raw storage:', extractErr.message);
        }
        if (text) {
          // Real text layer found — keep the text, drop the base64 entirely
          // so this doesn't bloat penny_drafts with a multi-MB blob (the
          // same save-timeout issue we already hit and fixed for the NCB
          // proprietary letter).
          setSowReviewFile({ name, size, base64: null, extractedText: text });
          toast.success('"' + name + '" uploaded — text extracted, ready to review');
        } else {
          // No usable text layer (scanned document, or extraction failed) —
          // upload to Supabase Storage instead of keeping base64 inline.
          let storagePath = null;
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user && contract?.id) {
              const path = user.id + '/' + contract.id + '/sow-review/' + name.replace(/\s+/g, '_');
              const bytes = atob(base64);
              const arr = new Uint8Array(bytes.length);
              for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
              const blob = new Blob([arr], { type: 'application/pdf' });
              const { error } = await supabase.storage.from('exhibit-files').upload(path, blob, { upsert: true });
              if (!error) storagePath = path;
              else console.warn('SOW review storage upload failed, using base64 fallback:', error.message);
            }
          } catch (e) { console.warn('SOW review upload error:', e.message); }
          setSowReviewFile({ name, size, storagePath, base64: storagePath ? null : base64 });
          toast.warn(storagePath
            ? '"' + name + '" uploaded and stored — Penny may not be able to read this file\'s contents directly (no text layer found), but it\'s attached to the review.'
            : '"' + name + '" uploaded — Penny may not be able to read this file\'s contents directly (no text layer found), and storage upload failed, so it\'s being kept inline (may cause save issues for large files).');
        }
      } catch (e) {
        toast.error('Could not process this document: ' + e.message);
        return false;
      } finally {
        setSowReviewFileLoading(false);
      }
      offerSwitchToNewCandidate('upload', 'uploaded document');
      return true;
    } else {
      setSowReviewFile({ name, base64, size });
      offerSwitchToNewCandidate('upload', 'uploaded document');
      return true;
    }
  }
  // Promotes whatever's currently loaded in Step 2 (an uploaded document,
  // reviewed or not) directly to the exhibit's working document — for the
  // case where the analyst brought in a document created outside GoldenScope
  // and, after Penny's review, wants to accept it as-is with no revision
  // needed. If text was extracted on upload, it becomes the working draft
  // text (same pipeline "Use Penny Draft" already relies on). Otherwise it's
  // promoted directly as the Final Version, the same shape a manual "Upload
  // Final PDF" produces.
  function promoteSowReviewFileToWorking() {
    if (!sowReviewFile) return;
    if (sowReviewFile.extractedText) {
      setSowDraftText(sowReviewFile.extractedText);
      toast.success('"' + sowReviewFile.name + '" set as the current working document for Exhibit A.');
    } else if (sowReviewFile.storagePath || sowReviewFile.base64) {
      updateC({ pennyDrafts: { exAFinal: {
        source: 'user_uploaded',
        fileName: sowReviewFile.name,
        size: sowReviewFile.size,
        storagePath: sowReviewFile.storagePath || null,
        base64: sowReviewFile.storagePath ? null : sowReviewFile.base64,
        uploadedAt: new Date().toISOString(),
      } } });
      toast.success('"' + sowReviewFile.name + '" set as the Final Version for Exhibit A — visible in Current Working Versions.');
    } else {
      toast.error('This document has no readable content to promote — try re-uploading it.');
    }
  }
  const [sowReviewLoading, setSowReviewLoading] = useState(false);
  const sowFindings = contract.pennyDrafts?.sowFindings || [];
  const setSowFindings = (v) => updateC({ pennyDrafts: { sowFindings: typeof v === 'function' ? v(contract.pennyDrafts?.sowFindings || []) : v } });

  // ── Sole Source / NCB Justification state ──────────────────────────
  const ncb = contract.pennyDrafts?.ncb || {};
  const setNcb = (patch) => updateC({ pennyDrafts: { ncb: { ...ncb, ...patch } } });
  const [ncbLoading, setNcbLoading] = useState(false);
  const [classifyLoading, setClassifyLoading] = useState(false);

  async function handleProprietaryLetterUpload(payload) {
    const { name, base64, size } = payload;
    if (base64 && /\.docx$/i.test(name)) {
      try {
        const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes]);
        const text = await extractDocxText(blob);
        setNcb({ letterFile: { name, size, base64: null, extractedText: text } });
        toast.success('"' + name + '" uploaded — text extracted');
      } catch (e) {
        toast.error('Could not read this Word document: ' + e.message);
      }
    } else if (base64) {
      const text = await extractPdfText(base64);
      if (text) {
        // Real text layer found — keep the text, drop the base64 entirely
        // so this doesn't bloat penny_drafts with a multi-MB blob.
        setNcb({ letterFile: { name, size, base64: null, extractedText: text } });
        toast.success('"' + name + '" uploaded — text extracted');
      } else {
        // No usable text layer (scanned letter) — upload to Supabase
        // Storage instead of keeping base64 inline, same pattern as the
        // CMAS doc upload. This is the real fix: a scanned letter can never
        // avoid needing its original bytes, so the fix is moving those
        // bytes out of penny_drafts entirely, not trying to shrink them.
        let storagePath = null;
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user && contract?.id) {
            const path = user.id + '/' + contract.id + '/ncb-letter/' + name.replace(/\s+/g, '_');
            const bytes = atob(base64);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            const blob = new Blob([arr], { type: 'application/pdf' });
            const { error } = await supabase.storage.from('exhibit-files').upload(path, blob, { upsert: true });
            if (!error) storagePath = path;
            else console.warn('NCB letter storage upload failed, using base64 fallback:', error.message);
          }
        } catch (e) { console.warn('NCB letter upload error:', e.message); }
        setNcb({ letterFile: { name, size, storagePath, base64: storagePath ? null : base64 } });
        toast.warn(storagePath
          ? '"' + name + '" looks like a scanned document — stored securely, no text layer was found.'
          : '"' + name + '" looks like a scanned document — no text layer found, and storage upload failed, so the file is being kept inline (may cause save issues for large files).');
      }
    } else {
      setNcb({ letterFile: { name, base64, size } });
    }
  }

  // Handles the signed STD 816 (DVBE Exemption Request) upload — same
  // storage-safe pattern as the NCB letter, since a signed/scanned form is
  // exactly the case that risks the base64-bloat save-timeout issue.
  async function handleStd816Upload(payload) {
    const { name, base64, size } = payload;
    if (base64 && /\.docx$/i.test(name)) {
      try {
        const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes]);
        const text = await extractDocxText(blob);
        updateC({ dvbe: { ...(contract.dvbe || {}), std816File: { name, size, base64: null, extractedText: text, uploadedAt: new Date().toISOString() } } });
        toast.success('"' + name + '" uploaded — text extracted');
      } catch (e) {
        toast.error('Could not read this Word document: ' + e.message);
      }
    } else if (base64) {
      const text = await extractPdfText(base64);
      if (text) {
        updateC({ dvbe: { ...(contract.dvbe || {}), std816File: { name, size, base64: null, extractedText: text, uploadedAt: new Date().toISOString() } } });
        toast.success('"' + name + '" uploaded — text extracted');
      } else {
        let storagePath = null;
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user && contract?.id) {
            const path = user.id + '/' + contract.id + '/std816/' + name.replace(/\s+/g, '_');
            const bytes = atob(base64);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            const blob = new Blob([arr], { type: 'application/pdf' });
            const { error } = await supabase.storage.from('exhibit-files').upload(path, blob, { upsert: true });
            if (!error) storagePath = path;
            else console.warn('STD 816 storage upload failed, using base64 fallback:', error.message);
          }
        } catch (e) { console.warn('STD 816 upload error:', e.message); }
        updateC({ dvbe: { ...(contract.dvbe || {}), std816File: { name, size, storagePath, base64: storagePath ? null : base64, uploadedAt: new Date().toISOString() } } });
        toast.success(storagePath ? '"' + name + '" uploaded — stored securely' : '"' + name + '" uploaded');
      }
    } else {
      updateC({ dvbe: { ...(contract.dvbe || {}), std816File: { name, base64, size, uploadedAt: new Date().toISOString() } } });
    }
  }

  // Uses SCM Vol 2's actual Goods-vs-Services classification criteria to help
  // the analyst pick the right acquisition type before relying on the
  // threshold logic above — this is the exact judgment call that led to the
  // wrong bucket being picked before, so Penny reasons it out explicitly
  // rather than the analyst having to infer it from the contract description.
  //
  // Pulls in the original intake SOW (if one was uploaded at contract
  // creation) and any Penny-drafted Exhibit A — both give real substance to
  // reason from instead of just the short description field.
  const ncbRequestDocAttachment = (contract.attachments || []).find(a => a.tag === 'Request Document');
  function ncbSowTextContext() {
    return sowDraftText ? '\n\nPENNY-DRAFTED EXHIBIT A (SCOPE OF WORK):\n' + sowDraftText.slice(0, 8000) : '';
  }
  function ncbSowDocBlock() {
    return ncbRequestDocAttachment?.base64
      ? [{ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: ncbRequestDocAttachment.base64 } }]
      : [];
  }
  // Only needed for the scanned/no-text-layer fallback case — when
  // extractedText is present, the letter's content is already in the text
  // context and doesn't need to be sent again as a document block. Handles
  // both the storage-backed path (storagePath) and the legacy/fallback
  // inline base64 path.
  async function ncbLetterDocBlock() {
    if (ncb.letterFile?.extractedText) return [];
    let base64 = ncb.letterFile?.base64;
    if (!base64 && ncb.letterFile?.storagePath) {
      try {
        const { data } = await supabase.storage.from('exhibit-files').download(ncb.letterFile.storagePath);
        if (data) { const full = await blobToBase64(data); base64 = full.split(',')[1]; }
      } catch (e) { console.warn('NCB letter download error:', e.message); }
    }
    return base64 ? [{ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } }] : [];
  }

  // Same pattern for the SOW review upload — resolves either inline base64
  // or a Storage-backed scanned document into an actual base64 string for
  // the API. Returns null if there's genuinely nothing usable.
  async function resolveSowReviewBase64() {
    if (sowReviewFile?.base64) return sowReviewFile.base64;
    if (sowReviewFile?.storagePath) {
      try {
        const { data } = await supabase.storage.from('exhibit-files').download(sowReviewFile.storagePath);
        if (data) { const full = await blobToBase64(data); return full.split(',')[1]; }
      } catch (e) { console.warn('SOW review download error:', e.message); }
    }
    return null;
  }

  async function classifyAcquisition() {
    setClassifyLoading(true);
    try {
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Description: ' + (contract.description || contract.notes || 'Not provided'),
        'Budget: ' + fmtMoney(contract.budget),
        'Category: ' + (contract.category || 'Professional Services'),
      ].join('\n');
      const letterNote = ncb.letterFile?.extractedText
        ? '\n\nContractor proprietary letter (text):\n' + ncb.letterFile.extractedText
        : '';
      const sowNote = ncbSowTextContext();

      const sys = `You are Penny, the GoldenScope contracting assistant. Classify this acquisition as GOODS or SERVICES per SCM Vol 2's actual classification criteria, then recommend the specific acquisition-type bucket for NCB purposes.

CLASSIFICATION CRITERIA (SCM Vol 2, "Distinguish Goods from Services" and "Determine the Main Purpose"):
- Ask: what is the MAIN VALUE of the contract — a tangible item/product, or the labor/expertise/service being performed?
- If the main value is a good with minimal or no accompanying services, classify as GOODS.
- If the main value is the service (including software development, customization, integration, maintenance, or support work), classify as SERVICES — even when proprietary software is involved.
- A one-time purchase or license of an existing, unmodified proprietary software product is normally GOODS.
- Ongoing maintenance, support, subscription renewal work, custom development, system integration, or any contract where a person is performing IT-related labor is normally SERVICES — per SCM's own example, a request met by developing custom software (not an off-the-shelf purchase) is treated as an IT SERVICE because the main value is the technical expertise, not a product.
- Items that merely depend on IT to function are not automatically IT-classified — consider whether IT skill/knowledge is the PRIMARY purpose or merely secondary to an overarching non-IT purpose.

Based on the contract context, the original intake SOW/Exhibit A if provided, and proprietary letter if provided, determine:
1. GOODS or SERVICES
2. Confidence: high, medium, or low
3. A 2-3 sentence explanation citing which criterion drove the classification — reference specific scope details from the SOW if available, not just the short description
4. The single best-fit acquisition type bucket from this exact list (use the value exactly as written):
   - "IT Goods — New Proprietary Software (purchase/license)"
   - "IT Services — Proprietary Maintenance/Support"
   - "IT Goods"
   - "IT Services"
   - "Non-IT Services"

Return ONLY valid JSON, no markdown: {"classification":"Goods|Services","confidence":"high|medium|low","reasoning":"...","recommendedType":"<exact value from list above>"}`;

      const letterDoc = await ncbLetterDocBlock();
      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-haiku-4-5-20251001', max_tokens: 2000,
        messages: [{ role: 'user', content: [...ncbSowDocBlock(), ...letterDoc, { type: 'text', text: contractCtx + sowNote + letterNote }] }],
        tag: 'ncb_classify',
        contract_id: contract.id,
      });
      const parsed = safeParseJson(raw);
      setNcb({ classification: parsed });
    } catch (e) {
      toast.error('Classification failed: ' + e.message);
    } finally {
      setClassifyLoading(false);
    }
  }

  // Recommends DVBE and SB participation status based on contract context,
  // grounded in SCM Ch.8's real rules — DVBE applies to "all contracts,
  // regardless of amount" unless the agency affirmatively exempts a specific
  // one (which requires STD 816), so the safe default lean is toward
  // "applies" rather than assuming exemption. SB and DVBE are evaluated
  // independently, and each has its own goal-vs-incentive distinction.
  const [dvbeSbRecLoading, setDvbeSbRecLoading] = useState(false);
  async function recommendDvbeSb() {
    setDvbeSbRecLoading(true);
    try {
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Description: ' + (contract.description || contract.notes || 'Not provided'),
        'Budget: ' + fmtMoney(contract.budget),
        'Category: ' + (contract.category || 'Professional Services'),
        'Procurement Method: ' + (contract.procMethod || 'not yet selected'),
        'IT Contract: ' + (contract.isIT === 'yes' ? 'Yes' : 'No'),
      ].join('\n');
      const sowNote = ncbSowTextContext();

      const sys = `You are Penny, the GoldenScope contracting assistant. Recommend DVBE and SB participation status for this contract, per SCM Vol 1 Chapter 8. Goal and Incentive are SEPARATE, independently-set values for both DVBE and SB — per the real STD 816 form, an agency can waive the DVBE Program Requirement (goal) while keeping the Incentive active, or waive either/both independently. Never assume waiving one implies waiving the other.

KEY RULES:
- DVBE and SB participation each apply to "all contracts, regardless of amount," UNLESS the procurement method/category falls categorically outside scope (e.g., NCB/Sole Source contracts, interagency agreements, legal services, expert witness contracts, statutory exemptions) OR the agency has affirmatively exempted this specific contract. For DVBE, an affirmative exemption requires a signed STD 816 — a real approval action, not a default.
- DVBE and SB are evaluated independently of each other. Goal and Incentive within each are ALSO evaluated independently of each other.
- For EACH of the four values (dvbeGoal, dvbeIncentive, sbGoal, sbIncentive), recommend one of these three exact values:
  - "applies" — this requirement/incentive applies to this contract
  - "waived" — this would normally apply but has been affirmatively exempted (only recommend this if there's a clear signal in the context that an exemption applies — otherwise prefer "applies", since waiving requires real approval you cannot confirm, and for DVBE specifically requires a signed STD 816)
  - "not_required" — this contract category/method falls outside the requirement entirely (e.g., NCB, Sole Source, interagency agreement, legal services)
- Default toward caution: if genuinely uncertain for a competitively-bid method, prefer "applies" over "not_required", since DVBE/SB incentives apply broadly even when a goal doesn't.
- Do NOT recommend "waived" unless the contract context clearly indicates a categorical reason an exemption would be sought — waiving is a deliberate exception, not a default.

Return ONLY valid JSON, no markdown: {"dvbeGoal":{"recommended":"applies|waived|not_required","reasoning":"1-2 sentences"},"dvbeIncentive":{"recommended":"applies|waived|not_required","reasoning":"1-2 sentences"},"sbGoal":{"recommended":"applies|waived|not_required","reasoning":"1-2 sentences"},"sbIncentive":{"recommended":"applies|waived|not_required","reasoning":"1-2 sentences"}}`;

      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-haiku-4-5-20251001', max_tokens: 2000,
        messages: [{ role: 'user', content: [...getContractRefDocs(), { type: 'text', text: contractCtx + sowNote }] }],
        tag: 'dvbe_sb_recommend',
        contract_id: contract.id,
      });
      const parsed = safeParseJson(raw);
      updateC({ pennyDrafts: { dvbeSbRec: parsed } });
      toast.success('DVBE/SB recommendation ready');
    } catch (e) {
      toast.error('Recommendation failed: ' + e.message);
    } finally {
      setDvbeSbRecLoading(false);
    }
  }

  // Helps the analyst determine whether CDT involvement is likely, using
  // the actual triggers found in SCM Vol 2 rather than a generic "check with
  // CDT" prompt — this can't be fully automated (reportable IT project
  // status in particular depends on records the app doesn't have access
  // to), so this surfaces a reasoned recommendation with a clear "verify
  // with CDT/your PAC" caveat rather than a confident yes/no.
  const [cdtClassifyLoading, setCdtClassifyLoading] = useState(false);
  async function classifyCdtApplicability() {
    setCdtClassifyLoading(true);
    try {
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Description: ' + (contract.description || contract.notes || 'Not provided'),
        'Budget: ' + fmtMoney(contract.budget),
        'Procurement Method: ' + (contract.procMethod || 'not yet selected'),
        'Acquisition Type (NCB module): ' + (ncb.acquisitionType || 'not yet selected'),
      ].join('\n');
      const sowNote = ncbSowTextContext();

      const sys = `You are Penny, the GoldenScope contracting assistant. Assess whether California Department of Technology (CDT) involvement is likely required for this acquisition, using these specific SCM Vol 2 triggers — do NOT give a generic "check with CDT" answer, reason through each one against the contract context:

1. RFP REVIEW (PCC §12104(c)) — ALL IT RFPs must be reviewed by CDT before release to the public. If the procurement method is an IT RFP (RFP-Primary or RFP-Secondary on an IT contract), this trigger applies with HIGH confidence — it is a hard statutory requirement, not a judgment call.
2. REPORTABLE IT PROJECT STATUS (PCC §12100) — if this acquisition is part of a project that has (or should have) a Project Approval Lifecycle (PAL) or Special Project Report (SPR) with CDT, and the acquisition is non-delegated or exceeds the department's delegated authority, CDT's procurement authority applies. Look for signals in the description/SOW: multi-phase rollouts, new system builds, statewide system deployments, anything that sounds like a formally tracked "IT project" rather than a routine purchase or maintenance renewal.
3. SYSTEM INTEGRATION — large-scale system integration projects, or work requiring Independent Verification and Validation (IVV) / Independent Project Oversight (IPO), are classified as IT services requiring CDT-adherent procurement rules (per SIMM Section 45).
4. SAAS/CLOUD — Software as a Service or Cloud Computing purchases tied to a Non-Delegated IT Project are subject to CDT's IT project procurement authority.

If none of these triggers are clearly indicated by the contract context, say so plainly and recommend the analyst confirm with their department's Purchasing Authority Contact (PAC) rather than guessing — do not force a "yes" or "no" you aren't reasonably confident in.

Return ONLY valid JSON, no markdown: {"applicable":true|false|"uncertain","confidence":"high|medium|low","reasoning":"...","triggeredBy":["list of trigger names from above that apply, or empty array"]}`;

      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-haiku-4-5-20251001', max_tokens: 2500,
        messages: [{ role: 'user', content: [...ncbSowDocBlock(), { type: 'text', text: contractCtx + sowNote }] }],
        tag: 'ncb_cdt_classify',
        contract_id: contract.id,
      });
      const parsed = safeParseJson(raw);
      setNcb({ cdtClassification: parsed, cdtApplicable: parsed.applicable === true });
    } catch (e) {
      toast.error('CDT classification failed: ' + e.message);
    } finally {
      setCdtClassifyLoading(false);
    }
  }

  // Computes which NCB threshold bucket this contract falls into per SCM
  // Vol 2 §1501/§1502/§1507, using the acquisition type the analyst selects
  // (the rules genuinely differ across these, so this can't be inferred
  // from contract.isIT alone). Note the Goods/Services split within
  // "proprietary" itself: code 025 (new software / license purchases) is
  // explicitly restricted to the IT Goods acquisition type ($1M threshold),
  // while code 024 (proprietary maintenance/support) is described as a
  // SERVICES exemption with its own, lower ($250K) threshold — these are
  // not interchangeable even though both stem from a "proprietary" letter.
  //
  // Also surfaces two additional routing/approval flags, mirroring the
  // existing olsRequired/otaRequired pattern elsewhere in the app:
  // - druRequired: whether this NCB likely needs DGS/PD/DRU review (a
  //   different threshold/agency than DGS/OLS — this is about the NCB
  //   Justification itself, not general contract execution routing)
  // - itPacRequired: the agency's own internal IT Purchasing Authority
  //   Contact sign-off, required specifically for proprietary software
  //   acquisitions under $1,000,000 per SCM Vol 2 §1507
  function ncbThresholdInfo() {
    const budgetNum = parseFloat(contract.budget) || 0;
    const type = ncb.acquisitionType || '';
    if (type === 'IT Goods — New Proprietary Software (purchase/license)') {
      const overCap = budgetNum > 1000000;
      return {
        needsJustification: overCap,
        druRequired: overCap,
        itPacRequired: true,
        note: overCap
          ? 'Exceeds $1,000,000 — an approved NCB Justification IS required (SCM Vol 2 §1507), and DGS/PD/DRU review applies since this exceeds the department-level exemption cap.'
          : 'Under $1,000,000 — new proprietary software purchases/licenses may be executed WITHOUT an NCB Justification (SCM Vol 2 §1507, acquisition code 025 — this code is explicitly restricted to the IT Goods acquisition type). Still requires the dated proprietary letter and cost-reasonableness documentation in the file.',
      };
    }
    if (type === 'IT Services — Proprietary Maintenance/Support') {
      const overCap = budgetNum > 250000;
      return {
        needsJustification: overCap,
        druRequired: overCap,
        itPacRequired: true,
        note: overCap
          ? 'Exceeds $250,000 — an approved NCB Justification IS required (SCM Vol 2 §1501, acquisition code 024), and DGS/PD/DRU review applies since this exceeds the department-level exemption cap.'
          : 'Under $250,000 — proprietary maintenance/support services may be executed WITHOUT an NCB Justification (SCM Vol 2 §1501, acquisition code 024 — this is classified as a SERVICES exemption, distinct from the Goods-only code 025 above). Still requires the dated proprietary letter and cost-reasonableness documentation in the file.',
      };
    }
    if (type === 'IT Services' || type === 'IT Goods') {
      const overDept = budgetNum > 25000;
      return {
        needsJustification: true,
        druRequired: overDept,
        itPacRequired: false,
        note: overDept
          ? 'Exceeds the $25,000 department NCB justification threshold for IT Goods/Services (SCM Vol 2 §1502) — submit to DGS/PD/DRU (or CDT if applicable) for review unless your PAAL grants a higher NCB threshold specifically.'
          : 'Within the $25,000 department NCB justification threshold for IT Goods/Services (SCM Vol 2 §1502) — check your PAAL for your agency\'s actual approved NCB threshold, which may be lower.',
      };
    }
    if (type === 'Non-IT Services') {
      const overDept = budgetNum >= 20000;
      return {
        needsJustification: true,
        druRequired: overDept,
        itPacRequired: false,
        note: overDept
          ? '$20,000 or more — must be sent to DGS/PD/DRU for review and approval (SCM Vol 2 §1502).'
          : 'Under the $9,999 department threshold for Non-IT Services (SCM Vol 2 §1502) — may be justified at the department level.',
      };
    }
    return { needsJustification: null, druRequired: false, itPacRequired: false, note: 'Select an acquisition type above to see the applicable NCB threshold. If your contract is for ongoing proprietary software maintenance/support rather than a new purchase or license, use the "IT Services — Proprietary Maintenance/Support" option, not the Goods option — SCM classifies these differently with different thresholds.' };
  }

  async function draftNcbJustification() {
    if (!ncb.acquisitionType) { toast.warn('Select an acquisition type first.'); return; }
    setNcbLoading(true);
    try {
      const threshold = ncbThresholdInfo();
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Budget: ' + fmtMoney(contract.budget),
        'Acquisition Type: ' + ncb.acquisitionType,
        'Category: ' + (contract.category || 'Professional Services'),
        'Threshold guidance: ' + threshold.note,
      ].join('\n');

      const letterContent = ncb.letterFile?.extractedText
        ? '\n\nCONTRACTOR PROPRIETARY LETTER (text):\n' + ncb.letterFile.extractedText
        : '';
      const sowContent = ncbSowTextContext();

      const sys = `You are Penny, the GoldenScope contracting assistant for the Office of Energy Infrastructure Safety (Energy Safety). Draft a Non-Competitively Bid (NCB) Justification narrative for submission through the DGS NCB Portal, based on SCM Vol 2 §1503.

The DGS NCB Portal requires clear, concise answers to these categories — write each as its own labeled paragraph:

1. WHY THE GOOD OR SERVICE IS RESTRICTED/UNIQUE — explain specifically what makes this proprietary and unavailable elsewhere, drawing on the contractor's letter and the SOW/scope details if provided.
2. BACKGROUND OF EVENTS — what circumstances led to this request. Use the SOW's actual scope and objectives if available rather than generic language.
3. CONSEQUENCES IF NOT PURCHASED — what happens to Energy Safety's operations/programs if this acquisition doesn't proceed.
4. CONFIRMATION NO COMPETITION EXISTS — state plainly that market research confirms no reseller or alternative product meets the business need (reference the contractor's letter as supporting evidence).
5. PRICE REASONABLENESS BASIS — describe how the price was determined fair and reasonable (market comparison, historical pricing, LPA pricing, or similar — use [PLACEHOLDER] if the analyst needs to supply specifics).
6. COST SAVINGS OR AVOIDANCE — note any quantifiable savings from this approach if applicable, or [PLACEHOLDER] if not yet known.

Also include a short REMINDERS section at the end listing, in plain language:
- The proprietary letter must be dated within 12 months of solicitation (SCM Vol 2 §1507) — flag if this cannot be confirmed from context.
- Signature authority required: Agency Secretary/Undersecretary (or highest-ranking officer if no Agency Secretary) + department director or designee.
- ${threshold.note}${threshold.itPacRequired ? '\n- IT Purchasing Authority Contact (PAC) sign-off is required for this acquisition (SCM Vol 2 §1507).' : ''}${ncb.cdtApplicable ? '\n- This was flagged as a possible reportable IT project or system integration effort — confirm with CDT whether their review or direct procurement authority applies before proceeding.' : ''}

Write in formal but plain language suitable for direct entry into a government justification portal. No markdown formatting — plain text with clear paragraph labels. Use [PLACEHOLDER] for anything not answerable from the given context — do not invent specifics.` + languageRuleText(resolveProfile('NCB'));

      const letterDoc = await ncbLetterDocBlock();
      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: [...ncbSowDocBlock(), ...letterDoc, { type: 'text', text: 'Contract context:\n' + contractCtx + sowContent + letterContent + '\n\nDraft the NCB Justification narrative.' }] }],
        tag: 'ncb_justification',
        contract_id: contract.id,
      });
      setNcb({ justificationText: text, justificationAt: new Date().toISOString() });
      toast.success('NCB Justification narrative drafted');
    } catch (e) {
      toast.error('NCB Justification drafting failed: ' + e.message);
    } finally {
      setNcbLoading(false);
    }
  }

  // Cost-reasonableness documentation is required for EVERY NCB/sole source
  // acquisition regardless of whether a formal NCB Justification is needed —
  // SCM Vol 2 is explicit that even fully policy/statute-exempt purchases
  // must have documentation supporting fair and reasonable pricing.
  const [costReasonLoading, setCostReasonLoading] = useState(false);
  async function draftCostReasonableness() {
    setCostReasonLoading(true);
    try {
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Budget: ' + fmtMoney(contract.budget),
        'Acquisition Type: ' + (ncb.acquisitionType || 'not yet selected'),
      ].join('\n');
      const sowContent = ncbSowTextContext();
      const letterContent = ncb.letterFile?.extractedText
        ? '\n\nCONTRACTOR PROPRIETARY LETTER (text):\n' + ncb.letterFile.extractedText
        : '';

      const sys = `You are Penny, the GoldenScope contracting assistant for the Office of Energy Infrastructure Safety (Energy Safety). Draft Cost-Reasonableness Documentation for the procurement file, per SCM Vol 2's requirement that ALL NCB/sole source purchases — even those fully exempt from a formal NCB Justification by statute or policy — must be documented as fair and reasonable in price.

Write the following as clearly labeled sections:

1. BASIS OF COMPARISON — identify which comparison method(s) apply and explain the reasoning. Choose from (use [PLACEHOLDER] for any figures the analyst must supply):
   - Market rate comparison (comparable products/services and their pricing)
   - Historical pricing (what Energy Safety or other State agencies have paid previously for this or similar items)
   - Existing contract/LPA pricing (if this pricing can be checked against a CMAS, MSA, or other LPA schedule as a ceiling reference, even if not procured through it)
   - Cost breakdown analysis (itemized components of the price, if available)
2. PRICE REASONABLENESS CONCLUSION — a direct statement that the price has been reviewed and determined fair and reasonable, referencing the basis of comparison above.
3. SUPPORTING DOCUMENTATION CHECKLIST — a short list of what should be attached/retained in the procurement file to support this conclusion (e.g., vendor price quotes, published rate sheets, prior contract pricing records, LPA ceiling price printout).

Use [PLACEHOLDER] freely for any specific dollar figures, comparison sources, or vendor names not available in the given context — do not invent numbers. Write in formal but plain language suitable for a procurement file memo. No markdown formatting.` + languageRuleText(resolveProfile('NCB'));

      const letterDoc = await ncbLetterDocBlock();
      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: [...ncbSowDocBlock(), ...letterDoc, { type: 'text', text: 'Contract context:\n' + contractCtx + sowContent + letterContent + '\n\nDraft the Cost-Reasonableness Documentation.' }] }],
        tag: 'ncb_cost_reasonableness',
        contract_id: contract.id,
      });
      setNcb({ costReasonText: text, costReasonAt: new Date().toISOString() });
      toast.success('Cost-Reasonableness Documentation drafted');
    } catch (e) {
      toast.error('Cost-Reasonableness drafting failed: ' + e.message);
    } finally {
      setCostReasonLoading(false);
    }
  }

  // ── Exhibit B review state ──────────────────────────────────────────
  const exBReviewFile = contract.pennyDrafts?.exBReviewFile || null;
  const setExBReviewFile = (v) => updateC({ pennyDrafts: { exBReviewFile: v } });
  async function handleExBReviewUpload({ name, size, base64 }) {
    // Validated before storing — this file gets sent to Anthropic as a raw
    // PDF document block with no further checks, same risk as the
    // sowSupplementDoc/referenceDocs gap fixed elsewhere in this file.
    const pages = await getPdfPageCount(base64);
    if (!pages) { toast.error('"' + name + '" doesn\'t look like a valid, readable PDF — please check the file and try again.'); return; }
    setExBReviewFile({ name, size, base64 });
  }
  const [exBReviewLoading, setExBReviewLoading] = useState(false);
  const exBReviewFindings = contract.pennyDrafts?.exBReviewFindings || [];
  const setExBReviewFindings = (v) => updateC({ pennyDrafts: { exBReviewFindings: typeof v === 'function' ? v(contract.pennyDrafts?.exBReviewFindings || []) : v } });
  const [exBReviewChecked, setExBReviewChecked] = useState({});
  const [exBRewriteLoading, setExBRewriteLoading] = useState(false);

  // ── Exhibit D review state ──────────────────────────────────────────
  const exDReviewFile = contract.pennyDrafts?.exDReviewFile || null;
  const setExDReviewFile = (v) => updateC({ pennyDrafts: { exDReviewFile: v } });
  async function handleExDReviewUpload({ name, size, base64 }) {
    const pages = await getPdfPageCount(base64);
    if (!pages) { toast.error('"' + name + '" doesn\'t look like a valid, readable PDF — please check the file and try again.'); return; }
    setExDReviewFile({ name, size, base64 });
  }
  const [exDReviewLoading, setExDReviewLoading] = useState(false);
  const exDReviewFindings = contract.pennyDrafts?.exDReviewFindings || [];
  const setExDReviewFindings = (v) => updateC({ pennyDrafts: { exDReviewFindings: typeof v === 'function' ? v(contract.pennyDrafts?.exDReviewFindings || []) : v } });
  const [exDReviewChecked, setExDReviewChecked] = useState({});
  const [exDRewriteLoading, setExDRewriteLoading] = useState(false);
  const sowChecked = contract.pennyDrafts?.sowChecked || {};
  const setSowChecked = (v) => updateC({ pennyDrafts: { sowChecked: typeof v === 'function' ? v(contract.pennyDrafts?.sowChecked || {}) : v } });
  const [sowFinalLoading, setSowFinalLoading] = useState(false);

  // Other drafts state
  const [draftLoading, setDraftLoading] = useState({});
  const draftResults = contract.pennyDrafts?.results || {};
  const setDraftResults = (updater) => {
    const current = contract.pennyDrafts?.results || {};
    const next = typeof updater === 'function' ? updater(current) : updater;
    updateC({ pennyDrafts: { results: next } });
  };
  const recMethodPick = contract.pennyDrafts?.recMethodPick || null;
  const setRecMethodPick = (v) => updateC({ pennyDrafts: { recMethodPick: typeof v === 'function' ? v(contract.pennyDrafts?.recMethodPick || null) : v } });
  const sourceDocs = contract.pennyDrafts?.sourceDocs || {};
  const setSourceDocs = (v) => updateC({ pennyDrafts: { sourceDocs: typeof v === 'function' ? v(contract.pennyDrafts?.sourceDocs || {}) : v } });
  const sourceDocRefs = useRef({});
  const sessionFileRef = useRef(null);

  // Contract reference docs — persisted on the contract itself
  const contractRefs = contract.referenceDocs || [];

  async function addContractRef(files) {
    const existing = contract.referenceDocs || [];
    const added = [];
    const rejected = [];
    for (const f of Array.from(files)) {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(f);
      });
      // Validate the PDF is actually readable before storing it — this doc
      // gets sent to Anthropic as a raw document block from over a dozen
      // different Penny functions with no further checks at read time. An
      // invalid/corrupted file stored here would get shipped to the AI on
      // every one of those calls, causing each to fail outright.
      const pages = await getPdfPageCount(b64);
      if (!pages) {
        rejected.push(f.name);
        continue;
      }
      const entry = { id: Date.now() + Math.random(), name: f.name, base64: b64, size: f.size, addedAt: new Date().toISOString().split('T')[0] };
      // Pre-upload to Anthropic Files API so Penny can reference without page limits
      try {
        const fileId = await uploadToAnthropicFiles(apiKey, b64, f.name);
        entry.anthropicFileId = fileId;
        entry.anthropicFileUploadedAt = new Date().toISOString();
      } catch (e) {
        console.warn('Files API pre-upload failed for "' + f.name + '":', e.message);
        // Doc still works via base64 fallback in Penny chat
      }
      added.push(entry);
    }
    if (added.length > 0) updateC({ referenceDocs: [...existing, ...added] });
    if (rejected.length > 0) toast.error((rejected.length === 1 ? rejected[0] : rejected.length + ' files') + ' couldn\'t be read as valid PDF' + (rejected.length === 1 ? '' : 's') + ' and ' + (rejected.length === 1 ? 'was' : 'were') + ' not added.');
    if (added.length > 0) toast.success(added.length === 1 ? added[0].name + ' added as reference' : added.length + ' reference docs added');
  }

  function removeContractRef(id) {
    updateC({ referenceDocs: (contract.referenceDocs || []).filter(d => d.id !== id) });
  }

  // Analyze state
  const analyzeDocType = contract.pennyDrafts?.analyzeDocType || 'sow';
  const setAnalyzeDocType = (v) => updateC({ pennyDrafts: { analyzeDocType: v } });
  const analyzeFile = contract.pennyDrafts?.analyzeFile || null;
  const setAnalyzeFile = (v) => updateC({ pennyDrafts: { analyzeFile: v } });
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const analyzeResult = contract.pennyDrafts?.complianceReview || '';
  // Per-exhibit compliance review state
  const [exhibitReviewLoading, setExhibitReviewLoading] = useState({}); // { A: bool, B: bool, D: bool }
  const setAnalyzeResult = (text) => updateC({ pennyDrafts: { complianceReview: text } });
  const analyzeRefs = contract.pennyDrafts?.complianceRefs || [];
  const setAnalyzeRefs = (updater) => {
    const current = contract.pennyDrafts?.complianceRefs || [];
    const next = typeof updater === 'function' ? updater(current) : updater;
    updateC({ pennyDrafts: { complianceRefs: next } });
  };

  // Exhibit B & D Studio state — selected templates (user picks which to use)
  const exBSelectedIds = contract.pennyDrafts?.exBSelectedIds ?? null;
  const setExBSelectedIds = (v) => updateC({ pennyDrafts: { exBSelectedIds: typeof v === 'function' ? v(contract.pennyDrafts?.exBSelectedIds ?? null) : v } });
  const exCSelectedIds = contract.pennyDrafts?.exCSelectedIds || null;
  const setExCSelectedIds = (v) => updateC({ pennyDrafts: { exCSelectedIds: v } });
  const [exBLoading, setExBLoading]         = useState(false);
  const exBFindings = contract.pennyDrafts?.exBFindings || [];
  const setExBFindings = (v) => updateC({ pennyDrafts: { exBFindings: typeof v === 'function' ? v(contract.pennyDrafts?.exBFindings || []) : v } });
  const exBChecked = contract.pennyDrafts?.exBChecked || {};
  const setExBChecked = (v) => updateC({ pennyDrafts: { exBChecked: typeof v === 'function' ? v(contract.pennyDrafts?.exBChecked || {}) : v } });
  const [exBDraftLoading, setExBDraftLoading] = useState(false);

  const [exCLoading, setExCLoading]         = useState(false);
  const exCFindings = contract.pennyDrafts?.exCFindings || [];
  const setExCFindings = (v) => updateC({ pennyDrafts: { exCFindings: typeof v === 'function' ? v(contract.pennyDrafts?.exCFindings || []) : v } });
  const setExCChecked = (v) => updateC({ pennyDrafts: { exCChecked: typeof v === 'function' ? v(contract.pennyDrafts?.exCChecked || {}) : v } });
  const exCChecked = contract.pennyDrafts?.exCChecked || {};
  const [exCDraftLoading, setExCDraftLoading] = useState(false);

  // Get templates matching this contract's type that have a given exhibit
  function getMatchingTemplates(exhibitKey) {
    const contractType = contract.category || contract.procMethod || 'Professional Services';
    const byType = templates.filter(t => t.type === contractType && t.exhibits?.includes(exhibitKey));
    // Fallback: if no matching type, use any template with that exhibit
    const matches = byType.length > 0 ? byType : templates.filter(t => t.exhibits?.includes(exhibitKey));
    // Sort canonical first within the matches — Penny prefers the agency's
    // blessed standard when one is set. Personal references still get
    // included as supplementary material. Stable sort preserves the existing
    // updated-at ordering within each tier.
    return matches.slice().sort((a, b) => {
      const aC = a.reference_kind === 'canonical' ? 1 : 0;
      const bC = b.reference_kind === 'canonical' ? 1 : 0;
      return bC - aC;
    });
  }

  // Estimate page count from base64 size (conservative 8KB/page for text PDFs).
  // Only used to surface a friendly "your library is huge" warning — actual
  // delivery uses Files API which has no inline page limit.
  function estimateTemplatePages(base64) {
    return Math.ceil(((base64.length * 3) / 4) / 8192);
  }

  // Load matching templates and return them as Anthropic Files API references
  // (`{ type: 'document', source: { type: 'file', file_id: '...' } }`) instead
  // of inline base64. Files API uploads bypass the ~100-page inline-PDF limit
  // entirely and avoid the base64-validity errors that occur when the storage
  // bucket holds a non-PDF file.
  //
  // Behavior:
  //   • Each template is uploaded to Files API once per session (cached in
  //     templateFileIdCacheRef).
  //   • If a template's bytes can't be parsed as a PDF (e.g. legacy docx
  //     uploaded under the old PDF-only flow), it's skipped with a clear
  //     warning rather than failing the whole call.
  //   • Returns the same shape as before: { docs, count, totalPages } so all
  //     four call sites work without changes.
  async function loadTemplatePdfs(tpls) {
    const docs = [];
    const skipped = [];
    let totalPages = 0;
    for (const tpl of tpls) {
      try {
        // Hit cache first
        let fileId = templateFileIdCacheRef.current[tpl.id]?.fileId;
        let b64 = null;
        if (!fileId) {
          // Fetch bytes from Supabase storage
          b64 = await templatesDb.getPdf(tpl.id);
          if (!b64) {
            skipped.push(tpl.name + ' (file missing in storage)');
            continue;
          }
          // PDFs start with "%PDF" → "JVBERi" in base64. If it's not a PDF
          // (e.g. a docx that was saved before the multi-format upload),
          // skip with a clear message rather than hitting the API and
          // getting a cryptic base64-data error.
          if (!b64.startsWith('JVBERi')) {
            skipped.push(tpl.name + ' (not a PDF — re-upload as PDF to use here)');
            continue;
          }
          fileId = await uploadToAnthropicFiles(apiKey, b64, tpl.fileName || tpl.name + '.pdf');
          templateFileIdCacheRef.current[tpl.id] = { fileId, uploadedAt: Date.now() };
        }
        // Track approx page count for diagnostics only — no longer enforced
        if (b64) totalPages += estimateTemplatePages(b64);
        docs.push({
          type: 'document',
          source: { type: 'file', file_id: fileId },
        });
      } catch (e) {
        skipped.push(tpl.name + ' (' + (e.message || 'upload failed') + ')');
      }
    }
    if (skipped.length > 0) {
      toast.warn(skipped.length + ' template' + (skipped.length > 1 ? 's' : '') + ' skipped: ' + skipped.join(', '));
    }
    return { docs, count: docs.length, totalPages };
  }

  // ── Analyze templates and extract exhibit B clauses ──
  async function runExBAnalysis() {
    const tpls = getMatchingTemplates('B');
    if (tpls.length === 0) {
      toast.warn('No templates with Exhibit B found. Add templates in the Template Library first.');
      return;
    }
    setExBLoading(true); setExBFindings([]); setExBChecked({});
    try {
      const { docs: templateDocs, count: tplCount } = await loadTemplatePdfs(tpls);
      const contractCtx = 'Contract: ' + contract.title + '\nBudget: ' + fmtMoney(contract.budget) + '\nType: ' + (contract.isIT === 'yes' ? 'IT' : 'Non-IT') + '\nMethod: ' + (contract.procMethod || 'to be determined') + '\nCategory: ' + (contract.category || 'Professional Services');
      const sys = 'You are Penny, the GoldenScope contracting assistant Penny. Analyze ALL of the uploaded sample contracts and extract a SYNTHESIZED set of budget detail and payment provision clauses for Exhibit B — Budget Detail and Payment Provisions. When multiple samples are provided, identify clauses that appear across multiple contracts (mark include:true) and clauses that appear in only one (mark include:false as optional). Return ONLY a valid JSON array:\n[{"id":1,"section":"Payment Schedule","clause":"Brief description of what this clause covers","include":true,"note":"Found in X of Y samples — or — specific note about this clause","category":"schedule|invoicing|rates|withholding|dispute|other"}]\nExtract 8-16 clauses synthesized across all samples. Set include:true for clauses found in majority of samples. Keep clause field under 150 chars.';
      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-haiku-4-5-20251001', max_tokens: 6000,
        messages: [{ role: 'user', content: [...templateDocs, { type: 'text', text: 'Extract all Exhibit B (Budget Detail and Payment Provisions) clauses from these sample contracts.\n\nNew contract context:\n' + contractCtx }] }],
        tag: 'exhibit_b_analysis',
        contract_id: contract.id,
      });
      const cleaned = raw.trim().replace(/^[\u0060]{3}[\w]*\n?/, '').replace(/\n?[\u0060]{3}$/, '').trim();
      const findings = JSON.parse(cleaned);
      const checked = {};
      findings.forEach(f => { checked[f.id] = f.include !== false; });
      setExBFindings(findings); setExBChecked(checked);
      toast.success(findings.length + ' Exhibit B clauses synthesized from ' + (tplCount || tpls.length) + ' template' + ((tplCount || tpls.length) > 1 ? 's' : ''));
    } catch (e) { toast.error('Analysis failed: ' + e.message); }
    finally { setExBLoading(false); }
  }

  // ── Draft Exhibit B from selected clauses ──
  // Two paths:
  // 1. Agency has a saved Exhibit B template (doc_template.exhibit_b_template):
  //    Penny customizes the agency baseline using the contract specifics. Skips
  //    the "select clauses" step entirely — the saved template IS the structure.
  // 2. No saved template: legacy from-scratch flow — Penny synthesizes Exhibit B
  //    from the user-selected clauses using sample templates as style guide.
  async function runExBDraft() {
    setExBDraftLoading(true);
    try {
      const exhibitBTemplate = agencyDocTemplate?.exhibit_b_template;
      const hasBaseline = exhibitBTemplate?.sections?.length > 0;

      if (hasBaseline) {
        // Path 1: Customize the agency baseline for this specific contract.
        // We send Penny the baseline sections (heading + text with placeholders),
        // along with contract specifics, and ask her to:
        //   • Substitute the placeholder tokens with real values
        //   • Add per-contract details (payment schedule based on deliverables,
        //     specific dollar breakdown, invoicing address if applicable)
        //   • Keep the overall structure and wording from the baseline
        const activeSections = exhibitBTemplate.sections.filter(s => s.active !== false);
        const baselineJson = JSON.stringify(activeSections.map(s => ({
          heading: s.heading, text: s.text,
        })), null, 2);

        const sys = `You are Penny, the GoldenScope contracting assistant. The agency has a pre-defined Exhibit B template (Budget Detail and Payment Provisions). Your job is to customize that template for THIS specific contract.

The baseline sections are provided as JSON. You MUST:
1. Keep the structure (same sections in the same order, with the same headings)
2. Keep the wording from the baseline as the foundation — change only what needs to change for this contract
3. CRITICAL — Replace "TBD" placeholders: The baseline text may use "TBD" as a stand-in for party names. You MUST replace EVERY "TBD" with the correct party:
     - When "TBD" refers to the STATE/AGENCY side → use "the State" or the agency name
     - When "TBD" refers to the CONTRACTOR side → use "Contractor"
     Read the surrounding context to determine which party each "TBD" refers to.
     There must be ZERO instances of "TBD" in the final output.
4. Leave these placeholder tokens AS-IS — the document builder resolves them automatically:
     {contractor_name}, {agreement_num}, {contract_title}, {agency_short_name}, {start_date}, {end_date}
   Do NOT replace these tokens with actual values or with "TBD" — leave them exactly as written.
5. DO expand these Penny-specific tokens with contract-appropriate content:
     {contract_total}      → total dollar amount from the contract specifics below
     {net_terms_days}      → use 30 unless contract specifies otherwise
     {invoicing_address}   → standard agency invoicing address (use a reasonable default if unknown)
     {payment_schedule}    → expand into a contract-appropriate payment schedule based on the budget and contract type
6. Where the baseline text uses generic placeholder wording in square brackets (e.g., "[describe payment schedule]" or "[insert deliverables breakdown]"), replace with contract-specific content
7. NEVER write "TBD", "[TBD]", or any bracketed placeholder text anywhere in the output.
8. Format output as plain text with section headings on their own lines (matching what the docx builder expects):
     1. PAYMENT SCHEDULE
     A.  Body text...
     B.  More text...
     2. INVOICING
     A.  ...

Do NOT include "EXHIBIT B" or "BUDGET DETAIL AND PAYMENT PROVISIONS" header lines — the docx builder adds those. Start directly with section 1.

Use formal California state contract voice throughout.` + languageRuleText(resolveProfile('B'));

        const contractCtx = 'Contract: ' + (contract.title || 'this contract') +
          '\nTotal: ' + fmtMoney(contract.budget) +
          '\nContractor: ' + (s215?.contractorName || 'the Contractor') +
          '\nAgreement No.: ' + (s215?.agreementNum || 'pending') +
          '\nEnd Date: ' + (s215?.endDate || 'to be determined') +
          '\nContract type: ' + (contract.isIT === 'yes' ? 'IT Services' : 'Non-IT Services');

        const userText = `BASELINE SECTIONS (customize these for the contract below):\n\n${baselineJson}\n\nCONTRACT SPECIFICS:\n${contractCtx}\n\nGenerate the customized Exhibit B following the rules in the system prompt.`;

        const rawTextB = await callAI(apiKey, {
          system: sys,
          model: 'claude-sonnet-4-6',
          max_tokens: 12000,
          messages: [{ role: 'user', content: [...getContractRefDocs(), ...getGeneralAttachmentDocs(), { type: 'text', text: userText }] }],
          tag: 'exhibit_b_customize',
          contract_id: contract.id,
        });
        // Post-process: replace any remaining "TBD" that Penny missed.
        // State-side TBDs should already be resolved; leftover = contractor.
        const text = rawTextB.replace(/\bTBD\b/g, 'Contractor');

        await buildAndDownloadDocx('Exhibit B — Budget Detail and Payment Provisions', text, profile, s215, contract, assignedCm, 'B', resolveProfile('B'));
        saveDoc('other', text, 'draft', ['Exhibit B — Budget Detail & Payment Provisions (from agency baseline)']);
        updateC({ pennyDrafts: { exBDraft: text, exBDraftAt: new Date().toISOString() } });
        toast.success('Exhibit B drafted from agency baseline');
        return;
      }

      // Path 2 (legacy): from-scratch flow using selected clauses from exBFindings
      const selected = exBFindings.filter(f => exBChecked[f.id]);
      if (selected.length === 0) { toast.warn('Select at least one clause.'); return; }
      const tpls = getMatchingTemplates('B');
      const { docs: templateDocs, count: tplCount } = await loadTemplatePdfs(tpls);
      const clauses = selected.map(f => '- [' + f.section + '] ' + f.clause).join('\n');
      const sys = 'You are Penny, the GoldenScope contracting assistant. Draft a complete Exhibit B — Budget Detail and Payment Provisions for this contract. Use the uploaded sample contract(s) as your style and formatting guide. Include ONLY the selected clauses. Use proper California state contract language throughout. Number each section. Be specific — include actual invoicing addresses, net-30 payment terms, and agency-specific requirements as shown in the samples. IMPORTANT: Never write "TBD", "[TBD]", or any bracketed placeholder text. If the contractor name is not yet known, use "Contractor" as a proper noun. Use {contractor_name} tokens where the contractor name would appear — the document builder fills them in automatically.' + languageRuleText(resolveProfile('B'));
      const msg = 'Contract: ' + contract.title + '\nBudget: ' + fmtMoney(contract.budget) + '\nContractor: ' + (s215?.contractorName || 'the Contractor') + '\nAgreement No.: ' + (s215?.agreementNum || 'pending') + '\n\nInclude these clauses:\n' + clauses;
      const rawTextBL = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: [...templateDocs, ...getContractRefDocs(), ...getGeneralAttachmentDocs(), { type: 'text', text: msg }] }],
        tag: 'exhibit_b_draft',
        contract_id: contract.id,
      });
      const text = rawTextBL.replace(/\bTBD\b/g, 'Contractor');
      await buildAndDownloadDocx('Exhibit B — Budget Detail and Payment Provisions', text, profile, s215, contract, assignedCm, 'B', resolveProfile('B'));
      saveDoc('other', text, 'draft', ['Exhibit B — Budget Detail & Payment Provisions', ...tpls.map(t => t.name)]);
      updateC({ pennyDrafts: { exBDraft: text, exBDraftAt: new Date().toISOString() } });
      toast.success('Exhibit B (Budget Detail & Payment Provisions) downloaded — open in Microsoft Word');
    } catch (e) { toast.error('Draft failed: ' + e.message); }
    finally { setExBDraftLoading(false); }
  }

  // ── Analyze templates and extract exhibit D clauses ──
  async function runExCAnalysis() {
    const tpls = getMatchingTemplates('D');
    if (tpls.length === 0) {
      toast.warn('No templates with Exhibit D (Special Terms and Conditions) found. Add templates in the Template Library first.');
      return;
    }
    setExCLoading(true); setExCFindings([]); setExCChecked({});
    try {
      const { docs: templateDocs, count: tplCount } = await loadTemplatePdfs(tpls);
      const sys = 'You are Penny, the GoldenScope contracting assistant Penny. Analyze ALL of the uploaded sample contracts and extract a SYNTHESIZED set of special terms and conditions provisions for Exhibit D — Special Terms and Conditions. When multiple samples are provided, identify provisions that appear consistently across contracts (mark include:true) and those that are contract-specific (mark include:false as optional). Return ONLY a valid JSON array:\n[{"id":1,"section":"Section name","clause":"Brief description of what this provision covers","include":true,"note":"Found in X of Y samples — or — specific note","category":"conflict|confidentiality|dvbe|data|sanctions|liability|termination|other"}]\nExtract all provisions synthesized across all samples (typically 10-18 total). Set include:true for provisions in majority of samples. Keep clause field under 150 chars.';
      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-haiku-4-5-20251001', max_tokens: 6000,
        messages: [{ role: 'user', content: [...templateDocs, { type: 'text', text: 'Extract all Exhibit D (Special Terms and Conditions) provisions from these sample contracts.' }] }],
        tag: 'exhibit_d_analysis',
        contract_id: contract.id,
      });
      const cleaned = raw.trim().replace(/^[\u0060]{3}[\w]*\n?/, '').replace(/\n?[\u0060]{3}$/, '').trim();
      const findings = JSON.parse(cleaned);
      const checked = {};
      findings.forEach(f => { checked[f.id] = f.include !== false; });
      setExCFindings(findings); setExCChecked(checked);
      toast.success(findings.length + ' Exhibit D provisions synthesized from ' + (tplCount || tpls.length) + ' template' + ((tplCount || tpls.length) > 1 ? 's' : ''));
    } catch (e) { toast.error('Analysis failed: ' + e.message); }
    finally { setExCLoading(false); }
  }

  // ── Draft Exhibit D from selected provisions ──
  async function runExCDraft() {
    setExCDraftLoading(true);
    try {
      const exhibitDTemplate = agencyDocTemplate?.exhibit_d_template;
      const hasBaseline = exhibitDTemplate?.sections?.length > 0;

      if (hasBaseline) {
        // Path 1: Customize the agency Exhibit D baseline for this contract.
        const activeSections = exhibitDTemplate.sections.filter(s => s.active !== false);
        const baselineJson = JSON.stringify(activeSections.map(s => ({
          heading: s.heading, text: s.text,
        })), null, 2);

        const sys = `You are Penny, the GoldenScope contracting assistant. The agency has a pre-defined Exhibit D template (Special Terms and Conditions). Your job is to customize it for THIS specific contract.

The baseline sections are provided as JSON. You MUST:
1. Keep the structure (same sections in the same order, with the same headings).
2. Keep the wording from the baseline as the foundation.
3. CRITICAL — Replace "TBD" placeholders: The baseline text uses "TBD" as a stand-in for party names. You MUST replace EVERY "TBD" with the correct party:
     - When "TBD" refers to the STATE/AGENCY side → use "the State" or the agency name
     - When "TBD" refers to the CONTRACTOR side → use "Contractor"
     Read the surrounding context to determine which party each "TBD" refers to. For example:
       "TBD may terminate this contract" (the state terminates) → "The State may terminate this contract"
       "giving notice to TBD" (notice goes to the contractor) → "giving notice to Contractor"
       "TBD agrees to use all reasonable efforts" (contractor agrees) → "Contractor agrees to use all reasonable efforts"
     There must be ZERO instances of "TBD" in the final output.
4. Leave these placeholder tokens AS-IS — the document builder resolves them automatically:
     {contractor_name}, {agreement_num}, {contract_title}, {agency_short_name}, {start_date}, {end_date}
   Do NOT replace these tokens with actual values or with "TBD" — leave them exactly as written.
5. Where the baseline uses generic descriptions in square brackets (e.g., "[describe termination process]"), replace with contract-appropriate language.
6. NEVER write "TBD", "[TBD]", or any bracketed placeholder text anywhere in the output.
7. Format output as plain text with section headings on their own lines:
     1. TERMINATION FOR CONVENIENCE
     A.  Body text...
     2. INDEMNIFICATION
     A.  ...

Do NOT include "EXHIBIT D" or "SPECIAL TERMS AND CONDITIONS" header lines — the docx builder adds those. Start directly with section 1.

Use formal California state contract voice throughout.` + languageRuleText(resolveProfile('D'));

        const contractCtx = 'Contract: ' + (contract.title || 'this contract') +
          '\nContractor: ' + (s215?.contractorName || 'the Contractor') +
          '\nAgreement No.: ' + (s215?.agreementNum || 'pending') +
          '\nEnd Date: ' + (s215?.endDate || 'to be determined');

        const userText = `BASELINE SECTIONS (customize these for the contract below):\n\n${baselineJson}\n\nCONTRACT SPECIFICS:\n${contractCtx}\n\nGenerate the customized Exhibit D following the rules in the system prompt.`;

        const rawText = await callAI(apiKey, {
          system: sys,
          model: 'claude-sonnet-4-6',
          max_tokens: 12000,
          messages: [{ role: 'user', content: [...getContractRefDocs(), ...getGeneralAttachmentDocs(), { type: 'text', text: userText }] }],
          tag: 'exhibit_d_customize',
          contract_id: contract.id,
        });
        // Post-process: replace any remaining "TBD" that Penny missed.
        // By this point, state-side TBDs should be resolved (Energy Safety /
        // the State). Any leftover TBD is a contractor reference.
        const text = rawText.replace(/\bTBD\b/g, 'Contractor');

        await buildAndDownloadDocx('Exhibit D — Special Terms and Conditions', text, profile, s215, contract, assignedCm, 'D', resolveProfile('D'));
        saveDoc('other', text, 'draft', ['Exhibit D — Special Terms and Conditions (from agency baseline)']);
        updateC({ pennyDrafts: { exDDraft: text, exDDraftAt: new Date().toISOString() } });
        toast.success('Exhibit D drafted from agency baseline');
        return;
      }

      // Path 2 (legacy): from-scratch flow using selected clauses
      const selected = exCFindings.filter(f => exCChecked[f.id]);
      if (selected.length === 0) { toast.warn('Select at least one provision.'); return; }
      const tpls = getMatchingTemplates('D');
      const { docs: templateDocs, count: tplCount } = await loadTemplatePdfs(tpls);
      const provisions = selected.map(f => '- [' + f.section + '] ' + f.clause).join('\n');
      const sys = 'You are Penny, the GoldenScope contracting assistant. Draft a complete Exhibit D — Special Terms and Conditions for this contract. Use the uploaded sample contract(s) as your exact style and language guide. Include ONLY the selected provisions. Use the same section numbering style as the samples. Copy the exact legal language from the samples where applicable — do not paraphrase required statutory provisions. IMPORTANT: Never write "TBD", "[TBD]", or any bracketed placeholder text. If the contractor name is not yet known, use "Contractor" as a proper noun. Use {contractor_name} tokens where the contractor name would appear — the document builder fills them in automatically.' + languageRuleText(resolveProfile('D'));
      const msg = 'Contract: ' + contract.title + '\nContractor: ' + (s215?.contractorName || 'the Contractor') + '\nAgreement No.: ' + (s215?.agreementNum || 'pending') + '\n\nInclude these provisions:\n' + provisions;
      const rawText = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: [...templateDocs, ...getContractRefDocs(), ...getGeneralAttachmentDocs(), { type: 'text', text: msg }] }],
        tag: 'exhibit_d_draft',
        contract_id: contract.id,
      });
      const text = rawText.replace(/\bTBD\b/g, 'Contractor');
      await buildAndDownloadDocx('Exhibit D — Special Terms and Conditions', text, profile, s215, contract, assignedCm, 'D', resolveProfile('D'));
      saveDoc('other', text, 'draft', ['Exhibit D — Special Terms and Conditions', ...tpls.map(t => t.name)]);
      updateC({ pennyDrafts: { exDDraft: text, exDDraftAt: new Date().toISOString() } });
      toast.success('Exhibit D (Special Terms and Conditions) downloaded — open in Microsoft Word');
    } catch (e) { toast.error('Draft failed: ' + e.message); }
    finally { setExCDraftLoading(false); }
  }

  const totalRefs = loadedRef + (contract.referenceDocs || []).length;
  const libNote = totalRefs > 0
    ? '\n\nReference documents loaded: ' + totalRefs + ' doc' + (totalRefs !== 1 ? 's' : '') + ' (' +
      [loadedRef > 0 ? loadedRef + ' SCM library' : '', (contract.referenceDocs || []).length > 0 ? (contract.referenceDocs || []).length + ' contract-specific' : ''].filter(Boolean).join(', ') +
      '). Reference and cite relevant sections.'
    : '';

  // Build SCM context note for system prompts — uses cached brief if available
  const scmContextNote = scmCacheValid
    ? '\n\nSCM COMPLIANCE BRIEF (from your uploaded ' + (contract.isIT === 'yes' ? 'SCM Vol 2 IT' : 'SCM Vol 1 Non-IT') + '):\n' + cachedScm.brief
    : '\n\nApply full ' + (contract.isIT === 'yes' ? 'SCM Vol 2 (IT Services)' : 'SCM Vol 1 (Non-IT Services)') + ' compliance requirements for a ' + (contract.procMethod || 'RFQ') + ' procurement.';

  // LPA context note — injected when method is any LPA type and brief exists
  const lpaVehicleDesc = contract.procMethod === 'CALNET'
    ? 'CALNET (California Network) — CDT-managed telecom and network services contract vehicle. No competitive solicitation needed. Orders placed directly against the CALNET master agreement. Pricing must be at or below approved CALNET rates. At least 3 vendor quotes required where applicable.'
    : contract.procMethod === 'SLP'
      ? 'SLP (Software Licensing Program) — DGS/OTech managed enterprise software licensing. No formal competitive solicitation needed, but a minimum of 3 authorized SLP suppliers must be contacted to obtain best-value offers (SCM Vol 2 §1608.1). Orders placed against approved SLP agreements — contract pricing is a maximum, negotiate lower where possible. Verify the specific software title and edition are covered under SLP before ordering.'
      : contract.procMethod === 'LPA-Other'
        ? 'LPA / Master Agreement — a leveraged procurement agreement or master agreement established competitively. No re-solicitation required. Orders placed directly against the master agreement terms. Document price reasonableness and vendor eligibility.'
        : 'CMAS (California Multiple Award Schedules) — leverages existing GSA/CMAS schedules. No competitive solicitation needed. The SOW must define services within allowable schedule categories. Document price reasonableness with at least 3 vendor quotes. Contractor price list and CMAS GTC replace Exhibit B and D.';

  const cmasContextNote = isCMAS && cmasContext?.brief
    ? '\n\nLPA / MASTER AGREEMENT BRIEF (from uploaded document "' + (cmasContext.fileName || 'LPA Document') + '"):\n' + cmasContext.brief + '\n\nThis is a ' + lpaLabel + ' procurement — no competitive solicitation is required. The SOW must describe services within the vehicle\'s allowable categories. Exhibit B and D are typically not required.'
    : isCMAS
      ? '\n\nThis is a ' + lpaLabel + ' procurement. ' + lpaVehicleDesc + ' STD 213 and STD 215 are required. No Exhibit B or D needed.'
      : '';



  // Extract method names from a rec draft output for the method picker
  function parseRecMethods(text) {
    if (!text) return null;
    // Look for "SELECTED METHOD: X" and "RECOMMENDED METHOD" / "Recommended Method: X"
    const selectedMatch = text.match(/SELECTED METHOD[:\s]+([^\n]+)/i);
    const recommendedMatch = text.match(/(?:PENNY'?S? RECOMMENDED METHOD|Recommended Method)[:\s*]*([^\n*]+)/i);
    const userMethod = selectedMatch ? selectedMatch[1].trim().replace(/[*#]/g, '').trim() : null;
    const pennyMethod = recommendedMatch ? recommendedMatch[1].trim().replace(/[*#\[\]]/g, '').trim() : null;
    // Only show picker if there's a comparison (user had pre-selected a method)
    if (userMethod && pennyMethod) {
      return { userMethod, pennyMethod, same: userMethod.toLowerCase() === pennyMethod.toLowerCase() };
    }
    // No pre-selection — just extract Penny's recommendation
    if (pennyMethod) {
      return { pennyMethod, userMethod: null, same: false };
    }
    return null;
  }

  // Build a human-readable list of all active references for Vault metadata
  function buildRefsList() {
    const refs = [];
    Object.entries(library).filter(([, v]) => v).forEach(([k, v]) => refs.push(v.name || k));
    (contract.referenceDocs || []).forEach(d => refs.push(d.name));
    return refs;
  }



  async function runSowDraft() {
    setSowLoading(true);
    setSowDraftText('');
    try {
      // Build context from everything Penny knows about this contract
      const description = contract.description || '';
      const requestDocAttachment = (contract.attachments || []).find(a => a.tag === 'Request Document');

      const contextParts = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Budget: ' + fmtMoney(contract.budget),
        'Category: ' + (contract.category || 'Professional Services'),
        'Procurement Method: ' + (contract.procMethod || 'to be determined'),
        'Contract Type: ' + (contract.isIT === 'yes' ? 'IT Services (SCM Vol 2)' : 'Non-IT Services (SCM Vol 1)'),
        description ? 'Contract Description: ' + description : '',
        sowNotes ? 'Requestor Reply (free text/uploaded):\n' + sowNotes : '',
        formatClarifyForPrompt() ? '\n--- CLARIFYING QUESTIONS & CONTEXT ---\n' +
          'Incorporate any answered/checked items directly into the SOW — do NOT leave [PLACEHOLDER] for items already resolved. Analyst notes marked "not yet reviewed" should still be treated as open compliance considerations to keep in mind while drafting:\n' +
          formatClarifyForPrompt() : '',
      ].filter(Boolean).join('\n');

      const sys = `You are Penny, the AI contracting assistant built into GoldenScope, assisting a contract specialist at the Office of Energy Infrastructure Safety (Energy Safety). Draft a complete, professional Exhibit A Scope of Work using all available context.

CRITICAL FORMATTING — follow this EXACT format. Every section heading, sub-letter, and item marker must appear on the SAME LINE as its content (or title). NEVER place a marker on a line by itself.

Format hierarchy (use these exact patterns):
  - Section heading line: "N. TITLE IN ALL CAPS" — number, period, space, title (e.g., "1. PURPOSE AND BACKGROUND")
  - Sub-section line:     "L.  text..." — capital letter, period, two spaces, content (e.g., "A.  The Office of Energy Infrastructure Safety requires...")
  - Item line:            "N)  text..." — digit, close-paren, two spaces, content (e.g., "1)  The Contractor will review...")
  - Sub-item line:        "l)  text..." — lowercase letter, close-paren, two spaces, content (e.g., "a)  Specific deliverable X")

VALID example (write your output exactly like this):

1. PURPOSE AND BACKGROUND
A.  The Office of Energy Infrastructure Safety (Energy Safety) requires professional fleet decal services for state-owned vehicles.
B.  This contract establishes an as-needed service agreement for comprehensive decal services including design review and installation.

2. SCOPE OF SERVICES
A.  Decal Design File Review
1)  The Contractor will review all design files provided by Energy Safety.
2)  The Contractor will identify any technical issues affecting print quality.
B.  Professional Vinyl Printing
1)  The Contractor will print decals using professional-grade vinyl materials.

3. DELIVERABLES
A.  Completed decal installations meeting all specified requirements.

INVALID — do NOT write it like this:

1.

A.

The Office of Energy Infrastructure Safety...

(The above is WRONG because the markers are on lines by themselves with no content.)

Additional rules:
- Do NOT include "EXHIBIT A", "SCOPE OF WORK", the contract title, "Agreement No.", or any cover/header lines. The docx builder adds these. Start your response DIRECTLY with "1. PURPOSE AND BACKGROUND".
- Do NOT use markdown (#, ##, **, *, backticks).
- Do NOT use "1.A" or "2.B.1" style concatenated markers. Use only the four marker types listed above.

DO NOT WRITE THESE SECTIONS — the system inserts them automatically:
- "Term of Performance" / "Term"
- "Contract Representatives"
The system inserts those as sections 2 and 3 in the final document. Number your sections continuously as 1, 2, 3, 4, 5, 6, 7, 8 — the system will renumber your sections 2-8 to become 4-10 in the final output.

Required sections to write: 1. Purpose and Background, 2. Scope of Services, 3. Deliverables, 4. Performance Standards, 5. Acceptance Criteria, 6. Contractor Responsibilities, 7. State Responsibilities, 8. Timeline and Milestones. If clarifying questions are provided in the context, treat answered items as confirmed requirements and incorporate directly — do not use [PLACEHOLDER] for those. Use [PLACEHOLDER] only for items with no available context. No Payment Terms or Invoicing sections (those belong in Exhibit B). Write in formal California state government contract language.` + libNote + scmContextNote + cmasContextNote + languageRuleText(resolveProfile('A'));

      // Build message content — include reference docs uploaded to the Documents
      // tab, plus a supplement doc if uploaded separately for this draft
      const userContent = [...getContractRefDocs(), ...getGeneralAttachmentDocs()];
      if (sowSupplementDoc?.base64) {
        // Defensive re-check — the upload handler validates this on the way
        // in, but documents saved before that validation existed could still
        // be sitting here. A genuinely unreadable PDF sent straight to
        // Anthropic causes the whole request to fail (and previously crashed
        // the Worker in a way that looked like a CORS error in the browser).
        const supplementPages = await getPdfPageCount(sowSupplementDoc.base64);
        if (supplementPages) {
          userContent.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: sowSupplementDoc.base64 } });
        } else {
          toast.warn('"' + sowSupplementDoc.name + '" couldn\'t be read as a valid PDF and was skipped for this draft — you may want to re-upload it.');
        }
      }
      userContent.push({ type: 'text', text: 'Draft a complete Exhibit A SOW using this contract context:\n\n' + contextParts });

      const text = await callAI(apiKey, {
        // Sonnet + 8000 tokens. Sonnet 4 supports up to 8192; we use 8000 for
        // headroom. Complex SOWs (20+ deliverables) can run 5000-7000 output
        // tokens. callAI now surfaces a toast warning if stop_reason='max_tokens'.
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 16000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'sow_draft',
        contract_id: contract.id,
      });

      setSowDraftText(text);
      offerSwitchToNewCandidate('penny', 'Penny draft');
      await buildAndDownloadDocx(contract.title || 'Professional Services', text, profile, s215, contract, assignedCm, 'A', resolveProfile('A'));
      toast.success('Exhibit A downloaded — review it, then use Step 2 to refine');
    } catch (e) { toast.error('Error: ' + e.message); }
    finally { setSowLoading(false); }
  }

  async function runSowReview(useDraftText = false) {
    if (!useDraftText && !sowReviewFile?.base64 && !sowReviewFile?.extractedText && !sowReviewFile?.storagePath) { toast.warn('Upload a SOW PDF or Word document first.'); return; }
    setSowReviewLoading(true); setSowFindings([]); setSowChecked({});
    try {
      const sys = 'You are a California state contracting specialist reviewing a Scope of Work for Energy Safety. Return ONLY a valid JSON array with no markdown. Each object must be complete and valid JSON. Keep issue and fix fields under 120 characters each.\n[{"id":1,"category":"Structure","issue":"Short problem description","fix":"Specific fix to apply","priority":"high|medium|low"},...]\nCategories: Structure, Deliverables, Performance Standards, Acceptance Criteria, Compliance, Clarity, Completeness, Clarifying Question Follow-up.\nUse "Clarifying Question Follow-up" specifically when the analyst-provided context below (change notes or clarifying question replies) indicates something should be reflected in the document but isn\'t, or appears inconsistent with what was actually written.\nReturn exactly 8-12 findings. Priority: high=must fix, medium=should fix, low=nice to have. IMPORTANT: Ensure the JSON array is complete and properly closed with ].';

      const clarifyCtx = formatClarifyForPrompt();
      const reviewContext = [
        sowReviewNotes ? 'ANALYST NOTES ON THIS REVISION (what changed, or what to check/incorporate):\n' + sowReviewNotes : '',
        clarifyCtx ? 'CLARIFYING QUESTION CONTEXT (verify these are reflected in the document):\n' + clarifyCtx : '',
      ].filter(Boolean).join('\n\n');
      const reviewInstruction = 'Review this Scope of Work for Energy Safety.' + (reviewContext ? ' Pay particular attention to whether the analyst notes and clarifying-question context below are actually reflected in the document.' : '') + ' Return the JSON findings array only — nothing else.' + (reviewContext ? '\n\n' + reviewContext : '');

      // Build message content — draft text, extracted docx text, or uploaded/stored PDF
      const resolvedBase64 = (!useDraftText && !sowReviewFile.extractedText) ? await resolveSowReviewBase64() : null;
      const userContent = useDraftText
        ? [{ type: 'text', text: reviewInstruction + '\n\nSOW TEXT:\n' + sowDraftText }]
        : sowReviewFile.extractedText
        ? [{ type: 'text', text: reviewInstruction + '\n\nSOW TEXT:\n' + sowReviewFile.extractedText }]
        : resolvedBase64
        ? [
            { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: resolvedBase64 } },
            { type: 'text', text: reviewInstruction },
          ]
        : (() => { throw new Error('Could not retrieve the uploaded document — try re-uploading it.'); })();
      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-haiku-4-5-20251001', max_tokens: 6000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'sow_review',
        contract_id: contract.id,
      });

      // Clean and parse — with fallback for truncated responses
      let cleaned = raw.trim().replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '').trim();

      let findings;
      try {
        findings = JSON.parse(cleaned);
      } catch (parseErr) {
        // Attempt to salvage truncated JSON by extracting complete objects
        const matches = [];
        const objPattern = /\{[^{}]*"id"\s*:\s*(\d+)[^{}]*"priority"\s*:\s*"[^"]*"[^{}]*\}/g;
        let m;
        while ((m = objPattern.exec(cleaned)) !== null) {
          try { matches.push(JSON.parse(m[0])); } catch (_) {}
        }
        if (matches.length > 0) {
          findings = matches;
          toast.warn('Response was truncated — showing ' + matches.length + ' complete findings.');
        } else {
          // Last resort: try adding closing bracket to truncated array
          const truncFix = cleaned.replace(/,?\s*\{[^}]*$/, '') + ']';
          findings = JSON.parse(truncFix);
        }
      }

      if (!Array.isArray(findings) || findings.length === 0) {
        throw new Error('No findings returned. Try a shorter document or try again.');
      }

      const checked = {};
      findings.forEach(f => { checked[f.id] = f.priority === 'high'; });
      setSowFindings(findings); setSowChecked(checked);
      toast.success(findings.length + ' findings identified');
    } catch (e) {
      toast.error('Analysis failed: ' + e.message);
    }
    finally { setSowReviewLoading(false); }
  }

  async function runSowFinal() {
    const approved = sowFindings.filter(f => sowChecked[f.id]);
    if (approved.length === 0) { toast.warn('Select at least one recommendation.'); return; }
    setSowFinalLoading(true);
    try {
      const fixes = approved.map(f => f.id + '. [' + f.category + '] ' + f.fix).join('\n');
      const sys = `You are Penny, the GoldenScope contracting assistant. Rewrite the SOW as a polished Exhibit A applying ALL listed fixes.

CRITICAL FORMATTING — every marker must appear on the SAME LINE as its content (or title). NEVER place a marker on a line by itself.

Format (exact patterns):
  - Section heading: "N. TITLE IN ALL CAPS" (e.g., "1. PURPOSE AND BACKGROUND")
  - Sub-section: "L.  text..." (e.g., "A.  The Office requires...")
  - Item: "N)  text..." (e.g., "1)  Contractor will...")
  - Sub-item: "l)  text..." (e.g., "a)  Specific point...")

VALID:
1. PURPOSE AND BACKGROUND
A.  The Office of Energy Infrastructure Safety requires...
B.  This contract establishes...
2. SCOPE OF SERVICES
A.  File Review
1)  The Contractor will review files.

INVALID (do NOT do this):
1.

A.

The Office of Energy Infrastructure Safety...

Additional rules:
- Do NOT include "EXHIBIT A", "SCOPE OF WORK", contract title, "Agreement No.", or header lines. Start DIRECTLY with "1. PURPOSE AND BACKGROUND".
- No markdown (#, ##, **, *, backticks).
- No concatenated markers like "1.A" or "2.B.1" — use only the four marker types above.

DO NOT WRITE "Term of Performance" or "Contract Representatives" sections — the system adds those automatically as sections 2 and 3 of the final output. Number your sections continuously as 1-8: 1. Purpose and Background, 2. Scope of Services, 3. Deliverables, 4. Performance Standards, 5. Acceptance Criteria, 6. Contractor Responsibilities, 7. State Responsibilities, 8. Timeline and Milestones. The system will renumber your sections 2-8 to become 4-10. No Payment Terms (Exhibit B).` + languageRuleText(resolveProfile('A'));
      // Use uploaded PDF or extracted docx text if available, otherwise use draft text
      const rewriteContext = [
        sowReviewNotes ? 'ANALYST NOTES ON THIS REVISION (what changed, or what to check/incorporate):\n' + sowReviewNotes : '',
        formatClarifyForPrompt() ? 'CLARIFYING QUESTION CONTEXT (ensure these are reflected in the rewrite):\n' + formatClarifyForPrompt() : '',
      ].filter(Boolean).join('\n\n');
      const rewriteInstruction = 'Rewrite this SOW applying these approved fixes:\n' + fixes + '\n\nContract: ' + (contract.title || 'this contract') + '\nBudget: ' + fmtMoney(contract.budget) + (rewriteContext ? '\n\n' + rewriteContext : '');
      const resolvedRewriteBase64 = (!sowReviewFile?.extractedText && sowReviewFile) ? await resolveSowReviewBase64() : null;
      const finalContent = sowReviewFile?.extractedText
        ? [{ type: 'text', text: rewriteInstruction + '\n\nSOW TEXT:\n' + sowReviewFile.extractedText }]
        : resolvedRewriteBase64
        ? [
            { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: resolvedRewriteBase64 } },
            { type: 'text', text: rewriteInstruction },
          ]
        : [{ type: 'text', text: rewriteInstruction + '\n\nSOW TEXT:\n' + sowDraftText }];
      const text = await callAI(apiKey, {
        // Sonnet + 8000 tokens (same headroom as initial draft)
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 16000,
        messages: [{ role: 'user', content: [...getContractRefDocs(), ...getGeneralAttachmentDocs(), ...finalContent] }],
        tag: 'sow_rewrite',
        contract_id: contract.id,
      });
      setSowDraftText(text);
      await buildAndDownloadDocx(contract.title || 'Professional Services', text, profile, s215, contract, assignedCm, 'A', resolveProfile('A'));
      toast.success('Revised Exhibit A saved as current draft and downloaded — you can review it again, mark it ready, or continue refining');
    } catch (e) { toast.error('Error: ' + e.message); }
    finally { setSowFinalLoading(false); }
  }

  // ── Exhibit B Review — upload revised doc, Penny checks for issues ──
  async function runExBReview() {
    if (!exBReviewFile?.base64) { toast.warn('Upload a revised Exhibit B PDF first.'); return; }
    setExBReviewLoading(true);
    setExBReviewFindings([]);
    setExBReviewChecked({});
    try {
      const exhibitBTemplate = agencyDocTemplate?.exhibit_b_template;
      const hasBaseline = exhibitBTemplate?.sections?.length > 0;
      const baselineContext = hasBaseline
        ? '\n\nAGENCY BASELINE SECTIONS (the standard your agency uses):\n' +
          exhibitBTemplate.sections.filter(s => s.active !== false).map(s => s.heading + '\n' + s.text).join('\n\n')
        : '';

      const sys = 'You are Penny, a California state contracting specialist reviewing a revised Exhibit B — Budget Detail and Payment Provisions. Compare the uploaded document against California procurement requirements (SCM Vol 1) and the agency baseline template (if provided). Return ONLY a valid JSON array — no markdown, no preamble.\n' +
        '[{"id":1,"category":"Payment Terms","issue":"Short problem description","fix":"Specific fix to apply","priority":"high|medium|low"},...]\n' +
        'Categories: Payment Terms, Invoicing, Rates, Withholding, Budget Detail, Compliance, Consistency, Completeness.\n' +
        'Return 5-12 findings. Priority: high=must fix before submission, medium=should fix, low=improvement.\n' +
        'Check for: missing required clauses, inconsistencies with the agency baseline, incorrect payment terms, missing budget detail, non-standard language, SCM compliance gaps.\n' +
        'IMPORTANT: Ensure the JSON array is complete and properly closed with ].' +
        baselineContext + scmContextNote;

      const userContent = [
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: exBReviewFile.base64 } },
        { type: 'text', text: 'Review this Exhibit B for ' + (contract.title || 'this contract') + '. Contract budget: ' + fmtMoney(contract.budget) + '. Contractor: ' + (s215?.contractorName || 'the Contractor') + '. Return the JSON findings array only.' },
      ];

      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'exhibit_b_review',
        contract_id: contract.id,
      });

      const findings = parseReviewFindings(raw);
      const checked = {};
      findings.forEach(f => { checked[f.id] = f.priority === 'high'; });
      setExBReviewFindings(findings);
      setExBReviewChecked(checked);
      toast.success(findings.length + ' findings identified for Exhibit B');
    } catch (e) {
      toast.error('Exhibit B review failed: ' + e.message);
    } finally {
      setExBReviewLoading(false);
    }
  }

  // Rewrite Exhibit B applying selected fixes
  async function runExBRewrite() {
    const approved = exBReviewFindings.filter(f => exBReviewChecked[f.id]);
    if (approved.length === 0) { toast.warn('Select at least one finding to apply.'); return; }
    setExBRewriteLoading(true);
    try {
      const fixes = approved.map(f => f.id + '. [' + f.category + '] ' + f.fix).join('\n');
      const sys = 'You are Penny, the GoldenScope contracting assistant. Rewrite the uploaded Exhibit B — Budget Detail and Payment Provisions applying ALL listed fixes. Keep the existing structure and section numbering. Use formal California state contract language. Do NOT include "EXHIBIT B" or "BUDGET DETAIL AND PAYMENT PROVISIONS" header lines — the docx builder adds those.' + scmContextNote + languageRuleText(resolveProfile('B'));
      const userContent = [
        ...getContractRefDocs(), ...getGeneralAttachmentDocs(),
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: exBReviewFile.base64 } },
        { type: 'text', text: 'Rewrite this Exhibit B applying these fixes:\n' + fixes + '\n\nContract: ' + (contract.title || 'this contract') + '\nBudget: ' + fmtMoney(contract.budget) },
      ];
      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 12000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'exhibit_b_rewrite',
        contract_id: contract.id,
      });
      await buildAndDownloadDocx('Exhibit B — Budget Detail and Payment Provisions', text, profile, s215, contract, assignedCm, 'B', resolveProfile('B'));
      saveDoc('other', text, 'draft', ['Exhibit B — Revised per review findings']);
      updateC({ pennyDrafts: { exBDraft: text, exBDraftAt: new Date().toISOString() } });
      toast.success('Revised Exhibit B downloaded');
    } catch (e) { toast.error('Rewrite failed: ' + e.message); }
    finally { setExBRewriteLoading(false); }
  }

  // ── Exhibit D Review — upload revised doc, Penny checks for issues ──
  async function runExDReview() {
    if (!exDReviewFile?.base64) { toast.warn('Upload a revised Exhibit D PDF first.'); return; }
    setExDReviewLoading(true);
    setExDReviewFindings([]);
    setExDReviewChecked({});
    try {
      const exhibitDTemplate = agencyDocTemplate?.exhibit_d_template;
      const hasBaseline = exhibitDTemplate?.sections?.length > 0;
      const baselineContext = hasBaseline
        ? '\n\nAGENCY BASELINE SECTIONS (the standard your agency uses):\n' +
          exhibitDTemplate.sections.filter(s => s.active !== false).map(s => s.heading + '\n' + s.text).join('\n\n')
        : '';

      const sys = 'You are Penny, a California state contracting specialist reviewing a revised Exhibit D — Special Terms and Conditions. Compare the uploaded document against California procurement requirements (SCM Vol 1) and the agency baseline template (if provided). Return ONLY a valid JSON array — no markdown, no preamble.\n' +
        '[{"id":1,"category":"Termination","issue":"Short problem description","fix":"Specific fix to apply","priority":"high|medium|low"},...]\n' +
        'Categories: Termination, Indemnification, Confidentiality, IP/Work Product, Insurance, Compliance, Force Majeure, Audit/Records, Consistency, Completeness.\n' +
        'Return 5-12 findings. Priority: high=must fix before submission, medium=should fix, low=improvement.\n' +
        'Check for: missing required provisions, inconsistencies with the agency baseline, non-standard language, missing statutory requirements, SCM compliance gaps.\n' +
        'IMPORTANT: Ensure the JSON array is complete and properly closed with ].' +
        baselineContext + scmContextNote;

      const userContent = [
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: exDReviewFile.base64 } },
        { type: 'text', text: 'Review this Exhibit D for ' + (contract.title || 'this contract') + '. Contractor: ' + (s215?.contractorName || 'the Contractor') + '. Return the JSON findings array only.' },
      ];

      const raw = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'exhibit_d_review',
        contract_id: contract.id,
      });

      const findings = parseReviewFindings(raw);
      const checked = {};
      findings.forEach(f => { checked[f.id] = f.priority === 'high'; });
      setExDReviewFindings(findings);
      setExDReviewChecked(checked);
      toast.success(findings.length + ' findings identified for Exhibit D');
    } catch (e) {
      toast.error('Exhibit D review failed: ' + e.message);
    } finally {
      setExDReviewLoading(false);
    }
  }

  // Rewrite Exhibit D applying selected fixes
  async function runExDRewrite() {
    const approved = exDReviewFindings.filter(f => exDReviewChecked[f.id]);
    if (approved.length === 0) { toast.warn('Select at least one finding to apply.'); return; }
    setExDRewriteLoading(true);
    try {
      const fixes = approved.map(f => f.id + '. [' + f.category + '] ' + f.fix).join('\n');
      const sys = 'You are Penny, the GoldenScope contracting assistant. Rewrite the uploaded Exhibit D — Special Terms and Conditions applying ALL listed fixes. Keep the existing structure and section numbering. Use formal California state contract language. Do NOT include "EXHIBIT D" or "SPECIAL TERMS AND CONDITIONS" header lines — the docx builder adds those.' + scmContextNote + languageRuleText(resolveProfile('D'));
      const userContent = [
        ...getContractRefDocs(), ...getGeneralAttachmentDocs(),
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: exDReviewFile.base64 } },
        { type: 'text', text: 'Rewrite this Exhibit D applying these fixes:\n' + fixes + '\n\nContract: ' + (contract.title || 'this contract') + '\nContractor: ' + (s215?.contractorName || 'the Contractor') },
      ];
      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 12000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'exhibit_d_rewrite',
        contract_id: contract.id,
      });
      await buildAndDownloadDocx('Exhibit D — Special Terms and Conditions', text, profile, s215, contract, assignedCm, 'D', resolveProfile('D'));
      saveDoc('other', text, 'draft', ['Exhibit D — Revised per review findings']);
      updateC({ pennyDrafts: { exDDraft: text, exDDraftAt: new Date().toISOString() } });
      toast.success('Revised Exhibit D downloaded');
    } catch (e) { toast.error('Rewrite failed: ' + e.message); }
    finally { setExDRewriteLoading(false); }
  }

  // Shared JSON findings parser — handles truncated responses the same way
  // SOW review does (extract complete objects, try closing bracket, etc.)
  function parseReviewFindings(raw) {
    let cleaned = raw.trim().replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '').trim();
    let findings;
    try {
      findings = JSON.parse(cleaned);
    } catch (parseErr) {
      const matches = [];
      const objPattern = /\{[^{}]*"id"\s*:\s*(\d+)[^{}]*"priority"\s*:\s*"[^"]*"[^{}]*\}/g;
      let m;
      while ((m = objPattern.exec(cleaned)) !== null) {
        try { matches.push(JSON.parse(m[0])); } catch (_) {}
      }
      if (matches.length > 0) {
        findings = matches;
      } else {
        const truncFix = cleaned.replace(/,?\s*\{[^}]*$/, '') + ']';
        findings = JSON.parse(truncFix);
      }
    }
    if (!Array.isArray(findings) || findings.length === 0) {
      throw new Error('No findings returned. Try again.');
    }
    return findings;
  }

  // Estimate PDF page count from base64 size (rough: ~50KB per page for typical PDFs)
  function estimatePages(base64) {
    // Conservative: assume 8KB per page for text-based PDFs (SCM docs are very text-dense)
    const bytes = (base64.length * 3) / 4;
    return Math.ceil(bytes / 8192);
  }

  // Filter docs to those safely under the 100-page API limit
  function safeDocs(docs) {
    const safe = [];
    const skipped = [];
    for (const doc of docs) {
      const b64 = doc?.source?.base64 || doc?.source?.data || '';
      const pages = estimatePages(b64);
      if (pages <= 95) {
        safe.push(doc);
      } else {
        skipped.push(pages);
      }
    }
    if (skipped.length > 0) {
      toast.warn(
        skipped.length + ' reference doc' + (skipped.length > 1 ? 's' : '') +
        ' skipped — estimated ' + skipped.join('/') + ' pages (API limit is 100). ' +
        'Upload just the relevant chapters as separate PDFs for full coverage.'
      );
    }
    return safe;
  }

  // Library docs (SCM Vol 1/2) are always large — use text context only.
  // Only send contract-specific reference docs the user uploaded for this contract.
  // If a page range is set, it means the doc is large — skip it here (page extraction is async, handled separately).
  // Docs without a page range and under the size limit are sent directly.
  function getContractRefDocs() {
    // Note: sync version uses size estimate — for accurate page counts use getContractRefDocsAsync
    // Only sends small docs (estimate under 80 pages to be safe) without page ranges
    return (contract.referenceDocs || [])
      .filter(d => d.base64 && !d.pageStart)
      .filter(d => Math.ceil((d.base64.length * 0.75) / 51200) <= 80) // conservative estimate
      .map(d => ({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: d.base64 } }));
  }

  // Documents uploaded via the actual Documents tab (contract.general_attachments)
  // — previously these only ever reached Penny as a filename label ("[PDF
  // uploaded]"), never their actual content. Handles both PDF/base64 uploads
  // and text-extracted .docx uploads (isText/content, same shape used for
  // Penny-drafted documents elsewhere in this file).
  function getGeneralAttachmentDocs() {
    const atts = contract.general_attachments || [];
    const blocks = [];
    for (const a of atts) {
      if (a.isText && a.content) {
        blocks.push({ type: 'text', text: 'UPLOADED DOCUMENT "' + (a.name || 'Unnamed') + '":\n' + a.content.slice(0, 12000) });
      } else if (a.base64 && Math.ceil((a.base64.length * 0.75) / 51200) <= 80) {
        blocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: a.base64 } });
      }
    }
    return blocks;
  }

  // Async version that extracts page ranges for large docs
  async function getContractRefDocsAsync() {
    const docs = [];
    for (const d of (contract.referenceDocs || [])) {
      if (!d.base64) continue;
      const pages = Math.ceil((d.base64.length * 0.75) / 51200);
      if (d.pageStart && d.pageEnd) {
        const extracted = await extractPdfPages(d.base64, d.pageStart, d.pageEnd);
        if (extracted?.isImages) {
          extracted.pageImages.forEach(imgB64 => {
            docs.push({ type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: imgB64 } });
          });
        }
      } else if (pages <= 95) {
        docs.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: d.base64 } });
      }
    }
    return docs;
  }

  async function runDraft(type) {
    setDraftLoading(p => ({ ...p, [type]: true }));
    const src = sourceDocs[type];

    // Library docs (SCM Vol 1/2) are excluded from all PDF sends — they're reliably over 100 pages.
    // libNote already tells Penny to apply SCM knowledge from training.
    // Only contract-specific reference docs the user uploaded are sent as PDFs (user controls the size).
    const refDocs = type === 'rec'
      ? []
      : safeDocs(getContractRefDocs());

    const srcDoc = src
      ? [{ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: src.base64 } }]
      : [];

    const allDocs = [...refDocs, ...srcDoc];

    const srcNote = src ? `\n\nUser uploaded their existing draft (${src.name}). Use it as the BASE — preserve their content and intent, improve structure, fix compliance issues, add any missing required elements.` : '';
    const exS = (contract.exhibits || []).length > 0 ? '\n\nExhibits: ' + (contract.exhibits || []).map(e => 'Exhibit ' + e.label + ': ' + e.name).join(', ') : '';

    // Pre-compute solicitation attachment list for prompt
    const solAttachList = (() => {
      const _budget = parseFloat(contract.budget) || 0;
      const _isIT = contract.isIT === 'yes';
      const _method = contract.procMethod || '';
      const _isCompetitive = ['IFB', 'RFQ', 'RFP-Primary', 'RFP-Secondary'].includes(_method);
      const _overrides = contract.pennyDrafts?.solAttachOverrides || {};
      const _atts = [
        { form: 'DGS PD 05-105', title: 'Bidder Declaration', scm: 'SCM Sec. 5.2', defaultOn: _isCompetitive,
          url: 'https://www.documents.dgs.ca.gov/dgs/fmc/gs/pd/gspd05-105.pdf' },
        { form: 'Darfur Certification', title: 'Darfur Contracting Act Certification', scm: 'PCC 10475', defaultOn: _budget >= 1000000,
          url: 'https://www.documents.dgs.ca.gov/dgs/FMC/GS/PD/PD_1.pdf' },
        { form: 'Iran Certification', title: 'Iran Contracting Act Certification', scm: 'PCC 2202', defaultOn: _budget >= 1000000,
          url: 'https://www.dgs.ca.gov/-/media/Divisions/PD/PTCS/OPPL/SCM/Iran_Contracting_Act_Verification_Form.pdf' },
        { form: 'STD 19', title: 'Nondiscrimination Compliance Statement', scm: 'GC 12990', defaultOn: true,
          url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std019.pdf' },
        { form: 'STD 843', title: 'DVBE Declarations', scm: 'MVC 999.5', defaultOn: !!(contract.dvbe?.dvbeGoalStatus === 'applies' || contract.dvbe?.dvbeIncentiveStatus === 'applies' || ((!contract.dvbe?.dvbeGoalStatus && !contract.dvbe?.dvbeIncentiveStatus) && _isCompetitive)),
          url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std843.pdf' },
        { form: 'STD 204', title: 'Payee Data Record', scm: 'SCM Sec. 7.1', defaultOn: (contract.category||'').toLowerCase().includes('consult'),
          url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std204.pdf' },
      ];
      const _included = _atts.filter(a => (a.form in _overrides ? _overrides[a.form] : a.defaultOn));
      const _custom = contract.pennyDrafts?.solAttachCustom || [];
      const _all = [
        ...(_included.map(a => '- ' + a.title + ' (' + a.form + ') — ' + a.scm + (a.url ? ' — Download form: ' + a.url : ''))),
        ...(_custom.map(a => '- ' + a.title + ' (' + a.form + ')' + (a.url ? ' — Download form: ' + a.url : ''))),
      ];
      return _all.length > 0 ? _all.join('\n') : 'No specific attachments selected — include standard terms only.';
    })();

    const systems = {
      rec: (() => {
        const hasMethod = !!(contract.procMethod);
        const basePrompt = 'You are Penny, a California state contracting specialist for the Office of Energy Infrastructure Safety (Energy Safety), Admin Services Division. ' +
          'Write a formal Procurement Method Recommendation memo. The contract description and title are the primary inputs — assess complexity, risk level, market competitiveness, and appropriate method. ' +
          'You do NOT need a completed SOW. Work from the description provided.\n\n';

        const methodSection = hasMethod
          ? '## SELECTED METHOD: ' + contract.procMethod + '\n' +
            '**SCM Reference:** [cite applicable SCM Vol 1 or Vol 2 section]\n\n' +
            '## PENNY\'S RECOMMENDED METHOD\n' +
            '**Recommended Method:** [your independent recommendation based on the contract details]\n' +
            '**SCM Reference:** [cite applicable section]\n\n' +
            '## COMPARISON: ' + contract.procMethod + ' vs. PENNY\'S RECOMMENDATION\n' +
            'If the selected method and recommended method are the same, validate the choice with specific reasoning. ' +
            'If they differ, provide an honest side-by-side comparison:\n' +
            '**' + contract.procMethod + ' — Pros:** [specific advantages for this contract]\n' +
            '**' + contract.procMethod + ' — Cons:** [specific risks or limitations]\n' +
            '**[Recommended Method] — Pros:** [specific advantages]\n' +
            '**[Recommended Method] — Cons:** [specific risks or limitations]\n' +
            '**Penny\'s Opinion:** Give a direct, honest assessment of which method better serves this contract and why. ' +
            'Do not hedge — make a clear recommendation.\n\n'
          : '## PROCUREMENT METHOD RECOMMENDATION\n' +
            '**Recommended Method:** [Method name]\n' +
            '**SCM Reference:** [SCM Vol 1 or Vol 2, section number]\n\n';

        return basePrompt + methodSection +
          '## THRESHOLD ANALYSIS\n' +
          'Compare budget against PAAL thresholds. State whether DGS/OLS routing is required.\n\n' +
          '## JUSTIFICATION\n' +
          'Explain why the recommended method fits this contract — competitiveness, market availability, urgency, exemptions.\n\n' +
          '## KEY REQUIREMENTS\n' +
          'List procedural requirements for the recommended method (advertising period, minimum bids, evaluation criteria, etc.).\n\n' +
          '## RISKS & CONSIDERATIONS\n' +
          'Flag risks, required approvals, or items to resolve before proceeding.\n\n' +
          '## RECOMMENDED NEXT STEPS\n' +
          'List immediate next steps in sequence.' +
          libNote + scmContextNote + cmasContextNote + srcNote;
      })(),
      sol: 'You are Penny, the GoldenScope contracting assistant. Draft a complete ' + (contract.procMethod || 'RFQ') + ' solicitation document for CalEProcure. Format using numbered sections (1. 2. 3.) and lettered sub-sections (A. B. C.) only — do NOT use markdown (#, ##, **, *). Use [PLACEHOLDER] for items requiring completion. Include all required sections per SCM: 1. Introduction and Purpose  2. Scope of Work Summary  3. Minimum Qualifications  4. Proposal Requirements  5. Evaluation Criteria  6. Budget and Payment Terms  7. Timeline and Key Dates  8. Submission Instructions  9. Terms and Conditions  10. Required Attachments and Certifications.\n\nIMPORTANT — Use the drafted exhibits provided below as the source of truth, but SUMMARIZE them — do NOT reproduce them verbatim:\n- Section 2 (SOW Summary): Write a concise 1-2 paragraph summary of the Exhibit A scope, key deliverables, and objectives. Do NOT copy the full SOW — the solicitation references it as an attachment.\n- Section 5 (Evaluation Criteria): Derive 4-6 weighted criteria from the SOW deliverables. Keep it concise.\n- Section 6 (Budget and Payment Terms): Summarize the Exhibit B payment structure in 1-2 paragraphs. Reference "See Exhibit B for full details."\n- Section 9 (Terms and Conditions): Briefly note key Exhibit D provisions. Reference "See Exhibit D for full Special Terms and Conditions."\nIf an exhibit has not been drafted yet, note that the section will be finalized when the exhibit is complete.\n\nDVBE AND SB PARTICIPATION — Goal and Incentive are set INDEPENDENTLY for each program (per the real STD 816 form, an agency can waive the DVBE goal while keeping the incentive, or waive either/both — never assume one implies the other). The analyst has determined:\n' + (function() {
        const statusText = (status, applyText, waivedText, notReqText) =>
          status === 'applies' ? applyText
          : status === 'waived' ? waivedText
          : status === 'not_required' ? notReqText
          : 'this has not yet been determined — use [PLACEHOLDER] wherever this specific language would normally appear, rather than assuming it applies.';
        return '- DVBE GOAL: ' + statusText(
          contract.dvbe?.dvbeGoalStatus,
          'a ' + (s215?.dvbePct || 3) + '% DVBE participation goal APPLIES. Include DVBE goal language in the appropriate sections (Minimum Qualifications and/or Terms and Conditions).',
          'the DVBE Program Requirement (goal) has been WAIVED per an approved STD 816. Do NOT include DVBE goal language.',
          'DVBE participation is NOT required for this procurement method. Do NOT include DVBE goal language.'
        ) + '\n- DVBE INCENTIVE: ' + statusText(
          contract.dvbe?.dvbeIncentiveStatus,
          'the DVBE bid-scoring incentive APPLIES, independent of whether the goal applies. ' + (
            contract.dvbe?.dvbeIncentiveStructure === 'custom' && contract.dvbe?.dvbeIncentiveCustomText
              ? 'Use this analyst-specified incentive structure: "' + contract.dvbe.dvbeIncentiveCustomText + '". State it clearly in the solicitation.'
              : 'This uses the STANDARD DGS scale. Do NOT write out the scale or a table yourself — instead, insert the EXACT literal text `' + DVBE_INCENTIVE_TOKEN + '` on its own line at the point in the document where the DVBE incentive schedule belongs (typically within Evaluation Criteria or Terms and Conditions). The app will automatically replace that token with the correct, properly-formatted table. Do not add any other DVBE incentive scale text around it beyond a brief introductory sentence.'
          ),
          'the DVBE incentive has been WAIVED per an approved STD 816. Do NOT include DVBE incentive language.',
          'the DVBE incentive is NOT applicable for this procurement method. Do NOT include DVBE incentive language.'
        ) + '\n- SB GOAL: ' + statusText(
          contract.dvbe?.sbGoalStatus,
          'a ' + (s215?.sbPct ?? 25) + '% SB participation goal APPLIES. Include SB goal language in the appropriate sections.',
          'the SB goal has been WAIVED for this contract. Do NOT include SB goal language.',
          'SB participation is NOT required for this procurement method. Do NOT include SB goal language.'
        ) + '\n- SB PREFERENCE (INCENTIVE): ' + statusText(
          contract.dvbe?.sbIncentiveStatus,
          'the SB bid preference APPLIES, independent of whether the goal applies. This is FIXED BY LAW at 5% (GC §14838(b), 2 CCR §1896.8) — NOT agency-discretionary. Do NOT write out the preference mechanism or percentage yourself — instead, insert the EXACT literal text `' + SB_PREFERENCE_TOKEN + '` on its own line at the point in the document where the SB preference statement belongs (typically within Evaluation Criteria or Terms and Conditions). The app will automatically replace that token with the correct, properly-worded statement including the $50,000 cap. Do not add any other SB preference mechanism text around it beyond a brief introductory sentence.',
          'the SB preference has been WAIVED for this contract. Do NOT include SB preference language.',
          'the SB preference is NOT applicable for this procurement method. Do NOT include SB preference language.'
        );
      })() + '\n\nFor Section 10, include ONLY the following attachments as confirmed by the analyst:\n' + solAttachList + '\nCite the applicable statute or SCM section for each. Do NOT add any other forms or certifications beyond this list, even ones you know about from general California contracting knowledge — in particular, do NOT list "Contractor Certification Clauses (CCC)" or "GSPD-401 IT General Provisions" as required attachments: both are incorporated by reference into the contract\'s terms and conditions once signed, not separate documents bidders complete and submit during the solicitation. Write in formal California state government procurement language. NEVER write "TBD" — use "Contractor" for the contractor and "the State" or the agency name for the state side.\n\nKEEP THE TOTAL SOLICITATION CONCISE — aim for 3,000-5,000 words. Do not pad sections or repeat information.' + libNote + scmContextNote + cmasContextNote + srcNote + languageRuleText(resolveProfile('SOL')),
      comp: 'You are Penny, the GoldenScope contracting assistant Penny. Perform a full SCM compliance review. Identify ALL compliance gaps, cite specific SCM sections, and provide a prioritized list of required corrective actions.' + libNote + scmContextNote + cmasContextNote,
    };

    const msgs = {
      rec: 'Contract Title: ' + (contract.title || '[Not set]') +
        '\nDescription: ' + (contract.description || contract.notes || '[Not provided — fill in the contract description field for a more accurate recommendation]') +
        '\nBudget: ' + (fmtMoney(contract.budget) || '[Not set]') +
        '\nContract Type: ' + (contract.isIT === 'yes' ? 'IT Services — SCM Vol 2' : 'Non-IT Services — SCM Vol 1') +
        (contract.procMethod
          ? '\nSelected Procurement Method: ' + contract.procMethod + ' ← THIS IS THE METHOD THE USER HAS ALREADY CHOSEN. Evaluate it, compare it to your recommendation, and give your honest opinion.'
          : '\nProcurement Method: Not yet selected — please recommend the best method.') +
        '\nCategory: ' + (contract.category || 'Professional Services') +
        '\nStage: ' + (contract.stage + 1) + ' of 16' +
        '\nDGS Threshold (Tier 1): Non-IT $50,000 / IT $200,000' +
        (contract.s215?.agreeOutline ? '\nScope Notes: ' + contract.s215.agreeOutline : '') +
        '\n\nPlease write the full memo.',
      sol: 'Contract: ' + contract.title +
        '\nDescription: ' + (contract.description || contract.notes || 'Not provided') +
        '\nMethod: ' + (contract.procMethod || 'RFQ') +
        '\nBudget: ' + fmtMoney(contract.budget) +
        '\nType: ' + (contract.isIT === 'yes' ? 'IT' : 'Non-IT') +
        '\nContractor: ' + (s215?.contractorName || 'To be determined') +
        exS +
        // Include drafted exhibit text so the solicitation reflects actual contract content
        (sowDraftText ? '\n\n── EXHIBIT A — SCOPE OF WORK (drafted) ──\n' + sowDraftText.slice(0, 12000) : '') +
        (exBDraftText ? '\n\n── EXHIBIT B — BUDGET DETAIL & PAYMENT PROVISIONS (drafted) ──\n' + exBDraftText.slice(0, 6000) : '') +
        (exDDraftText ? '\n\n── EXHIBIT D — SPECIAL TERMS AND CONDITIONS (drafted) ──\n' + exDDraftText.slice(0, 6000) : '') +
        (contract.description ? '\n\nContract Description: ' + contract.description : ''),
      comp: 'Contract: ' + contract.title + '\nBudget: ' + fmtMoney(contract.budget) + '\nType: ' + (contract.isIT === 'yes' ? 'IT' : 'Non-IT') + '\nMethod: ' + (contract.procMethod || 'to be determined') + '\nStage: ' + (contract.stage + 1),
    };

    try {
      const content = [
        ...(allDocs.length > 0 ? allDocs : []),
        { type: 'text', text: msgs[type] },
      ];
      const text = await callAI(apiKey, {
        system: systems[type], model: 'claude-sonnet-4-6',
        // Solicitation needs more room — full 10-section RFP document.
        // Recommendation and compliance are shorter outputs, but 2000 was
        // cutting them off — bumped for headroom.
        max_tokens: type === 'sol' ? 24000 : 8000,
        messages: [{ role: 'user', content }],
        tag: 'draft_' + type,
        contract_id: contract.id,
      });
      setDraftResults(p => ({ ...p, [type]: text, [type + '_refs']: buildRefsList() }));
      if (type === 'rec') {
        const parsed = parseRecMethods(text);
        setRecMethodPick(parsed ? { ...parsed, selected: null } : null);
      }
    } catch (e) { toast.error('Draft failed: ' + e.message); }
    finally { setDraftLoading(p => ({ ...p, [type]: false })); }
  }

  async function runAnalyze() {
    const hasSowDraft = analyzeRefs.includes('sow-draft') && sowDraftText;
    const hasSolDraft = analyzeRefs.includes('sol-draft') && draftResults['sol'];
    const hasUpload = analyzeFile?.base64;
    if (!hasSowDraft && !hasSolDraft && !hasUpload) {
      toast.warn('Select at least one document to review.'); return;
    }
    setAnalyzeLoading(true);
    setAnalyzeResult('');
    try {
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Budget: ' + fmtMoney(contract.budget),
        'Type: ' + (contract.isIT === 'yes' ? 'IT Services — SCM Vol 2' : 'Non-IT Services — SCM Vol 1'),
        'Method: ' + (contract.procMethod || 'to be determined'),
        'Category: ' + (contract.category || 'Professional Services'),
        'Stage: ' + (contract.stage + 1) + ' of 16',
      ].join('\n');

      const sys = 'You are Penny, the GoldenScope contracting assistant for the Office of Energy Infrastructure Safety (Energy Safety). Perform a thorough SCM compliance review of the provided contract documents. Structure your review as follows:\n\n1. COMPLIANCE SUMMARY\n2-3 sentences overall assessment.\n\n2. CRITICAL ISSUES (must fix before submission)\nNumber each issue. Cite the specific SCM section (e.g., SCM Vol 1, §4.04). State what is missing or incorrect and exactly what needs to be done.\n\n3. RECOMMENDED IMPROVEMENTS\nNumber each. Cite SCM sections where applicable. These are best practice improvements that strengthen the package.\n\n4. COMPLIANT ELEMENTS\nBriefly note what is done correctly to give the analyst confidence.\n\n5. NEXT STEPS\nList the top 3 immediate actions in order of priority.' + libNote + scmContextNote + cmasContextNote;

      // Build message content from all selected sources
      const userContent = [...getContractRefDocs(), ...getGeneralAttachmentDocs()];
      if (hasUpload) {
        userContent.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: analyzeFile.base64 } });
      }
      const textParts = ['Contract context:\n' + contractCtx];
      if (hasSowDraft) textParts.push('\n\nEXHIBIT A — SCOPE OF WORK (Penny draft):\n' + sowDraftText);
      if (hasSolDraft) textParts.push('\n\nSOLICITATION DOCUMENT (Penny draft):\n' + draftResults['sol']);
      userContent.push({ type: 'text', text: textParts.join('') + '\n\nPlease perform a full SCM compliance review of all provided documents.' });

      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'contract_review',
        contract_id: contract.id,
      });
      setAnalyzeResult(text);
    } catch (e) { setAnalyzeResult('Error: ' + e.message); }
    finally { setAnalyzeLoading(false); }
  }

  async function runExhibitReview(label, name, docSource) {
    setExhibitReviewLoading(p => ({ ...p, [label]: true }));
    const key = 'exhibitReview' + label;
    try {
      const contractCtx = [
        'Contract Title: ' + (contract.title || 'this contract'),
        'Budget: ' + fmtMoney(contract.budget),
        'Type: ' + (contract.isIT === 'yes' ? 'IT — SCM Vol 2' : 'Non-IT — SCM Vol 1'),
        'Method: ' + (contract.procMethod || 'to be determined'),
        'Category: ' + (contract.category || 'Professional Services'),
      ].join(' · ');

      const exhibitRules = {
        SOL: `This is a solicitation document (${contract.procMethod || 'RFQ'}) for CalEProcure. Assess whether it is complete and compliant per SCM requirements:
- Is the solicitation method correct for the budget (${ fmtMoney(contract.budget)}) and contract type?
- Does it include all required sections: Introduction, Scope Summary, Minimum Qualifications, Proposal Requirements, Evaluation Criteria, Budget/Payment Terms, Timeline, Submission Instructions?
- Are evaluation criteria clearly defined and objective?
- Are DVBE/SB participation requirements documented if applicable?
- Does the scope summary align with what would be in Exhibit A?
- Are there any prohibited provisions or missing mandatory clauses per SCM?
- Is the submission timeline reasonable and clearly stated?`,
        A: `Exhibit A is the Scope of Work. Assess whether it contains all required sections per SCM Vol 1 §4.04:
1. Purpose and Background
2. Scope of Services (specific, measurable deliverables)
3. Deliverables (clearly defined, with acceptance criteria)
4. Performance Standards (measurable, not vague)
5. Acceptance Criteria (how the State will verify completion)
6. Contractor Responsibilities
7. State Responsibilities
8. Timeline and Milestones

Also check: Is the language specific enough to be enforceable? Are there any vague terms like "as needed" or "various" without definition? Does the SOW avoid Payment Terms (those belong in Exhibit B)?`,
        B: `Exhibit B is the Budget Detail and Payment Provisions. Assess:
- Is the budget itemized with sufficient detail?
- Are payment milestones tied to deliverables in Exhibit A?
- Are invoicing requirements clearly stated (frequency, format, supporting docs)?
- Is the DVBE participation goal documented if required?
- Does the payment schedule comply with Prompt Payment Act (Net 45)?
- Are there any payment terms that conflict with Exhibit A deliverables?`,
        D: `Exhibit D is the Special Terms and Conditions. Assess:
- Are all required clauses present for this contract type (conflict of interest, confidentiality, data ownership, DVBE, termination)?
- Do any terms conflict with the GTC (Exhibit C)?
- Are there any agency-specific requirements missing?
- Is the contractor's insurance requirement specified if applicable?
- Are there any ambiguous or unenforceable terms?`,
      };

      const sys = `You are Penny, GoldenScope's contracting assistant for the Office of Energy Infrastructure Safety. 
Perform a focused SCM compliance assessment of a single exhibit.

Contract context: ${contractCtx}
Exhibit: ${label} — ${name}

${exhibitRules[label] || 'Assess whether this exhibit is complete, compliant, and contains sufficient information.'}
${scmContextNote}

Structure your response EXACTLY as:

OVERALL STATUS: [✅ Compliant | ⚠ Needs Revision | ❌ Non-Compliant]

SECTION ASSESSMENT:
For each required section or element, one line: [✅/⚠/❌] Section Name — brief note

CRITICAL ISSUES:
Number each issue. Cite SCM section. State exactly what is missing or must change.

RECOMMENDATIONS:
Number each. Best practice improvements that strengthen the exhibit.

VERDICT:
1-2 sentences on whether this exhibit is ready for submission.`;

      const userContent = [...getContractRefDocs(), ...getGeneralAttachmentDocs()];
      if (docSource?.type === 'pdf' && docSource.base64) {
        userContent.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: docSource.base64 } });
        userContent.push({ type: 'text', text: `Review the uploaded Exhibit ${label} PDF for SCM compliance.` });
      } else if (docSource?.type === 'text' && docSource.content) {
        userContent.push({ type: 'text', text: `Exhibit ${label} draft text:

${docSource.content}

Review for SCM compliance.` });
      } else {
        userContent.push({ type: 'text', text: `No exhibit content available yet. Based on the contract context, provide guidance on what Exhibit ${label} must contain and what common gaps to watch for.` });
      }

      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 8000,
        messages: [{ role: 'user', content: userContent }],
        tag: 'exhibit_review_' + (label || 'unknown').toLowerCase(),
        contract_id: contract.id,
      });
      updateC({ pennyDrafts: { [key]: text } });
    } catch (e) {
      updateC({ pennyDrafts: { ['exhibitReview' + label]: 'Error: ' + e.message } });
    } finally {
      setExhibitReviewLoading(p => ({ ...p, [label]: false }));
    }
  }

  const DRAFT_ITEMS = [
    { key: 'rec', label: 'Procurement Method Recommendation', desc: contract.procMethod ? 'Method selected: ' + contract.procMethod + ' — Penny will evaluate it and compare to her recommendation' : 'No method selected yet — Penny will recommend one based on your contract details' },
    { key: 'sol', label: 'Solicitation Document', desc: (contract.procMethod || 'RFQ') + ' format for CalEProcure' },
  ];

  return (
    <div>
      {/* Library status */}
      {loadedTotal > 0
        ? <Callout type="teal" style={{ marginBottom: 8 }}>
            {contract.isIT === 'yes' ? '✓ SCM Vol 2 (IT) loaded' : '✓ SCM Vol 1 (Non-IT) loaded'}{loadedSam > 0 ? ` · ${loadedSam} sample${loadedSam !== 1 ? 's' : ''} loaded` : ''}
          </Callout>
        : <Callout type="blue" style={{ marginBottom: 8 }}>
            Upload SCM Vol 1/2 PDFs in Reference Library for better, more accurate drafts with proper section citations.
          </Callout>
      }

      {/* SCM Compliance Brief — two-call system */}
      <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 'var(--r-md)', background: scmCacheValid ? 'var(--teal-50)' : 'var(--slate-50)', border: '1.5px solid ' + (scmCacheValid ? 'var(--teal-200)' : 'var(--slate-200)'), display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          {scmCacheValid ? (
            <div style={{ fontSize: 12.5, color: 'var(--teal-700)', lineHeight: 1.5 }}>
              <strong>✦ SCM Compliance Brief active</strong> — {contract.procMethod || 'RFQ'} · {contract.isIT === 'yes' ? 'SCM Vol 2 (IT)' : 'SCM Vol 1 (Non-IT)'} · Generated {scmCacheAge === 0 ? 'today' : scmCacheAge === 1 ? '1 day ago' : scmCacheAge + ' days ago'}
              <div style={{ fontSize: 11, color: 'var(--teal-600)', marginTop: 2 }}>All Penny drafts use this brief for precise, citation-backed SCM compliance. Persists with this contract — refresh only if procurement method changes or the SCM has been updated.</div>
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--slate-600)', lineHeight: 1.5 }}>
              <strong>SCM Compliance Brief not generated</strong> — Penny will read your uploaded {contract.isIT === 'yes' ? 'SCM Vol 2' : 'SCM Vol 1'} and build a {contract.procMethod || 'procurement'}-specific compliance context. Every Penny draft on this tab then uses this brief for precise section citations.
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>Recommended before drafting SOW, solicitation, or clarifying questions. Takes ~30 seconds. Cached for 14 days.</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
          {scmCacheValid && (
            <button className="btn btn-ghost btn-xs" style={{ color: 'var(--slate-400)' }} onClick={clearScmContext} title="Clear cached brief">✕</button>
          )}
          <button className="btn btn-gold btn-sm" disabled={scmLoading} onClick={generateScmContext}>
            {scmLoading ? <><div className="spin spin-sm" /> Reading SCM…</> : scmCacheValid ? '↺ Refresh' : '✦ Generate SCM Brief'}
          </button>
        </div>
      </div>

      {/* Contract reference docs — persisted */}
      <CollapsibleCard id="refDocs" title="Contract Reference Documents" collapsed={collapsed.refDocs} onToggle={() => toggleCard('refDocs')} style={{ marginBottom: 16, padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: contractRefs.length > 0 ? 12 : 0 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              Contract Reference Documents
              {contractRefs.length > 0 && (
                <span className="badge badge-teal" style={{ marginLeft: 8, fontSize: 9.5 }}>{contractRefs.length} loaded</span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginTop: 2 }}>
              PDFs uploaded here are included in every Penny draft. The API allows 95 pages combined across all documents — Penny prioritizes your request doc first, then SCM, then reference docs.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => sessionFileRef.current?.click()}>
              <span>↑</span> Add Reference PDF
            </button>
            <input ref={sessionFileRef} type="file" multiple accept=".pdf" style={{ display: 'none' }}
              onChange={e => { if (e.target.files.length > 0) addContractRef(e.target.files); e.target.value = ''; }} />
          </div>
        </div>
        {contractRefs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {contractRefs.map(doc => {
              const estPages = doc.base64 ? Math.ceil((doc.base64.length * 0.75) / 51200) : 0;
              const isLarge = estPages > 100;
              const hasRange = doc.pageStart || doc.pageEnd;
              return (
                <div key={doc.id} style={{
                  padding: '8px 12px', background: isLarge ? 'var(--orange-50)' : 'var(--teal-50)',
                  border: '1px solid ' + (isLarge ? 'var(--orange-200)' : 'var(--teal-100)'),
                  borderRadius: 'var(--r-md)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>📄</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 500, color: isLarge ? 'var(--orange-700)' : 'var(--teal-600)' }}>{doc.name}</span>
                      {estPages > 0 && (
                        <span style={{ fontSize: 10, color: 'var(--slate-400)', marginLeft: 8 }}>
                          ~{estPages} pages{hasRange ? ` · Using pp. ${doc.pageStart}–${doc.pageEnd}` : ''}
                        </span>
                      )}
                      {doc.addedAt && !estPages && <span style={{ fontSize: 10.5, color: 'var(--slate-400)', marginLeft: 8 }}>Added {doc.addedAt}</span>}
                    </div>
                    <button className="btn btn-xs btn-ghost" style={{ color: 'var(--red-400)' }} onClick={() => removeContractRef(doc.id)}>
                      <span>✕</span>
                    </button>
                  </div>
                  {/* Show handling info for all docs */}
                  {estPages > 0 && (
                    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--white)', borderRadius: 'var(--r-md)', border: '1px solid ' + (isLarge ? 'var(--orange-100)' : 'var(--teal-100)') }}>
                      {!isLarge && estPages <= 100 && (
                        <div style={{ fontSize: 11, color: 'var(--teal-700)' }}>
                          ✓ Under 100 pages — Penny will read the full document automatically.
                        </div>
                      )}
                      {!isLarge && estPages > 100 && estPages <= 300 && (
                        <div style={{ fontSize: 11, color: 'var(--teal-700)' }}>
                          ✓ {estPages} pages — Penny will auto-split this into chunks and read the entire document. No action needed.
                        </div>
                      )}
                      {isLarge && (
                        <>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--orange-700)', marginBottom: 6 }}>
                            ⚠ Over 300 pages — too large to auto-split. Select the most relevant page range for Penny to read:
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 8, lineHeight: 1.5 }}>
                            For a Senate Bill, try pages 1–20 (definitions + key provisions). For a policy doc, try the section most relevant to this contract's scope.
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: 'var(--slate-500)' }}>Pages</span>
                            <input
                              type="number" min="1" max={estPages} placeholder="Start"
                              value={doc.pageStart || ''}
                              onChange={e => updateC({ referenceDocs: contractRefs.map(d => d.id === doc.id ? { ...d, pageStart: parseInt(e.target.value) || '' } : d) })}
                              style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1.5px solid var(--orange-200)', fontSize: 13, textAlign: 'center' }}
                            />
                            <span style={{ fontSize: 12, color: 'var(--slate-400)' }}>to</span>
                            <input
                              type="number" min="1" max={estPages} placeholder="End"
                              value={doc.pageEnd || ''}
                              onChange={e => updateC({ referenceDocs: contractRefs.map(d => d.id === doc.id ? { ...d, pageEnd: parseInt(e.target.value) || '' } : d) })}
                              style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1.5px solid var(--orange-200)', fontSize: 13, textAlign: 'center' }}
                            />
                            <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>of ~{estPages}</span>
                            {doc.pageStart && doc.pageEnd && (
                              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--teal-600)', marginLeft: 4 }}>
                                ✓ {doc.pageEnd - doc.pageStart + 1} pages selected
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {contractRefs.length === 0 && loadedTotal === 0 && (
          <div style={{ fontSize: 12, color: 'var(--slate-400)', fontStyle: 'italic', marginTop: 4 }}>
            No reference docs yet — upload related PDFs (previous contracts, agency templates, scope notes) to improve Penny's output.
          </div>
        )}
      </CollapsibleCard>

      {/* ── Clarifying Questions — not shown for LPA/CMAS contracts ── */}
      {!isCMAS && <CollapsibleCard id="clarifying" title="✦ Clarifying Questions for Requestor · Penny" collapsed={collapsed.clarifying} onToggle={() => toggleCard('clarifying')} style={{ borderLeft: '3px solid var(--gold-400)' }}>
        <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 16 }}>
          Penny reviews your contract details and drafts 3–5 targeted questions to send back to the requestor.
          Early clarification prevents scope creep, amendment requests, and protest risk.
        </p>
        <button
          className="btn btn-gold"
          disabled={clarifyLoading}
          onClick={async () => {
            setClarifyLoading(true);
            try {
              // Call 1 — Get SCM compliance brief (cached or fresh)
              toast.info('Loading SCM compliance context...');
              const scmBrief = await getScmContext();

              const sys = 'You are Penny, the GoldenScope contracting assistant for the Office of Energy Infrastructure Safety (Energy Safety). Your job is to review a new contract intake and generate TWO SEPARATE sets of clarifying items: questions to send to the original requestor, and internal notes for the analyst handling compliance and drafting.' +
                '\n\nSCM COMPLIANCE BRIEF FOR THIS CONTRACT:\n' + (scmBrief || 'Apply standard California SCM requirements.') +
                '\n\nUsing the above SCM brief, generate:' +
                '\n\n(A) 3-5 REQUESTOR QUESTIONS — things ONLY the requestor would know, written in plain, non-technical language suitable for a program-side requestor with no procurement background. Cover:' +
                '\n- Gaps in the scope or requirements that could cause problems later' +
                '\n- Missing deliverables, acceptance criteria, or performance standards' +
                '\n- Assumptions that need to be confirmed before drafting begins' +
                '\n- Any special terms, insurance requirements, or statutory obligations the requestor needs to weigh in on' +
                '\nDo NOT include SCM citations or compliance jargon in these — they are for the requestor, not the analyst.' +
                '\n\n(B) 2-4 ANALYST NOTES — internal compliance/drafting considerations for the CONTRACT ANALYST to resolve themselves, not the requestor. Cover:' +
                '\n- Specific compliance gaps identified in the SCM brief above' +
                '\n- Whether the contract term and total budget are accurate and consistent with each other — especially if the term is multi-year, since the budget should represent the total contract value across all years, not a single year' +
                '\n- Procurement method fit, threshold considerations, or anything requiring the analyst\'s own judgment rather than requestor input' +
                '\nFor each analyst note, cite the SPECIFIC SCM subsection (e.g., §5.11, §8.20, §9.11 — not just the chapter number) and a one-sentence explanation of why it matters for this contract. Use the SCM brief above to identify the exact subsections.' +
                languageRuleText(resolveProfile('CLARIFY')) +
                '\n\nReturn ONLY valid JSON, no markdown, no other text: {"requestorQuestions": ["question 1 text, plain language, no citations", "question 2 text", ...], "analystNotes": ["note 1 text with its (§X.XX) citation and one-sentence explanation inline", ...]}. Each array item is a single self-contained question/note as plain text — do not number them yourself, the app handles numbering.';

              const termInfo = CONTRACT_TERMS.find(t => t.value === contract.term);
              const termLabel = contract.term === 'other' ? (contract.termOther || 'Other (unspecified)') : (termInfo?.label || '1 year');
              const budgetNum = parseFloat(contract.budget) || 0;
              // Budget is already treated as total contract value for multi-year terms per the intake form's label —
              // this just gives Penny threshold context to reason about, not a recalculation of the number.
              let termNote = 'Contract Term: ' + termLabel + ' — Budget above is the total contract value across this term.';
              if (budgetNum > 0) {
                if (budgetNum > 100000) termNote += ' Exceeds $100,000 — TACPA work-site preference may apply if the work site is not fixed (SCM 1 Ch.5, Table 5.1).';
                if (contract.term !== '1yr' && contract.term !== 'lt1yr' && budgetNum >= 250000 && budgetNum < 260000) termNote += ' Near the $250,000 SB/DVBE Option ceiling (cumulative across the full term, including option years/amendments) — confirm this method still fits if the term is right at the edge.';
              }

              const ctx = [
                'Contract Title: ' + (contract.title || 'this contract'),
                'Category: ' + (contract.category || 'Professional Services'),
                'Budget: $' + (contract.budget || 'not set'),
                termNote,
                'Type: ' + (contract.isIT === 'yes' ? 'IT Services — SCM Vol 2' : 'Non-IT Services — SCM Vol 1'),
                'Procurement Method: ' + (contract.procMethod || 'to be determined'),
                'Description: ' + (contract.description || 'No description provided'),
                sowNotes ? 'Prior Requestor Reply:\n' + sowNotes : '',
                libNote ? 'Reference context: ' + libNote.trim() : '',
              ].filter(Boolean).join('\n');

              // Build message content — COMBINED limit of 100 pages/images across ALL docs in request
              // Priority order: Request doc → SCM → Reference docs → Additional doc
              // Each doc gets pages from the shared budget. Analyst page ranges always honored first.
              const requestDoc = (contract.attachments || []).find(a => a.tag === 'Request Document');
              const contractRefDocs = (contract.referenceDocs || []).filter(d => d.base64);
              const userContent = [];
              const docSummary = [];
              const skipped = [];
              let pagesBudget = 95; // leave 5 pages buffer under the 100 combined limit

              // Process a doc within the shared budget
              async function processDoc(b64, name, pageStart, pageEnd) {
                if (pagesBudget <= 0) { skipped.push(name + ' (budget exhausted)'); return; }

                // Analyst-specified page range — always honor it
                if (pageStart && pageEnd) {
                  const rangeSize = pageEnd - pageStart + 1;
                  if (rangeSize > pagesBudget) {
                    // Trim to budget
                    const trimEnd = pageStart + pagesBudget - 1;
                    const extracted = await extractPdfPages(b64, pageStart, trimEnd);
                    if (extracted?.isImages) {
                      extracted.pageImages.forEach(img => userContent.push({ type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: img } }));
                      pagesBudget -= extracted.pageCount;
                      docSummary.push(name + ' (pp. ' + pageStart + '–' + trimEnd + ', trimmed to fit budget)');
                    }
                  } else {
                    const extracted = await extractPdfPages(b64, pageStart, pageEnd);
                    if (extracted?.isImages) {
                      extracted.pageImages.forEach(img => userContent.push({ type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: img } }));
                      pagesBudget -= extracted.pageCount;
                      docSummary.push(name + ' (pp. ' + pageStart + '–' + pageEnd + ')');
                    }
                  }
                  return;
                }

                // Get actual page count
                const actualPages = await getPdfPageCount(b64);
                if (!actualPages) { skipped.push(name + ' (unreadable)'); return; }

                if (actualPages <= pagesBudget) {
                  // Fits in budget — send as PDF
                  userContent.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: b64 } });
                  pagesBudget -= actualPages;
                  docSummary.push(name + ' (' + actualPages + ' pages)');
                } else if (pagesBudget >= 10) {
                  // Partial — send as many pages as budget allows (first N pages)
                  const extracted = await extractPdfPages(b64, 1, pagesBudget);
                  if (extracted?.isImages) {
                    extracted.pageImages.forEach(img => userContent.push({ type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: img } }));
                    docSummary.push(name + ' (pp. 1–' + pagesBudget + ' of ' + actualPages + ', budget limited — set a page range for a specific section)');
                    pagesBudget = 0;
                  }
                } else {
                  skipped.push(name + ' (' + actualPages + ' pages — budget full, set a page range)');
                }
              }

              // 1. Request document (highest priority)
              if (requestDoc?.base64) {
                await processDoc(requestDoc.base64, requestDoc.name);
              }

              // 2. SCM — injected into system prompt (not sent as PDF — too large and Penny knows it from training)
              const isIT = contract.isIT === 'yes';
              const scmVol = isIT ? 'SCM Vol 2 (IT Services)' : 'SCM Vol 1 (Non-IT Services)';

              // 3. Contract reference documents
              for (const doc of contractRefDocs) {
                if (doc.base64) await processDoc(doc.base64, doc.name, doc.pageStart, doc.pageEnd);
              }

              // 4. Additional document
              if (sowSupplementDoc?.base64) {
                await processDoc(sowSupplementDoc.base64, 'Additional document');
              }

              const totalBlocks = userContent.filter(c => c.type === 'document' || c.type === 'image').length;
              let docNote = 'Governing document: ' + scmVol + ' (applied from training — full compliance knowledge active).';
              if (docSummary.length > 0) docNote += '\nAdditional documents: ' + docSummary.join(' | ');
              if (skipped.length > 0) docNote += '\nExcluded (budget full): ' + skipped.join(', ');
              if (totalBlocks === 0) docNote += '\nNo contract-specific documents attached — base questions on the contract details and SCM requirements.';

              userContent.push({ type: 'text', text: docNote + '\n\nGenerate clarifying questions for this contract intake:\n\n' + ctx });

              const text = await callAI(apiKey, {
                system: sys,
                model: 'claude-sonnet-4-6',
                max_tokens: 8000,
                messages: [{ role: 'user', content: userContent }],
                tag: 'clarifying_questions',
                contract_id: contract.id,
              });
              setClarifyQuestions(parseClarifyOutput(text));
            } catch (e) { toast.error('Draft failed: ' + e.message); }
            finally { setClarifyLoading(false); }
          }}
        >
          {clarifyLoading ? <><div className="spin spin-sm" /> Drafting questions…</> : '✦ Draft Clarifying Questions'}
        </button>

        {clarifyLoading && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--slate-400)', fontStyle: 'italic' }}>
            Penny is reviewing your contract details…
          </div>
        )}

        {(clarifyQuestions.requestor.length > 0 || clarifyQuestions.analyst.length > 0) && !clarifyLoading && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
              <button
                onClick={() => setClarifyTab('requestor')}
                className={clarifyTab === 'requestor' ? 'btn btn-gold btn-sm' : 'btn btn-ghost btn-sm'}
                style={{ opacity: clarifyQuestions.requestor.length ? 1 : 0.5 }}
              >
                📨 For the Requestor ({clarifyQuestions.requestor.filter(q => q.checked).length}/{clarifyQuestions.requestor.length} selected)
              </button>
              <button
                onClick={() => setClarifyTab('analyst')}
                className={clarifyTab === 'analyst' ? 'btn btn-gold btn-sm' : 'btn btn-ghost btn-sm'}
                style={{ opacity: clarifyQuestions.analyst.length ? 1 : 0.5 }}
              >
                🔍 For You ({clarifyQuestions.analyst.filter(n => n.status === 'responded' || n.status === 'not_applicable').length}/{clarifyQuestions.analyst.length} resolved)
              </button>
            </div>

            <div style={{
              background: 'var(--navy-900)', borderRadius: 'var(--r-lg)',
              padding: '20px 24px', marginBottom: 12,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold-400)', marginBottom: 14 }}>
                {clarifyTab === 'requestor' ? '✦ Select which questions to send the requestor' : '✦ Review each compliance/drafting note'}
              </div>

              {clarifyTab === 'requestor' ? (
                clarifyQuestions.requestor.length === 0
                  ? <div style={{ fontSize: 13, color: 'var(--navy-300)' }}>No requestor questions generated for this contract.</div>
                  : clarifyQuestions.requestor.map((q, i) => (
                      <div key={q.id} style={{ padding: '8px 0', borderBottom: i < clarifyQuestions.requestor.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <input type="checkbox" checked={q.checked} onChange={() => toggleRequestorChecked(q.id)} style={{ marginTop: 3, cursor: 'pointer' }} />
                          <span onClick={() => toggleRequestorChecked(q.id)} style={{ flex: 1, fontSize: 13.5, lineHeight: 1.7, color: q.checked ? 'var(--navy-100)' : 'var(--navy-400)', textDecoration: q.checked ? 'none' : 'line-through', cursor: 'pointer' }}>
                            {i + 1}. {q.text}
                          </span>
                          <button
                            onClick={() => openReplace(q.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: replacingId === q.id ? 'var(--gold-400)' : 'var(--navy-400)', fontSize: 12.5, flexShrink: 0, padding: '2px 4px' }}
                            title="Replace this question"
                          >
                            🔄
                          </button>
                        </div>

                        {replacingId === q.id && (
                          <div style={{ marginTop: 8, marginLeft: 24, padding: '10px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                            {replaceLoading ? (
                              <div style={{ fontSize: 12, color: 'var(--navy-300)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div className="spin spin-sm" /> Drafting a replacement…
                              </div>
                            ) : replaceMode === null ? (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 12, color: 'var(--navy-300)', marginRight: 4 }}>Replace with a question:</span>
                                <button className="btn btn-ghost btn-sm" onClick={() => regenerateRequestorQuestion(q.id, 'importance')}>
                                  ⭐ By Importance
                                </button>
                                <button className="btn btn-ghost btn-sm" onClick={() => setReplaceMode('topic')}>
                                  💬 By Topic
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                  type="text"
                                  value={replaceTopic}
                                  onChange={e => setReplaceTopic(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter' && replaceTopic.trim()) regenerateRequestorQuestion(q.id, 'topic', replaceTopic.trim()); }}
                                  placeholder="e.g., insurance requirements, delivery timeline…"
                                  autoFocus
                                  style={{ flex: 1, minWidth: 180, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'var(--navy-100)', fontSize: 12.5 }}
                                />
                                <button className="btn btn-gold btn-sm" disabled={!replaceTopic.trim()} onClick={() => regenerateRequestorQuestion(q.id, 'topic', replaceTopic.trim())}>
                                  Generate
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
              ) : (
                clarifyQuestions.analyst.length === 0
                  ? <div style={{ fontSize: 13, color: 'var(--navy-300)' }}>No analyst notes generated for this contract.</div>
                  : clarifyQuestions.analyst.map((n, i) => (
                      <div key={n.id} style={{ padding: '10px 0', borderBottom: i < clarifyQuestions.analyst.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                        <div style={{ fontSize: 13.5, lineHeight: 1.7, color: 'var(--navy-100)', marginBottom: 8 }}>
                          {i + 1}. {n.text}
                        </div>
                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: n.status === 'responded' ? 8 : 0 }}>
                          {[
                            { value: 'not_reviewed', label: 'Not Reviewed' },
                            { value: 'not_applicable', label: 'Not Applicable' },
                            { value: 'responded', label: 'Responded Below' },
                          ].map(opt => (
                            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--navy-300)', cursor: 'pointer' }}>
                              <input type="radio" name={'analyst-status-' + n.id} value={opt.value} checked={n.status === opt.value} onChange={() => setAnalystStatus(n.id, opt.value)} />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                        {n.status === 'responded' && (
                          <textarea
                            value={n.response}
                            onChange={e => setAnalystResponse(n.id, e.target.value)}
                            rows={2}
                            placeholder="Type your response/resolution here…"
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', fontSize: 12.5, border: '1px solid rgba(255,255,255,0.15)', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', color: 'var(--navy-100)' }}
                          />
                        )}
                      </div>
                    ))
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {clarifyTab === 'requestor' && (
                <button
                  className="btn btn-gold btn-sm"
                  disabled={clarifyQuestions.requestor.filter(q => q.checked).length === 0}
                  onClick={() => {
                    const selected = clarifyQuestions.requestor.filter(q => q.checked);
                    const bodyQuestions = selected.map((q, i) => (i + 1) + '. ' + q.text).join('\n');
                    const subject = 'Clarifying Questions — ' + (contract.title || 'Contract Request');
                    const cmFirstName = assignedCm?.name ? assignedCm.name.trim().split(' ')[0] : null;
                    const greeting = cmFirstName ? `Hi ${cmFirstName},` : 'Hello,';
                    const cmTo = assignedCm?.email || '';
                    const body = greeting + '\n\nThank you for submitting your contract request. Before we proceed with drafting, we have a few clarifying questions:\n\n' + bodyQuestions + '\n\nPlease respond at your earliest convenience so we can move forward efficiently.\n\nThank you,\n' + (profile?.contactName || 'Contract Analyst') + '\n' + (profile?.agencyDivision || 'Admin Services Division');
                    window.location.href = 'mailto:' + encodeURIComponent(cmTo) + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
                  }}
                >
                  📧 Open in Email (checked only)
                </button>
              )}
              <button
                className="btn btn-ghost btn-sm"
                disabled={clarifyTab === 'requestor' ? clarifyQuestions.requestor.filter(q => q.checked).length === 0 : clarifyQuestions.analyst.length === 0}
                onClick={() => {
                  const text = clarifyTab === 'requestor'
                    ? clarifyQuestions.requestor.filter(q => q.checked).map((q, i) => (i + 1) + '. ' + q.text).join('\n')
                    : clarifyQuestions.analyst.map((n, i) => (i + 1) + '. ' + n.text + (n.response ? ' — Response: ' + n.response : '')).join('\n');
                  navigator.clipboard.writeText(text);
                  setClarifyCopied(true);
                  setTimeout(() => setClarifyCopied(false), 2000);
                }}
              >
                {clarifyCopied ? '✓ Copied' : '📋 Copy This Tab'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setClarifyQuestions({ requestor: [], analyst: [] })}
                style={{ color: 'var(--slate-400)' }}
              >
                Clear Both
              </button>
            </div>
          </div>
        )}

      {/* ── Requestor Replies — moved here from Exhibit A ── */}
      {(clarifyQuestions.requestor.length > 0 || clarifyQuestions.analyst.length > 0) && (
        <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 'var(--r-lg)', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal-700)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
            ✦ Step 2 — Add Requestor Replies
          </div>
          <p style={{ fontSize: 12, color: 'var(--slate-500)', lineHeight: 1.6, marginBottom: 12 }}>
            Once the requestor replies, paste their answers below or upload their response. Penny will incorporate confirmed details directly into the SOW when drafting Exhibit A.
          </p>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Upload Requestor Reply <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— PDF or email printout</span>
            </div>
            {!sowSupplementDoc ? (
              <div
                onClick={() => sowSupplementRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0]; if (!f) return;
                  const r = new FileReader();
                  r.onload = () => handleSowSupplementUpload({ name: f.name, size: f.size, base64: r.result.split(',')[1] });
                  r.readAsDataURL(f);
                }}
                style={{ border: '1.5px dashed var(--teal-300)', borderRadius: 'var(--r-md)', padding: '12px 16px', cursor: 'pointer', background: 'var(--white)', display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <span style={{ fontSize: 22 }}>📥</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--slate-700)' }}>Upload reply document</div>
                  <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>Requestor email reply, revised scope, stakeholder notes, technical specs — any PDF</div>
                </div>
                <input ref={sowSupplementRef} type="file" accept=".pdf" style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files[0]; if (!f) return;
                    const r = new FileReader();
                    r.onload = () => handleSowSupplementUpload({ name: f.name, size: f.size, base64: r.result.split(',')[1] });
                    r.readAsDataURL(f);
                  }} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--teal-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--teal-200)' }}>
                <span style={{ fontSize: 18 }}>📥</span>
                <div style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: 'var(--teal-700)' }}>{sowSupplementDoc.name}</div>
                <button onClick={() => setSowSupplementDoc(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-400)', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Requestor Reply <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— paste their answer to the questions you sent</span>
            </div>
            <textarea
              value={sowNotes}
              onChange={e => setSowNotes(e.target.value)}
              rows={4}
              placeholder="Paste the requestor's reply here — e.g.:

Q1 answer: Deliverables accepted within 10 business days.
Q2 answer: SB participation goal is 25%.

Penny will treat these as confirmed requirements in the SOW. (For your own compliance/drafting notes, use the response field under each item in the 'For You' tab above instead.)"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', fontSize: 12.5, border: '1.5px solid var(--teal-200)', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--white)', color: 'var(--slate-700)', lineHeight: 1.6 }}
            />
          </div>
        </div>
      )}

      {/* Also show when no clarifying questions yet — so analyst can add context proactively */}
      {!(clarifyQuestions.requestor.length > 0 || clarifyQuestions.analyst.length > 0) && (
        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 'var(--r-md)', background: 'var(--slate-50)', border: '1px solid var(--slate-200)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate-500)', marginBottom: 6 }}>Optional — Additional Context for Penny</div>
          <textarea
            value={sowNotes}
            onChange={e => setSowNotes(e.target.value)}
            rows={3}
            placeholder="Any specific requirements, constraints, or context — e.g., deliverables must align with the quarterly reporting cycle, contractor must use Energy Safety templates, weekly status meetings required."
            style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', fontSize: 12.5, border: '1.5px solid var(--slate-200)', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--white)', color: 'var(--slate-700)', lineHeight: 1.6 }}
          />
        </div>
      )}
      </CollapsibleCard>}

      {/* ── Sole Source / NCB Justification — only shown for NCB / Sole Source procurement methods ──────── */}
      {(contract.procMethod === 'NCB' || contract.procMethod === 'Sole Source') && (
        <CollapsibleCard id="soleSourceNcb" title="✦ Sole Source / NCB Justification · Penny" subtitle="Upload the contractor's proprietary letter, confirm the acquisition type, and Penny drafts the NCB Justification narrative for the DGS NCB Portal." collapsed={collapsed.soleSourceNcb} onToggle={() => toggleCard('soleSourceNcb')} style={{ borderLeft: '3px solid var(--gold-400)' }}>
          <div style={{ marginBottom: 14 }}>
            <button className="btn btn-ghost btn-sm" disabled={classifyLoading} onClick={classifyAcquisition}>
              {classifyLoading ? <><div className="spin spin-sm" /> Classifying…</> : '✦ Not sure Goods vs. Services? Ask Penny to classify this acquisition'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 4 }}>
              Uses: {[
                ncbRequestDocAttachment ? 'original intake SOW (' + ncbRequestDocAttachment.name + ')' : null,
                sowDraftText ? 'Penny-drafted Exhibit A' : null,
                ncb.letterFile ? 'proprietary letter' : null,
              ].filter(Boolean).join(', ') || 'contract description only — upload an intake SOW or draft Exhibit A for more detail'}
            </div>
            {ncb.classification && (
              <div style={{ marginTop: 10 }}>
                <Callout type={ncb.classification.confidence === 'high' ? 'green' : ncb.classification.confidence === 'low' ? 'orange' : 'blue'}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {ncb.classification.classification} — {ncb.classification.confidence} confidence
                  </div>
                  <div style={{ fontSize: 12.5, marginBottom: 8 }}>{ncb.classification.reasoning}</div>
                  <button className="btn btn-gold btn-sm" onClick={() => setNcb({ acquisitionType: ncb.classification.recommendedType })}>
                    Use recommended type: {ncb.classification.recommendedType}
                  </button>
                </Callout>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--slate-500)', marginBottom: 8 }}>Acquisition Type</div>
            <select value={ncb.acquisitionType || ''} onChange={e => setNcb({ acquisitionType: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, background: 'white' }}>
              <option value="">Select acquisition type…</option>
              <option value="IT Goods — New Proprietary Software (purchase/license)">IT Goods — New Proprietary Software (purchase/license) — $1M threshold</option>
              <option value="IT Services — Proprietary Maintenance/Support">IT Services — Proprietary Maintenance/Support — $250K threshold</option>
              <option value="IT Goods">IT Goods (other, non-proprietary-exempt)</option>
              <option value="IT Services">IT Services (other, non-proprietary-exempt)</option>
              <option value="Non-IT Services">Non-IT Services</option>
            </select>
            <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 4 }}>
              SCM classifies a new software purchase/license as Goods (code 025) but ongoing proprietary maintenance/support as a Services exemption (code 024) — these have different thresholds ($1M vs $250K), so pick based on what this contract actually is, not just that it involves proprietary software.
            </div>
          </div>

          {ncb.acquisitionType && (() => {
            const t = ncbThresholdInfo();
            return (
              <div style={{ marginBottom: 4 }}>
                <Callout type={t.needsJustification === false ? 'green' : 'orange'}>
                  {t.note}
                </Callout>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, fontWeight: 700, background: t.druRequired ? '#fee2e2' : '#dcfce7', color: t.druRequired ? '#b91c1c' : '#15803d' }}>
                    {t.druRequired ? '⚠ DGS/PD/DRU review likely required' : '✓ DRU review not indicated at this budget'}
                  </span>
                  {t.itPacRequired && (
                    <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>
                      ⚠ IT PAC sign-off required (SCM Vol 2 §1507)
                    </span>
                  )}
                </div>
                {(ncb.acquisitionType === 'IT Services' || ncb.acquisitionType === 'IT Goods' || ncb.acquisitionType.startsWith('IT ')) && (
                  <div style={{ marginTop: 10, padding: 10, background: 'var(--slate-50)', borderRadius: 8, border: '1px solid var(--slate-200)' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!ncb.cdtApplicable} onChange={e => setNcb({ cdtApplicable: e.target.checked })} style={{ marginTop: 2 }} />
                      <span>Is this a reportable IT project or large-scale system integration effort? CDT involvement may apply — this isn't something the app can determine from budget alone.</span>
                    </label>
                    <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} disabled={cdtClassifyLoading} onClick={classifyCdtApplicability}>
                      {cdtClassifyLoading ? <><div className="spin spin-sm" /> Checking…</> : '✦ Not sure? Ask Penny to check against SCM Vol 2 triggers'}
                    </button>
                    {ncb.cdtClassification && (
                      <Callout type={ncb.cdtClassification.applicable === true ? 'orange' : ncb.cdtClassification.applicable === 'uncertain' ? 'blue' : 'green'} >
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                          {ncb.cdtClassification.applicable === true ? 'CDT likely applies' : ncb.cdtClassification.applicable === 'uncertain' ? 'Uncertain — verify with your PAC' : 'CDT likely does not apply'} — {ncb.cdtClassification.confidence} confidence
                        </div>
                        <div style={{ fontSize: 12.5, marginBottom: ncb.cdtClassification.triggeredBy?.length ? 6 : 0 }}>{ncb.cdtClassification.reasoning}</div>
                        {ncb.cdtClassification.triggeredBy?.length > 0 && (
                          <div style={{ fontSize: 11.5, fontStyle: 'italic' }}>Triggered by: {ncb.cdtClassification.triggeredBy.join(', ')}</div>
                        )}
                      </Callout>
                    )}
                    {ncb.cdtApplicable && (
                      <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginTop: 6 }}>
                        Confirm with CDT whether this acquisition requires their review or falls under their direct procurement authority before proceeding — the standard DGS/PD/DRU routing above may not be sufficient on its own.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ marginTop: 14, marginBottom: 8, fontWeight: 600, fontSize: 12, color: 'var(--slate-500)' }}>
            Contractor's Proprietary Letter
          </div>
          <UploadZone
            file={ncb.letterFile}
            onFile={handleProprietaryLetterUpload}
            accept=".pdf,.docx"
            title="Click or drag the proprietary letter here"
            subtitle="Must state the software/service is not available through any other source, dated within 12 months of solicitation (SCM Vol 2 §1507). PDF preferred — Penny reads it directly; .docx only gets its text extracted."
          />

          <div style={{ marginTop: 14 }}>
            <button className="btn btn-gold btn-sm" disabled={!ncb.acquisitionType || ncbLoading} onClick={draftNcbJustification}>
              {ncbLoading ? <><div className="spin spin-sm" /> Drafting…</> : 'Draft NCB Justification Narrative'}
            </button>
          </div>

          <OutputPanel
            label="NCB Justification Narrative"
            content={ncb.justificationText}
            loading={ncbLoading}
            loadMsg="Drafting NCB Justification narrative…"
            markdown={true}
            onCopy={() => {}}
          />

          {ncb.justificationText && (
            <>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => downloadMemoAsDocx('NCB Justification Narrative', ncb.justificationText, contract)}>
                  ⬇ Download as Word
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => downloadMemoAsPdf('NCB Justification Narrative', ncb.justificationText, contract).catch(e => toast.error(e.message))}>
                  ⬇ Download as PDF
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 8 }}>
                This narrative is meant to be copied into the DGS NCB Portal — NCB Justifications are submitted electronically, not as an attached document (SCM Vol 2 §1503). The downloads above are for your own records/review, not for portal submission. Keep the dated proprietary letter and cost-reasonableness documentation in your procurement file regardless of whether a formal justification is required.
              </div>
            </>
          )}

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--slate-200)' }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--slate-500)', marginBottom: 8 }}>
              Cost-Reasonableness Documentation
            </div>
            <div style={{ fontSize: 11, color: 'var(--slate-400)', marginBottom: 10 }}>
              Required for the procurement file regardless of whether a formal NCB Justification is needed — even fully policy/statute-exempt purchases must be documented as fair and reasonable in price (SCM Vol 2).
            </div>
            <button className="btn btn-gold btn-sm" disabled={costReasonLoading} onClick={draftCostReasonableness}>
              {costReasonLoading ? <><div className="spin spin-sm" /> Drafting…</> : 'Draft Cost-Reasonableness Documentation'}
            </button>
            <OutputPanel
              label="Cost-Reasonableness Documentation"
              content={ncb.costReasonText}
              loading={costReasonLoading}
              loadMsg="Drafting cost-reasonableness documentation…"
              markdown={true}
              onCopy={() => {}}
            />
            {ncb.costReasonText && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => downloadMemoAsDocx('Cost-Reasonableness Documentation', ncb.costReasonText, contract)}>
                  ⬇ Download as Word
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => downloadMemoAsPdf('Cost-Reasonableness Documentation', ncb.costReasonText, contract).catch(e => toast.error(e.message))}>
                  ⬇ Download as PDF
                </button>
              </div>
            )}
          </div>
        </CollapsibleCard>
      )}

      {/* ── CMAS Cards — only shown when procMethod === CMAS ──────── */}
      {isCMAS && (
        <>
          {/* CMAS Document Upload & Brief */}
          <CollapsibleCard
            id="cmas"
            title={"✦ " + lpaLabel + " — Master Agreement · Penny"}
            subtitle={cmasDoc ? "Master agreement on file — Penny can analyze it to build a persistent brief for this procurement." : "No master agreement uploaded yet. If you uploaded one at intake it will appear here automatically. Otherwise upload it now."}
            collapsed={collapsed.cmas}
            onToggle={() => toggleCard('cmas')}
            style={{ borderLeft: '3px solid var(--gold-400)' }}
          >
            {/* Upload area */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Master Agreement / Schedule Document</div>
              {!cmasDoc ? (
                <div
                  onClick={() => cmasFileRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) uploadCmasDoc(e.dataTransfer.files[0]); }}
                  style={{ border: '2px dashed var(--slate-300)', borderRadius: 'var(--r-md)', padding: '20px', textAlign: 'center', cursor: 'pointer', background: 'var(--slate-50)' }}
                >
                  <div style={{ fontSize: 24, marginBottom: 6 }}>📋</div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--slate-700)' }}>Upload Master Agreement / Schedule</div>
                  <div style={{ fontSize: 11.5, color: 'var(--slate-400)', marginTop: 4 }}>Upload here if you didn't provide one at intake, or if you switched to {lpaLabel} after creating this contract</div>
                  <input ref={cmasFileRef} type="file" accept=".pdf" style={{ display: 'none' }}
                    onChange={e => { if (e.target.files[0]) uploadCmasDoc(e.target.files[0]); }} />
                </div>
              ) : (
                <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', background: cmasContext ? 'var(--teal-50)' : 'var(--gold-50)', border: '1.5px solid ' + (cmasContext ? 'var(--teal-200)' : 'var(--gold-200)'), display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>📋</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--slate-800)' }}>{cmasDoc.fileName}</div>
                    <div style={{ fontSize: 11.5, color: cmasContext ? 'var(--teal-700)' : 'var(--gold-700)', marginTop: 2 }}>
                      {cmasContext ? '✦ Penny has analyzed this ' + lpaLabel + ' document — brief active' : 'Run analysis to generate Penny\'s ' + lpaLabel + ' brief'}
                    </div>
                    {cmasContext?.generatedAt && (
                      <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 1 }}>
                        Generated {Math.floor((Date.now() - new Date(cmasContext.generatedAt)) / 86400000) === 0 ? 'today' : Math.floor((Date.now() - new Date(cmasContext.generatedAt)) / 86400000) + ' days ago'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-gold btn-sm" disabled={cmasLoading} onClick={generateCmasBrief}>
                      {cmasLoading ? <><div className="spin spin-sm" /> Analyzing…</> : cmasContext ? '↺ Re-analyze' : '✦ Analyze Schedule'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setCmasDoc(null); setCmasContext(null); }} title="Remove document">×</button>
                  </div>
                </div>
              )}
            </div>

            {/* CMAS Brief */}
            {cmasContext?.brief && !cmasLoading && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
                  ✦ Penny's LPA Brief — active for all drafting
                </div>
                <div
                  style={{ padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', fontSize: 12.5, color: 'var(--navy-800)', lineHeight: 1.75, maxHeight: 400, overflowY: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: renderMd(cmasContext.brief) }}
                />
              </div>
            )}

            {/* CMAS Ordering Procedure Guidance — shown even without uploaded doc */}
            <div style={{ padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--slate-50)', border: '1px solid var(--slate-200)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 10 }}>✦ LPA / Master Agreement Ordering Procedure — General Guidance</div>
              {[
                { step: '1', title: 'Confirm Vendor Eligibility', desc: 'Verify the vendor holds an active contract under this LPA/schedule for the service or product category you need. Check DGS, CDT, or the applicable program office. Confirm the contract is not expired.' },
                { step: '2', title: 'Solicit Quotes (3+ Vendors)', desc: 'Contact at least 3 vendors under the same schedule or category. Document all vendor communications. If fewer than 3 eligible vendors exist, document the limited competition.' },
                { step: '3', title: 'Draft Scope of Work', desc: 'Use the Exhibit A SOW Studio below — Penny will draft a SOW aligned with the allowable service categories. Exhibit B and D are typically not required — the vendor\'s price list and master agreement terms apply.' },
                { step: '4', title: 'Document Price Reasonableness', desc: 'Compare quotes received and document why the selected price is fair and reasonable. Pricing must be at or below the approved rates on the master agreement or schedule.' },
                { step: '5', title: 'SB/DVBE Still Applies', desc: 'Small Business and DVBE participation goals still apply to LPA orders. Document outreach efforts and participation in the SB/DVBE Compliance stage.' },
                { step: '6', title: 'STD 213 & 215 Required', desc: 'Execute the STD 213 Standard Agreement and complete the STD 215 routing form. Route through DGS/OLS if above your PAAL threshold.' },
              ].map(({ step, title, desc }) => (
                <div key={step} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--navy-800)', color: 'var(--gold-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{step}</div>
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 2 }}>{title}</div>
                    <div style={{ fontSize: 12, color: 'var(--slate-600)', lineHeight: 1.6 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleCard>


        </>
      )}

      {/* ── Exhibit A SOW Studio ────────────────────────────────── */}
      <CollapsibleCard id="exhibitA" title="✦ Exhibit A — SOW Studio · Penny" subtitle="Two-step workflow — Penny drafts a complete SOW from your contract context, then you refine it." collapsed={collapsed.exhibitA} onToggle={() => toggleCard('exhibitA')}>

        {/* Step tabs */}
        <div className="sow-options">
          {[[1, 'Step 1', 'Create Draft', 'Draft with Penny, upload an existing one, or both'], [2, 'Step 2', 'Review & Improve', 'Penny identifies issues → revised draft']].map(([n, num, label, desc]) => (
            <div key={n} className={`sow-option-card ${sowStep === n ? 'active' : ''}`} onClick={() => setSowStep(n)}>
              <div className="sow-option-num">{num}</div>
              <div className="sow-option-label">{label}</div>
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 3 }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* ── Step 1: Create Draft — Penny drafts it, you upload one, or both ── */}
        {sowStep === 1 && (
          <div>
            {/* Context summary — show what Penny already knows */}
            <div style={{ background: 'var(--teal-50)', border: '1px solid var(--teal-200)', borderRadius: 'var(--r-md)', padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--teal-700)', marginBottom: 8 }}>✒ Penny already knows:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[
                  ['Contract Title', contract.title],
                  ['Contract No.', contract.contractNum],
                  ['Description', contract.description],
                  ['Budget', fmtMoney(contract.budget)],
                  ['Category', contract.category],
                  ['Method', contract.procMethod],
                  ['Request Doc', (contract.attachments || []).find(a => a.tag === 'Request Document')?.name],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                    <span style={{ color: 'var(--teal-600)', fontWeight: 600, minWidth: 100 }}>{label}:</span>
                    <span style={{ color: 'var(--slate-700)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
                  </div>
                ))}
                {(clarifyQuestions.requestor.length > 0 || clarifyQuestions.analyst.length > 0) && (
                  <div style={{ marginTop: 6, padding: '6px 10px', background: 'var(--teal-100)', borderRadius: 6, fontSize: 11.5, color: 'var(--teal-700)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>✦</span>
                    <span><strong>Clarifying questions included</strong> — Penny will incorporate any answered items directly into the SOW instead of using [PLACEHOLDER].</span>
                  </div>
                )}
              </div>
            </div>

            <div className="btn-row">
              <button className="btn btn-gold" disabled={sowLoading} onClick={runSowDraft}>
                {sowLoading ? <><div className="spin spin-sm" /> Drafting SOW…</> : <><span style={{marginRight:4}}>↓</span> Draft Exhibit A (.docx)</>}
              </button>
              <button className="btn btn-ghost" onClick={() => setSowShowUpload(s => !s)}>
                📎 {sowShowUpload ? 'Hide Upload' : 'Use Existing Exhibit A or SOW'}
              </button>
              {sowActiveSource && (
                <button className="btn btn-teal btn-sm" onClick={() => setSowStep(2)}>
                  Step 2 — Review & Improve →
                </button>
              )}
            </div>
            {sowLoading && <LoadRow msg="Penny is drafting your Scope of Work — this considers the SCM, compliance requirements, and any attached documents, so complex contracts can take 1-2 minutes…" />}

            {/* ── Inline upload option — same working document either way ── */}
            {sowShowUpload && (
              <div style={{ marginTop: 14 }}>
                <div style={{ background: 'var(--teal-50)', border: '1px solid var(--teal-200)', borderRadius: 'var(--r-md)', padding: '12px 16px', marginBottom: 12, fontSize: 12, color: 'var(--teal-700)' }}>
                  Already have a completed Exhibit A or SOW from outside GoldenScope? Upload it here — you can still run Penny's compliance review on it, or accept it as-is.
                </div>
                <UploadZone
                  file={sowReviewFile}
                  onFile={handleSowReviewUpload}
                  title="Upload existing Exhibit A (PDF or Word)"
                  subtitle="PDF preferred — Penny reads it directly; .docx only gets its text extracted"
                />
                {sowReviewFileLoading && <LoadRow msg="Reading document…" />}
              </div>
            )}

            {/* ── Which candidate is currently active ── */}
            {(sowDraftText || sowReviewFile) && (
              <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {sowDraftText && (
                  <div style={{ flex: '1 1 240px', padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (sowActiveSource === 'penny' ? 'var(--gold-400)' : 'var(--slate-200)'), background: sowActiveSource === 'penny' ? 'var(--gold-50)' : 'var(--white)' }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--slate-700)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      ✒ Penny's Draft {sowActiveSource === 'penny' && <span style={{ fontSize: 10, color: 'var(--gold-600)', fontWeight: 700 }}>● ACTIVE</span>}
                    </div>
                    {sowActiveSource !== 'penny' && (
                      <button className="btn btn-ghost btn-xs" style={{ marginTop: 6 }} onClick={() => setSowActiveSource('penny')}>✓ Use This One</button>
                    )}
                  </div>
                )}
                {sowReviewFile && (
                  <div style={{ flex: '1 1 240px', padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (sowActiveSource === 'upload' ? 'var(--gold-400)' : 'var(--slate-200)'), background: sowActiveSource === 'upload' ? 'var(--gold-50)' : 'var(--white)' }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--slate-700)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      📎 Uploaded: {sowReviewFile.name} {sowActiveSource === 'upload' && <span style={{ fontSize: 10, color: 'var(--gold-600)', fontWeight: 700 }}>● ACTIVE</span>}
                    </div>
                    {sowActiveSource !== 'upload' && (
                      <button className="btn btn-ghost btn-xs" style={{ marginTop: 6 }} onClick={() => setSowActiveSource('upload')}>✓ Use This One</button>
                    )}
                  </div>
                )}
              </div>
            )}

            {sowDraftText && !sowLoading && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--green-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--green-200)', fontSize: 12, color: 'var(--green-700)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>✓ SOW drafted and downloaded. Review the Word doc, then click <strong>Step 2 — Review & Improve</strong> to refine further.</span>
                {sowHistory.length > 0 && (
                  <button onClick={() => setShowSowHistory(s => !s)} style={{ fontSize: 11, color: 'var(--green-700)', background: 'none', border: '1px solid var(--green-300)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', flexShrink: 0, marginLeft: 10 }}>
                    🕐 {sowHistory.length} version{sowHistory.length !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
            )}

            {/* SOW Version History */}
            {showSowHistory && sowHistory.length > 0 && (
              <div style={{ marginTop: 8, border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', background: 'var(--slate-50)', borderBottom: '1px solid var(--slate-200)', fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>SOW Version History</span>
                  <button onClick={() => setShowSowHistory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--slate-400)', fontSize: 16 }}>×</button>
                </div>
                {sowHistory.map((v, i) => (
                  <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid var(--slate-100)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy-700)' }}>{v.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>{v.savedAt ? new Date(v.savedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''} · {v.text?.length || 0} characters</div>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={async () => {
                        if (window.confirm('Restore ' + v.label + '? Current draft will become the newest version in history.')) {
                          setSowDraftText(v.text);
                          toast.success(v.label + ' restored');
                        }
                      }}
                    >Restore</button>
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => buildAndDownloadDocx(
                        (contract.title || 'SOW') + ' — ' + v.label,
                        v.text,
                        profile, s215, contract, null, 'A', resolveProfile('A')
                      )}
                    >↓ .docx</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Review & Improve ── */}
        {sowStep === 2 && (
          <div>
            <Callout type="teal" style={{ marginBottom: 16 }}>
              Upload the SOW from Step 1 (or any existing SOW). Penny identifies specific issues — you approve which to fix, then download the revised draft.
            </Callout>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Choose SOW to Review</div>

              {/* Use current working draft directly — reflects the latest saved
                  version, whether that's the original Step 1 draft or a later
                  Step 2 revision (setSowDraftText updates on every save) */}
              {sowDraftText && (
                <div style={{ marginBottom: 12, padding: '12px 16px', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>✒</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--teal-800)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(contract.pennyDrafts?.sowHistory?.length > 0) ? 'Use Current Draft' : 'Use Step 1 Draft'}
                      {sowActiveSource === 'penny' && <span style={{ fontSize: 10, color: 'var(--gold-600)', fontWeight: 700 }}>● ACTIVE</span>}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--teal-600)' }}>
                      {(contract.pennyDrafts?.sowHistory?.length > 0)
                        ? 'Review the latest revision again — no re-upload needed'
                        : 'Review the SOW Penny just generated — no upload needed'}
                    </div>
                  </div>
                  <button className={sowActiveSource === 'penny' ? 'btn btn-gold btn-sm' : 'btn btn-teal btn-sm'} disabled={sowReviewLoading} onClick={() => { setSowActiveSource('penny'); runSowReview(true); }}>
                    {sowReviewLoading ? <><div className="spin spin-sm" /> Analyzing…</> : 'Analyze Current Draft'}
                  </button>
                </div>
              )}

              <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--slate-500)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                {sowDraftText ? 'Or upload a revised version:' : 'Upload SOW PDF or Word document:'}
                {sowActiveSource === 'upload' && sowReviewFile && <span style={{ fontSize: 10, color: 'var(--gold-600)', fontWeight: 700 }}>● ACTIVE</span>}
              </div>
              <UploadZone
                file={sowReviewFile}
                onFile={handleSowReviewUpload}
                accept=".pdf,.docx"
                loading={sowReviewFileLoading}
                title="Click or drag SOW PDF or .docx here"
                subtitle="Upload if you've made manual edits in Word — PDF preferred when possible, since Penny reads it directly"
              />
              {sowReviewFile && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className={sowActiveSource === 'upload' ? 'btn btn-gold btn-sm' : 'btn btn-teal btn-sm'} disabled={(!sowReviewFile.base64 && !sowReviewFile.extractedText && !sowReviewFile.storagePath) || sowReviewLoading} onClick={() => { setSowActiveSource('upload'); runSowReview(false); }}>
                    {sowReviewLoading ? <><div className="spin spin-sm" /> Analyzing…</> : 'Analyze Uploaded SOW'}
                  </button>
                  <button className="btn btn-teal btn-sm" disabled={!sowReviewFile.base64 && !sowReviewFile.extractedText && !sowReviewFile.storagePath}
                    onClick={() => { setSowActiveSource('upload'); promoteSowReviewFileToWorking(); }}
                    title="Use this document as-is, without running or waiting on Penny's review">
                    ✓ Use As-Is — No Revision Needed
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSowReviewFile(null); setSowFindings([]); setSowChecked({}); setSowReviewNotes(''); }}>
                    Clear
                  </button>
                </div>
              )}
              {sowReviewFile && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    What did you change, or what should Penny check/incorporate? (optional, but recommended)
                  </div>
                  <textarea
                    value={sowReviewNotes}
                    onChange={e => setSowReviewNotes(e.target.value)}
                    rows={3}
                    placeholder="e.g., I edited the Deliverables section to change the WMP count from 9 to 6, and added a table with delivery dates in section 3. Please verify these are consistent throughout and check for anything I may have missed."
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', fontSize: 12.5, border: '1.5px solid var(--slate-200)', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--white)', color: 'var(--slate-700)', lineHeight: 1.6 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 4 }}>
                    Penny also automatically checks against any clarifying-question replies already on file for this contract.
                  </div>
                </div>
              )}
              {sowReviewLoading && <LoadRow msg="Reading SOW and identifying issues — usually 20-30 seconds, longer for large documents…" />}
            </div>

            {sowFindings.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Review Findings</div>
                <p style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 12 }}>
                  High-priority items are pre-selected. Check the recommendations you want applied.
                </p>
                <div style={{ border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                  {sowFindings.map(f => (
                    <div key={f.id} style={{ display: 'flex', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--slate-100)', alignItems: 'flex-start', background: sowChecked[f.id] ? 'var(--blue-50)' : 'var(--white)' }}>
                      <input type="checkbox" checked={!!sowChecked[f.id]}
                        onChange={e => setSowChecked(p => ({ ...p, [f.id]: e.target.checked }))}
                        style={{ marginTop: 3, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                            background: f.priority === 'high' ? 'var(--red-50)' : f.priority === 'medium' ? 'var(--orange-50)' : 'var(--blue-50)',
                            color: f.priority === 'high' ? 'var(--red-600)' : f.priority === 'medium' ? 'var(--orange-600)' : 'var(--blue-600)',
                            textTransform: 'uppercase',
                          }}>{f.priority}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)' }}>{f.category}</span>
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{f.issue}</div>
                        <div style={{ fontSize: 12, color: 'var(--slate-600)' }}>{f.fix}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button className="btn btn-gold" disabled={sowFinalLoading || Object.values(sowChecked).filter(Boolean).length === 0} onClick={runSowFinal}>
                    {sowFinalLoading ? <><div className="spin spin-sm" /> Generating…</> : <><span style={{marginRight:4}}>↓</span> Generate Revised Exhibit A (.docx)</>}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>
                    {Object.values(sowChecked).filter(Boolean).length} of {sowFindings.length} recommendations selected
                  </span>
                </div>
                {sowFinalLoading && <LoadRow msg="Rewriting SOW with approved fixes — about 30 seconds…" />}
              </div>
            )}
          </div>
        )}
        {/* ── Exhibit A Status ── */}
        {(() => {
          const hasDraft = !!(exAFinal || sowDraftText);
          const hasReview = !!(contract.pennyDrafts?.exhibitReviewA);
          const status = exAStatus;
          const s = EXHIBIT_STATUSES[status];
          return (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1px solid ' + s.border, background: s.bg, display: 'flex', alignItems: 'center', gap: 12 }}>
              <ExhibitStatusBadge status={status} />
              <span style={{ fontSize: 12, color: 'var(--slate-600)', flex: 1 }}>
                {status === 'draft' && (hasDraft ? 'Draft ready — run SCM review then mark ready for review' : 'No draft yet')}
                {status === 'ready_for_review' && 'Marked ready — include in a review package on the Sharing tab'}
                {status === 'in_review' && 'Currently under review via Sharing tab'}
                {status === 'approved' && (contract.pennyDrafts?.exAApprovedBy ? 'Approved by ' + contract.pennyDrafts.exAApprovedBy + (contract.pennyDrafts?.exAApprovedAt ? ' on ' + new Date(contract.pennyDrafts.exAApprovedAt).toLocaleDateString() : '') : 'Approved')}
                {status === 'needs_revision' && 'Reviewer requested revisions — update and re-mark when ready'}
              </span>
              {status === 'draft' && hasDraft && (
                <button className="btn btn-gold btn-sm" onClick={() => {
                  // If there's no Final Version set yet, populate it from whatever
                  // the current working document is (matches what "Use Penny
                  // Draft" does) so marking ready also shows up immediately in
                  // Current Working Versions — previously these were disconnected:
                  // marking ready only set a status flag with no actual content.
                  if (!exAFinal && sowDraftText) {
                    updateC({ pennyDrafts: {
                      exAStatus: 'ready_for_review',
                      exAFinal: { source: 'penny_draft', content: sowDraftText, uploadedAt: new Date().toISOString() },
                    } });
                  } else {
                    setExStatus('exAStatus', 'ready_for_review');
                  }
                  toast.success('Exhibit A marked Ready for Review');
                }}>
                  {!hasReview ? '⚠ Mark Ready (no SCM review)' : '✓ Mark Ready for Review'}
                </button>
              )}
              {status === 'ready_for_review' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setExStatus('exAStatus', 'draft')}>↩ Back to Draft</button>
              )}
              {(status === 'approved' || status === 'needs_revision') && (
                <button className="btn btn-ghost btn-xs" style={{ color: 'var(--slate-400)' }} onClick={() => setExStatus('exAStatus', 'draft')}>Reset</button>
              )}
            </div>
          );
        })()}

        {/* ── Inline SCM Compliance Review — Exhibit A ── */}
        {(() => {
          const pennyFinalA = contract.pennyDrafts?.['exAFinal'];
          const reviewResultA = contract.pennyDrafts?.['exhibitReviewA'] || '';
          const isLoadingA = !!exhibitReviewLoading['A'];
          const hasPdfA = pennyFinalA?.source === 'user_uploaded' || pennyFinalA?.source === 'legal_revised' || pennyFinalA?.source === 'exec_approved';
          const hasDraftA = pennyFinalA?.source === 'penny_draft' && pennyFinalA?.content;
          let docSourceA = null;
          if (hasPdfA && (pennyFinalA.storagePath || pennyFinalA.base64)) docSourceA = { type: 'pdf', base64: pennyFinalA.base64 };
          else if (hasDraftA) docSourceA = { type: 'text', content: pennyFinalA.content };
          else if (sowDraftText) docSourceA = { type: 'text', content: sowDraftText };
          const statusMatchA = reviewResultA.match(/OVERALL STATUS:\s*([^\n]+)/);
          const statusA = statusMatchA ? statusMatchA[1].trim() : null;
          const statusColorA = statusA?.includes('✅') ? 'var(--green-600)' : statusA?.includes('⚠') ? 'var(--orange-600)' : statusA?.includes('❌') ? 'var(--red-600)' : 'var(--slate-400)';
          return (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>✦ SCM Compliance Review — Exhibit A</span>
                  {statusA && <span style={{ fontSize: 11, fontWeight: 700, color: statusColorA }}>{statusA}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-gold btn-sm" disabled={isLoadingA}
                    onClick={() => runExhibitReview('A', 'Scope of Work', docSourceA)}>
                    {isLoadingA ? <><div className="spin spin-sm" /> Reviewing…</> : reviewResultA ? '↺ Re-review' : '✦ Review'}
                  </button>
                  {reviewResultA && <button className="btn btn-ghost btn-xs" onClick={() => updateC({ pennyDrafts: { ['exhibitReviewA']: '' } })}>Clear</button>}
                </div>
              </div>
              {isLoadingA && <LoadRow msg="Penny is reviewing Exhibit A against SCM requirements…" />}
              {reviewResultA && !isLoadingA && (
                <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (statusA?.includes('✅') ? 'var(--green-200)' : statusA?.includes('❌') ? 'var(--red-200)' : 'var(--orange-200)'), background: statusA?.includes('✅') ? 'var(--green-50)' : statusA?.includes('❌') ? 'var(--red-50)' : 'var(--orange-50)', fontSize: 12.5, lineHeight: 1.7, color: 'var(--slate-700)', fontFamily: 'inherit' }} dangerouslySetInnerHTML={{ __html: renderMd(reviewResultA) }}>
                </div>
              )}
            </div>
          );
        })()}
      </CollapsibleCard>

      {/* ── Penny Drafts ──────────────────────────────────────── */}
      <CollapsibleCard id="pennyDrafts" title="✦ Solicitation Studio · Penny" subtitle="Penny drafts your procurement method recommendation and solicitation document for CalEProcure." collapsed={collapsed.pennyDrafts} onToggle={() => toggleCard('pennyDrafts')}>
        <div style={{ marginBottom: 12 }}>
          <button className="btn btn-ghost btn-sm" disabled={dvbeSbRecLoading} onClick={recommendDvbeSb}>
            {dvbeSbRecLoading ? <><div className="spin spin-sm" /> Analyzing…</> : '✦ Ask Penny to recommend DVBE & SB status'}
          </button>
          {contract.pennyDrafts?.dvbeSbRec && (() => {
            const rec = contract.pennyDrafts.dvbeSbRec;
            const items = [
              { key: 'dvbeGoal', field: 'dvbeGoalStatus', label: 'DVBE Goal' },
              { key: 'dvbeIncentive', field: 'dvbeIncentiveStatus', label: 'DVBE Incentive' },
              { key: 'sbGoal', field: 'sbGoalStatus', label: 'SB Goal' },
              { key: 'sbIncentive', field: 'sbIncentiveStatus', label: 'SB Incentive' },
            ].filter(i => rec[i.key]);
            return (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(i => (
                  <Callout key={i.key} type="blue">
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{i.label}: {rec[i.key].recommended}</div>
                    <div style={{ fontSize: 12.5, marginBottom: 8 }}>{rec[i.key].reasoning}</div>
                    <button className="btn btn-gold btn-sm" onClick={() => updateC({ dvbe: { ...(contract.dvbe || {}), [i.field]: rec[i.key].recommended } })}>
                      Apply {i.label} Recommendation
                    </button>
                  </Callout>
                ))}
                {items.length > 1 && (
                  <button className="btn btn-gold btn-sm" onClick={() => updateC({ dvbe: {
                    ...(contract.dvbe || {}),
                    ...(rec.dvbeGoal ? { dvbeGoalStatus: rec.dvbeGoal.recommended } : {}),
                    ...(rec.dvbeIncentive ? { dvbeIncentiveStatus: rec.dvbeIncentive.recommended } : {}),
                    ...(rec.sbGoal ? { sbGoalStatus: rec.sbGoal.recommended } : {}),
                    ...(rec.sbIncentive ? { sbIncentiveStatus: rec.sbIncentive.recommended } : {}),
                  } })}>
                    Apply All 4 Recommendations
                  </button>
                )}
                <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>
                  This is a recommendation based on contract details — you can always select something different below.
                </div>
            </div>
            );
          })()}
        </div>
        <div style={{ marginBottom: 16, padding: 14, background: (contract.dvbe?.dvbeGoalStatus || contract.dvbe?.dvbeIncentiveStatus) ? 'var(--teal-50)' : 'var(--orange-50)', border: '1.5px solid ' + ((contract.dvbe?.dvbeGoalStatus || contract.dvbe?.dvbeIncentiveStatus) ? 'var(--teal-200)' : 'var(--orange-200)'), borderRadius: 'var(--r-md)' }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--slate-700)', marginBottom: 4 }}>
            DVBE Participation for This Solicitation
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginBottom: 8 }}>
            Goal and Incentive are set independently — per the actual STD 816 form, an agency can waive the DVBE Program Requirement (goal) while still applying the Incentive, or waive either/both. Penny uses both fields together to decide what language to include. (Same fields as the SB/DVBE panel in Stage 7 — setting them here keeps both in sync.)
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', display: 'block', marginBottom: 3 }}>Goal Requirement</label>
              <select
                value={contract.dvbe?.dvbeGoalStatus || ''}
                onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeGoalStatus: e.target.value } })}
                style={{ width: 220, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, background: 'white' }}
              >
                <option value="">Not yet determined</option>
                <option value="applies">Applies</option>
                <option value="waived">Waived</option>
                <option value="not_required">Not required for this method</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', display: 'block', marginBottom: 3 }}>Incentive</label>
              <select
                value={contract.dvbe?.dvbeIncentiveStatus || ''}
                onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeIncentiveStatus: e.target.value } })}
                style={{ width: 220, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, background: 'white' }}
              >
                <option value="">Not yet determined</option>
                <option value="applies">Applies</option>
                <option value="waived">Waived</option>
                <option value="not_required">Not required for this method</option>
              </select>
            </div>
          </div>
          {contract.dvbe?.dvbeIncentiveStatus === 'applies' && (
            <div style={{ marginTop: 8, padding: 10, background: 'white', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)' }}>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', display: 'block', marginBottom: 4 }}>Incentive Structure</label>
              <select
                value={contract.dvbe?.dvbeIncentiveStructure || 'standard'}
                onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeIncentiveStructure: e.target.value } })}
                style={{ width: '100%', maxWidth: 320, padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12.5 }}
              >
                <option value="standard">Standard DGS scale (default — 1% incentive per 1% proposed DVBE participation, up to 5% max)</option>
                <option value="custom">Custom — specify a flat percentage or different scale</option>
              </select>
              {contract.dvbe?.dvbeIncentiveStructure === 'standard' && (
                <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 4 }}>
                  This is what departments use unless they've obtained managerial approval for an override, documented in the procurement file (SCM Vol 2 §1200s; CCR §1896.99.100).
                </div>
              )}
              {contract.dvbe?.dvbeIncentiveStructure === 'custom' && (
                <div style={{ marginTop: 6 }}>
                  <input
                    type="text"
                    value={contract.dvbe?.dvbeIncentiveCustomText || ''}
                    onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeIncentiveCustomText: e.target.value } })}
                    placeholder="e.g., flat 5% for all proposed DVBE participation, or describe your custom scale"
                    style={{ width: '100%', padding: '7px 9px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12.5, boxSizing: 'border-box' }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--orange-600)', marginTop: 4 }}>
                    Must stay within 1%–5% (low price) or 1%–5% of total points (high score), with managerial approval and rationale documented in the procurement file.
                  </div>
                </div>
              )}
            </div>
          )}
          {contract.dvbe?.dvbeGoalStatus === 'applies' && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, color: 'var(--slate-600)' }}>Goal percentage:</label>
              <input
                type="number" min="0" max="100" step="0.5"
                value={s215?.dvbePct ?? 3}
                onChange={e => updateC({ s215: { ...(s215 || {}), dvbePct: e.target.value } })}
                style={{ width: 70, padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
              />
              <span style={{ fontSize: 12, color: 'var(--slate-500)' }}>% — defaults to the statewide 3% minimum (GC §14838 / MVC §999), override if this agency sets a different target. Same field as STD 215 Field 24.</span>
            </div>
          )}
          {(contract.dvbe?.dvbeGoalStatus === 'waived' || contract.dvbe?.dvbeIncentiveStatus === 'waived') && (
            <div style={{ marginTop: 10, padding: 12, background: 'var(--orange-50)', border: '1.5px solid var(--orange-200)', borderRadius: 'var(--r-md)' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--orange-800)', marginBottom: 4 }}>
                ⚠ STD 816 required before releasing this solicitation
              </div>
              <div style={{ fontSize: 12, color: 'var(--orange-700)', lineHeight: 1.6, marginBottom: 8 }}>
                Waiving the {contract.dvbe?.dvbeGoalStatus === 'waived' && contract.dvbe?.dvbeIncentiveStatus === 'waived' ? 'Program Requirement AND Incentive' : contract.dvbe?.dvbeGoalStatus === 'waived' ? 'Program Requirement' : 'Incentive'} requires a signed <strong>DVBE Exemption Request (STD 816)</strong>, approved by a department designee and the highest-ranking executive or designee, <strong>before</strong> the solicitation is released. Keep it in the procurement file — upload the signed copy below once you have it so it's attached to the final package.
              </div>
              <a
                href="https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std816.pdf"
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--orange-800)', marginBottom: 10 }}
              >
                <span>↓</span> Download blank STD 816 form (DGS)
              </a>
              <UploadZone
                file={contract.dvbe?.std816File}
                onFile={handleStd816Upload}
                accept=".pdf,.docx"
                title="Click or drag the signed STD 816 here"
                subtitle="Required once approved — will be included in the final contract package. PDF preferred over .docx."
              />
            </div>
          )}
        </div>
        <div style={{ marginBottom: 16, padding: 14, background: (contract.dvbe?.sbGoalStatus || contract.dvbe?.sbIncentiveStatus) ? 'var(--teal-50)' : 'var(--orange-50)', border: '1.5px solid ' + ((contract.dvbe?.sbGoalStatus || contract.dvbe?.sbIncentiveStatus) ? 'var(--teal-200)' : 'var(--orange-200)'), borderRadius: 'var(--r-md)' }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--slate-700)', marginBottom: 4 }}>
            SB Participation for This Solicitation
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginBottom: 8 }}>
            Goal and Preference (incentive) are independent, same as DVBE — and both are independent of DVBE itself. Note: unlike DVBE, there is no dedicated SCM/DGS waiver form for SB — "Waived" here just means the agency has determined it doesn't apply to this contract, not that a formal exemption process was completed. (Same fields as the SB/DVBE panel in Stage 7 — setting them here keeps both in sync.)
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', display: 'block', marginBottom: 3 }}>Goal Requirement</label>
              <select
                value={contract.dvbe?.sbGoalStatus || ''}
                onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), sbGoalStatus: e.target.value } })}
                style={{ width: 220, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, background: 'white' }}
              >
                <option value="">Not yet determined</option>
                <option value="applies">Applies</option>
                <option value="waived">Waived</option>
                <option value="not_required">Not required for this method</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', display: 'block', marginBottom: 3 }}>Preference (Incentive)</label>
              <select
                value={contract.dvbe?.sbIncentiveStatus || ''}
                onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), sbIncentiveStatus: e.target.value } })}
                style={{ width: 220, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, background: 'white' }}
              >
                <option value="">Not yet determined</option>
                <option value="applies">Applies</option>
                <option value="waived">Waived</option>
                <option value="not_required">Not required for this method</option>
              </select>
            </div>
          </div>
          {contract.dvbe?.sbGoalStatus === 'applies' && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, color: 'var(--slate-600)' }}>Goal percentage:</label>
              <input
                type="number" min="0" max="100" step="0.5"
                value={s215?.sbPct ?? 25}
                onChange={e => updateC({ s215: { ...(s215 || {}), sbPct: e.target.value } })}
                style={{ width: 70, padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
              />
              <span style={{ fontSize: 12, color: 'var(--slate-500)' }}>% — defaults to the statewide 25% goal (GC §14838), override if this agency sets a different target.</span>
            </div>
          )}
        </div>
        {(() => {
          const renderDraftItem = ({ key, label, desc }) => {
          const src = sourceDocs[key];
          const isRec = key === 'rec';
          const recIsCollapsed = isRec && collapsed.procMethodRec;
          return (
            <div key={key} style={{ marginBottom: 16, padding: 16, background: 'var(--slate-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: recIsCollapsed ? 0 : 8, gap: 12, cursor: isRec ? 'pointer' : 'default' }}
                onClick={isRec ? () => toggleCard('procMethodRec') : undefined}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {isRec && <span style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 2, transform: recIsCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s' }}>▾</span>}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>{desc}</div>
                  </div>
                </div>
                {!recIsCollapsed && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                    <input
                      ref={el => { if (el) sourceDocRefs.current[key] = el; }}
                      type="file" accept=".pdf" style={{ display: 'none' }}
                      onChange={async e => {
                        const f = e.target.files[0]; if (!f) return;
                        const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(f); });
                        setSourceDocs(p => ({ ...p, [key]: { name: f.name, base64: b64 } }));
                      }}
                    />
                    {src && <span style={{ fontSize: 11, color: 'var(--green-600)', alignSelf: 'center' }}>✓ {src.name}</span>}
                    <button className="btn btn-ghost btn-xs" onClick={() => sourceDocRefs.current[key]?.click()}>
                      {src ? 'Replace Draft' : 'Upload My Draft'}
                    </button>
                    {src && <button className="btn btn-ghost btn-xs" onClick={() => setSourceDocs(p => { const n = { ...p }; delete n[key]; return n; })}>Clear</button>}
                    <button className="btn btn-primary btn-sm" disabled={draftLoading[key]} onClick={() => runDraft(key)}>
                      {draftLoading[key] ? <><div className="spin spin-sm" /> Drafting…</> : 'Draft'}
                    </button>
                  </div>
                )}
              </div>
              {!recIsCollapsed && (
              <>
              {key === 'sol' ? (
                draftLoading['sol'] && <LoadRow msg={'Drafting ' + label + '…'} />
              ) : (
                <OutputPanel
                  label={label}
                  content={draftResults[key]}
                  loading={draftLoading[key]}
                  loadMsg={'Drafting ' + label + '…'}
                  markdown={true}
                  onCopy={() => {}}
                  onSave={() => saveDoc(key, draftResults[key], 'draft', draftResults[key + '_refs'] || buildRefsList())}
                />
              )}

              {/* Solicitation .docx export */}
              {key === 'sol' && draftResults['sol'] && !draftLoading['sol'] && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-gold btn-sm"
                    onClick={() => buildAndDownloadDocx(
                      (contract.procMethod || 'Solicitation') + ' — ' + (contract.title || 'Contract'),
                      draftResults['sol'], profile, s215, contract, assignedCm, 'SOL', resolveProfile('SOL')
                    )}
                  >
                    <span style={{ marginRight: 4 }}>↓</span> Export as Word Doc (.docx)
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => saveDoc('sol', draftResults['sol'], 'draft', draftResults['sol_refs'] || buildRefsList())}
                  >
                    💾 Save to Vault
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => updateC({ showCalEProcure: !contract.showCalEProcure })}
                  >
                    📋 {contract.showCalEProcure ? 'Hide' : 'Show'} CalEProcure Posting Summary
                  </button>
                </div>
              )}

              {/* Method picker — only shown after a rec draft */}
              {key === 'rec' && recMethodPick && draftResults['rec'] && !draftLoading['rec'] && (
                <div style={{
                  marginTop: 12, padding: '14px 16px',
                  background: 'var(--navy-900)', borderRadius: 'var(--r-lg)',
                  border: '1px solid rgba(255,255,255,.08)',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold-400)', marginBottom: 10 }}>
                    ✒ Apply a Method to This Contract
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--navy-300)', marginBottom: 12, lineHeight: 1.6 }}>
                    Select a procurement method below. Your choice will update the Stage &amp; Info tab immediately.
                    {recMethodPick.selected && <span style={{ color: 'var(--green-400)', marginLeft: 6 }}>✓ Set to: <strong>{recMethodPick.selected}</strong></span>}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {/* Penny's recommendation */}
                    {recMethodPick.pennyMethod && (
                      <button
                        onClick={() => {
                          const m = recMethodPick.pennyMethod.split(' ')[0].split('/')[0].trim();
                          updateC({ procMethod: m });
                          setRecMethodPick(p => ({ ...p, selected: m }));
                          toast.success('Procurement method updated to ' + m);
                        }}
                        style={{
                          flex: 1, minWidth: 160, padding: '12px 14px',
                          background: recMethodPick.selected === recMethodPick.pennyMethod.split(' ')[0].split('/')[0].trim()
                            ? 'linear-gradient(135deg, var(--gold-500) 0%, var(--gold-400) 100%)'
                            : 'rgba(196,153,42,.12)',
                          border: '1.5px solid ' + (recMethodPick.selected === recMethodPick.pennyMethod.split(' ')[0].split('/')[0].trim() ? 'var(--gold-400)' : 'rgba(196,153,42,.3)'),
                          borderRadius: 'var(--r-md)', cursor: 'pointer',
                          color: recMethodPick.selected === recMethodPick.pennyMethod.split(' ')[0].split('/')[0].trim() ? 'var(--navy-900)' : 'var(--gold-300)',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', opacity: .7, marginBottom: 4 }}>
                          ✒ Penny Recommends
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{recMethodPick.pennyMethod.split('—')[0].trim()}</div>
                      </button>
                    )}

                    {/* User's pre-selected method (only shown if different from Penny's) */}
                    {recMethodPick.userMethod && !recMethodPick.same && (
                      <button
                        onClick={() => {
                          const m = recMethodPick.userMethod.split(' ')[0].split('/')[0].trim();
                          updateC({ procMethod: m });
                          setRecMethodPick(p => ({ ...p, selected: m }));
                          toast.success('Procurement method updated to ' + m);
                        }}
                        style={{
                          flex: 1, minWidth: 160, padding: '12px 14px',
                          background: recMethodPick.selected === recMethodPick.userMethod.split(' ')[0].split('/')[0].trim()
                            ? 'rgba(96,165,250,.25)'
                            : 'rgba(255,255,255,.04)',
                          border: '1.5px solid ' + (recMethodPick.selected === recMethodPick.userMethod.split(' ')[0].split('/')[0].trim() ? 'var(--blue-600)' : 'rgba(255,255,255,.1)'),
                          borderRadius: 'var(--r-md)', cursor: 'pointer',
                          color: 'var(--navy-200)', textAlign: 'left',
                        }}
                      >
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', opacity: .7, marginBottom: 4 }}>
                          Your Selection
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{recMethodPick.userMethod.split('—')[0].trim()}</div>
                      </button>
                    )}

                    {/* Keep current / no change */}
                    {contract.procMethod && (
                      <button
                        onClick={() => setRecMethodPick(p => ({ ...p, selected: null }))}
                        style={{
                          padding: '12px 14px', minWidth: 120,
                          background: 'transparent',
                          border: '1.5px solid rgba(255,255,255,.08)',
                          borderRadius: 'var(--r-md)', cursor: 'pointer',
                          color: 'var(--navy-400)', fontSize: 12,
                        }}
                      >
                        Keep current<br />
                        <span style={{ fontWeight: 600, color: 'var(--navy-300)' }}>{contract.procMethod}</span>
                      </button>
                    )}
                  </div>

                  {recMethodPick.same && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--green-400)', display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span>✓</span>
                      <span>Penny agrees with your selected method. Click to confirm it stays set.</span>
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
          );
          };
          return (
            <>
              {renderDraftItem(DRAFT_ITEMS[0])}

        {/* ── Conditional Attachments Panel ── */}
        {(() => {
          const budget = parseFloat(contract.budget) || 0;
          const isIT = contract.isIT === 'yes';
          const method = contract.procMethod || '';
          const isCompetitive = ['IFB', 'RFQ', 'RFP-Primary', 'RFP-Secondary'].includes(method);
          const isSB = contract.s215?.sbParticipation || contract.sbDvbeCertNum;
          const isDVBE = contract.dvbe?.required || contract.s215?.dvbeGoal;

          // Build required attachments based on contract characteristics
          const attachments = [];

          // Always required for competitive solicitations
          if (isCompetitive) {
            attachments.push({
              required: true, form: 'DGS PD 05-105', title: 'Bidder Declaration',
              reason: 'Required for all competitive solicitations. Bidder must declare SB/DVBE/NP status or document good faith effort.',
              scm: 'SCM Vol.' + (isIT ? '2' : '1') + ', Sec. 5.2', category: 'certification',
              url: 'https://www.documents.dgs.ca.gov/dgs/fmc/gs/pd/gspd05-105.pdf',
            });
          }

          // Darfur — contracts $1M+
          if (budget >= 1000000) {
            attachments.push({
              required: true, form: 'Darfur Certification', title: 'Darfur Contracting Act Certification',
              reason: 'Required for contracts $1M or more. Vendor must certify they do not engage in investment activities in Sudan.',
              scm: 'PCC §10475–10481', category: 'legal',
              url: 'https://www.documents.dgs.ca.gov/dgs/FMC/GS/PD/PD_1.pdf',
            });
          }

          // Iran — contracts $1M+
          if (budget >= 1000000) {
            attachments.push({
              required: true, form: 'Iran Certification', title: 'Iran Contracting Act Certification',
              reason: "Required for contracts $1M or more. Vendor must certify no investment in Iran's energy sector.",
              scm: 'PCC §2202–2208', category: 'legal',
              url: 'https://www.dgs.ca.gov/-/media/Divisions/PD/PTCS/OPPL/SCM/Iran_Contracting_Act_Verification_Form.pdf',
            });
          }

          // Civil Rights — all contracts
          attachments.push({
            required: true, form: 'STD 19', title: 'Nondiscrimination Compliance Statement',
            reason: 'Required on all state contracts. Contractor must certify compliance with CA civil rights laws including FEHA and Unruh Act.',
            scm: 'GC §12990, SCM Vol.' + (isIT ? '2' : '1'), category: 'civil_rights',
            url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std019.pdf',
          });

          // DVBE — required whenever a DVBE goal has been set for this contract, and
          // recommended by default on any competitively solicited contract since the
          // DVBE participation requirement applies "regardless of amount" unless the
          // agency exempts this specific contract (SCM 1 §8.12; MVC §999 et seq.)
          if (isDVBE || isCompetitive) {
            attachments.push({
              required: isDVBE,
              form: 'STD 843', title: 'DVBE Declarations',
              reason: isDVBE ? 'DVBE participation goal is set — bidders must complete DVBE declarations.' : 'Recommended for competitively solicited contracts — DVBE participation applies regardless of dollar amount unless this contract is specifically exempted.',
              scm: 'MVC §999.5, SCM Sec. 5.4', category: 'dvbe',
              url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std843.pdf',
            });
          }

          // STD 816 — required whenever DVBE has been affirmatively waived for
          // this contract; the exemption must be signed BEFORE release and kept
          // in the procurement file (SCM Vol.1 Ch.8; CCR §1896.99.100-120).
          if ((contract.dvbe?.dvbeGoalStatus === 'waived' || contract.dvbe?.dvbeIncentiveStatus === 'waived')) {
            attachments.push({
              required: true,
              form: 'STD 816', title: 'DVBE Exemption Request (signed)',
              reason: 'DVBE Goal and/or Incentive was marked Waived for this contract — a signed STD 816, approved by a department designee and the highest-ranking executive/designee, must be in the procurement file before release.',
              scm: 'SCM Vol.1 Ch.8, CCR §1896.99.100', category: 'dvbe',
              url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std816.pdf',
            });
          }

          // IT-specific
          if (isIT) {
            attachments.push({
              required: false, form: 'GSPD-401 IT', title: 'IT General Provisions (incorporated by reference — not a separate bidder submission)',
              reason: 'Applies automatically to IT contracts via incorporation by reference into the contract terms, similar to the GTC — no separate document for bidders to complete or the analyst to attach.',
              scm: 'SCM Vol.2, Sec. 6.1', category: 'it',
              url: 'https://www.dgs.ca.gov/PD/Forms',
            });
          }

          // Consulting contracts
          if ((contract.category || '').toLowerCase().includes('consult')) {
            attachments.push({
              required: true, form: 'STD 204', title: 'Payee Data Record (STD 204)',
              reason: 'Required for consulting contracts. Contractor must complete before award for tax reporting purposes.',
              scm: 'SCM Vol.' + (isIT ? '2' : '1') + ', Sec. 7.1', category: 'tax',
              url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std204.pdf',
            });
          }

          const categoryColors = {
            legal: { bg: 'var(--red-50)', border: 'var(--red-200)', badge: 'var(--red-600)' },
            civil_rights: { bg: 'var(--blue-50)', border: 'var(--blue-200)', badge: 'var(--blue-600)' },
            certification: { bg: 'var(--teal-50)', border: 'var(--teal-200)', badge: 'var(--teal-700)' },
            dvbe: { bg: 'var(--purple-50)', border: 'var(--purple-200)', badge: 'var(--purple-700)' },
            sb: { bg: 'var(--green-50)', border: 'var(--green-200)', badge: 'var(--green-700)' },
            it: { bg: 'var(--navy-50)', border: 'var(--navy-200)', badge: 'var(--navy-700)' },
            tax: { bg: 'var(--orange-50)', border: 'var(--orange-200)', badge: 'var(--orange-600)' },
          };

          if (attachments.length === 0) return null;

          return (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>📋 Conditional Attachments & Certifications</span>
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'var(--navy-100)', color: 'var(--navy-600)', fontWeight: 600 }}>
                  {attachments.filter(a => a.required).length} required · {attachments.filter(a => !a.required).length} recommended
                </span>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginBottom: 12 }}>
                Based on: {method || 'no method set'} · ${budget >= 1000000 ? (budget/1000000).toFixed(1)+'M' : budget >= 1000 ? Math.round(budget/1000)+'K' : budget || '0'} · {isIT ? 'IT' : 'Non-IT'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {attachments.map((att, i) => {
                  const colors = categoryColors[att.category] || categoryColors.certification;
                  const overrides = contract.pennyDrafts?.solAttachOverrides || {};
                  // Default: required = included, recommended = excluded unless analyst adds
                  const defaultIncluded = att.required;
                  const isIncluded = att.form in overrides ? overrides[att.form] : defaultIncluded;
                  return (
                    <div key={i} style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (isIncluded ? colors.border : 'var(--slate-200)'), background: isIncluded ? colors.bg : 'var(--slate-50)', opacity: isIncluded ? 1 : 0.6, transition: 'all .15s' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        {/* Checkbox */}
                        <label style={{ display: 'flex', alignItems: 'center', marginTop: 2, cursor: 'pointer', flexShrink: 0 }}>
                          <input type="checkbox" checked={isIncluded}
                            onChange={e => {
                              const updated = { ...overrides, [att.form]: e.target.checked };
                              updateC({ pennyDrafts: { solAttachOverrides: updated } });
                            }}
                            style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--navy-600)' }} />
                        </label>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: att.required ? colors.badge : 'var(--slate-200)', color: att.required ? '#fff' : 'var(--slate-600)', whiteSpace: 'nowrap', marginTop: 1 }}>
                          {att.required ? 'REQUIRED' : 'RECOMMENDED'}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-800)', textDecoration: isIncluded ? 'none' : 'line-through' }}>{att.title}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 1 }}>
                            <span style={{ fontSize: 11.5, fontFamily: 'monospace', color: colors.badge }}>{att.form}</span>
                            {att.url && (
                              <a href={att.url} target="_blank" rel="noreferrer"
                                style={{ fontSize: 10.5, color: 'var(--navy-600)', textDecoration: 'underline', fontFamily: 'inherit' }}
                                onClick={e => e.stopPropagation()}>
                                ↓ Download form
                              </a>
                            )}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--slate-600)', marginTop: 4, lineHeight: 1.6 }}>{att.reason}</div>
                          <div style={{ fontSize: 10.5, color: 'var(--slate-400)', marginTop: 4 }}>📖 {att.scm}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--slate-400)' }}>
                ✓ Checked = included in solicitation · Uncheck to exclude · Changes are saved automatically
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                Always verify current requirements with the SCM and your agency legal counsel before solicitation.
              </div>

              {/* ── Form Search & Manual Add ── */}
              {(() => {
                const FORM_LIBRARY = [
                  // ── Standard Agreement & Routing ──
                  { form: 'STD 213', title: 'Standard Agreement', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std213.pdf' },
                  { form: 'STD 213A', title: 'Standard Agreement Amendment', url: 'https://www.documents.dgs.ca.gov/dgs/FMC/PDF/std213A.pdf' },
                  { form: 'STD 215', title: 'Agreement Summary', url: 'https://www.documents.dgs.ca.gov/dgs/FMC/PDF/std215.pdf' },
                  { form: 'STD 204', title: 'Payee Data Record', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std204.pdf' },
                  // ── Certifications & Compliance ──
                  { form: 'Darfur Contracting Act', title: 'Darfur Contracting Act Certification (PCC §10478)', url: 'https://www.documents.dgs.ca.gov/dgs/FMC/GS/PD/PD_1.pdf' },
                  { form: 'CA Civil Rights Laws', title: 'California Civil Rights Laws Attachment (contracts ≥$100K)', url: 'https://www.dgs.ca.gov/-/media/Divisions/OLS/Forms/CALIFORNIA-CIVIL-RIGHTS-LAWS-ATTACHMENT.pdf' },
                  { form: 'Iran Contracting Act', title: 'Iran Contracting Act Certification (PCC §2204, contracts ≥$1M)', url: 'https://www.dgs.ca.gov/-/media/Divisions/PD/PTCS/OPPL/SCM/Iran_Contracting_Act_Verification_Form.pdf' },
                  { form: 'CCC', title: 'Contractor Certification Clauses (CCC-04/2017) — incorporated by reference into the GTC, not a separate bidder submission', url: 'https://www.dgs.ca.gov/OLS/Resources/Page-Content/Office-of-Legal-Services-Resources-List-Folder/Standard-Contract-Language' },
                  { form: 'GTC', title: 'General Terms and Conditions (GTC-04/2017)', url: 'https://www.dgs.ca.gov/OLS/Resources/Page-Content/Office-of-Legal-Services-Resources-List-Folder/Standard-Contract-Language' },
                  { form: 'CalRecycle 74', title: 'Postconsumer-Content Certification (SABRC program)', url: 'https://www2.calrecycle.ca.gov/Docs/Web/112038' },
                  { form: 'STD 19', title: 'Nondiscrimination Compliance Statement', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std019.pdf' },
                  // ── SB/DVBE & Bidding ──
                  { form: 'STD 843', title: 'DVBE Declarations', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std843.pdf' },
                  { form: 'STD 816', title: 'DVBE Exemption Request (signed)', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std816.pdf' },
                  { form: 'STD 821', title: 'Contract Advertising Exemption Request', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std821.pdf' },
                  { form: 'DGS PD 05-105', title: 'Bidder Declaration (Written)', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/gs/pd/gspd05-105.pdf' },
                  { form: 'DGS PD 05-106', title: 'Bidder Declaration (Verbal)', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/gs/pd/gspd05-106.pdf' },
                  { form: 'DGS PD 09-007', title: 'Non-Competitively Bid Contract Justification', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/gs/pd/gspd09-007.pdf' },
                  // ── Other Standard Forms ──
                  { form: 'STD 830', title: 'TACPA Preference Request Form', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std830.pdf' },
                  { form: 'STD 4', title: 'Contract/Contractors Evaluation', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std004.pdf' },
                  { form: 'STD 21', title: 'Drug-Free Workplace Certification', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std021.pdf' },
                  { form: 'STD 261', title: 'Authorization to Use Privately Owned Vehicle', url: 'https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std261.pdf' },
                  // ── Tax / IRS ──
                  { form: 'W-9', title: 'Request for Taxpayer ID (IRS)', url: 'https://www.irs.gov/pub/irs-pdf/fw9.pdf' },
                  { form: 'W-8BEN', title: 'Certificate of Foreign Status (IRS)', url: 'https://www.irs.gov/pub/irs-pdf/fw8ben.pdf' },
                ];

                const customAtts = contract.pennyDrafts?.solAttachCustom || [];
                const [formSearch, setFormSearch] = React.useState('');
                const [showLibrary, setShowLibrary] = React.useState(false);
                const [manualForm, setManualForm] = React.useState('');
                const [manualTitle, setManualTitle] = React.useState('');
                const [manualUrl, setManualUrl] = React.useState('');

                const filteredForms = formSearch.trim().length > 1
                  ? FORM_LIBRARY.filter(f =>
                      f.form.toLowerCase().includes(formSearch.toLowerCase()) ||
                      f.title.toLowerCase().includes(formSearch.toLowerCase()))
                  : FORM_LIBRARY;

                const alreadyAdded = (form) => {
                  // Custom additions
                  if (customAtts.some(a => a.form === form)) return true;
                  // Only mark as added if it's in the conditional panel AND checked
                  const overrides = contract.pennyDrafts?.solAttachOverrides || {};
                  const conditionalForms = {
                    'GSPD-05-105': isCompetitive,
                    'Darfur Certification': budget >= 1000000,
                    'Iran Certification': budget >= 1000000,
                    'GSPD-ADM-1023': true,
                    'STD 843': !!(isDVBE || isCompetitive),
                    'STD 816': (contract.dvbe?.dvbeGoalStatus === 'waived' || contract.dvbe?.dvbeIncentiveStatus === 'waived'),
                    'GSPD-401 IT': isIT,
                    'STD 204': (contract.category||'').toLowerCase().includes('consult'),
                  };
                  if (form in conditionalForms) {
                    const defaultOn = conditionalForms[form];
                    const isChecked = form in overrides ? overrides[form] : defaultOn;
                    return isChecked;
                  }
                  return false;
                };

                function addCustom(att) {
                  const existing = customAtts.find(a => a.form === att.form);
                  if (existing) return;
                  updateC({ pennyDrafts: { solAttachCustom: [...customAtts, att] } });
                  toast.success(att.form + ' added to solicitation');
                }

                function removeCustom(form) {
                  updateC({ pennyDrafts: { solAttachCustom: customAtts.filter(a => a.form !== form) } });
                }

                return (
                  <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>🔍 Add Additional Forms</span>
                    </div>

                    {/* Search bar */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <input
                        value={formSearch}
                        onChange={e => { setFormSearch(e.target.value); setShowLibrary(true); }}
                        onFocus={() => setShowLibrary(true)}
                        placeholder="Search form library (e.g. STD 204, DVBE, conflict of interest…)"
                        style={{ flex: 1, padding: '7px 12px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12.5, fontFamily: 'inherit' }}
                      />
                      <a href={'https://www.dgs.ca.gov/OLS/Forms?search=' + encodeURIComponent(formSearch)}
                        target="_blank" rel="noreferrer"
                        className="btn btn-ghost btn-sm" style={{ whiteSpace: 'nowrap' }}>
                        🔗 Search DGS
                      </a>
                    </div>

                    {/* Form library results */}
                    {showLibrary && (
                      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', marginBottom: 10 }}>
                        {filteredForms.length === 0 && (
                          <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--slate-400)', textAlign: 'center' }}>
                            No matching forms — try Search DGS above or add manually below
                          </div>
                        )}
                        {filteredForms.map((f, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: i < filteredForms.length - 1 ? '1px solid var(--slate-100)' : 'none', background: alreadyAdded(f.form) ? 'var(--slate-50)' : '#fff' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)', fontFamily: 'monospace' }}>{f.form}</span>
                              <span style={{ fontSize: 12, color: 'var(--slate-600)', marginLeft: 8 }}>{f.title}</span>
                            </div>
                            <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--navy-600)', textDecoration: 'underline', whiteSpace: 'nowrap' }}>↓ Form</a>
                            <button className="btn btn-ghost btn-xs" disabled={alreadyAdded(f.form)}
                              onClick={() => { addCustom(f); setShowLibrary(false); setFormSearch(''); }}
                              style={{ whiteSpace: 'nowrap', color: alreadyAdded(f.form) ? 'var(--slate-300)' : 'var(--navy-700)' }}>
                              {alreadyAdded(f.form) ? '✓ Added' : '+ Add'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Manual add */}
                    <details style={{ marginBottom: 10 }}>
                      <summary style={{ fontSize: 12, color: 'var(--slate-500)', cursor: 'pointer', userSelect: 'none' }}>+ Add form manually (not in library)</summary>
                      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 8 }}>
                        <input value={manualForm} onChange={e => setManualForm(e.target.value)} placeholder="Form # (e.g. STD 999)"
                          style={{ padding: '6px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'monospace' }} />
                        <input value={manualTitle} onChange={e => setManualTitle(e.target.value)} placeholder="Form title / description"
                          style={{ padding: '6px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'inherit' }} />
                        <input value={manualUrl} onChange={e => setManualUrl(e.target.value)} placeholder="URL (optional)"
                          style={{ padding: '6px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}
                        disabled={!manualForm.trim() || !manualTitle.trim()}
                        onClick={() => {
                          addCustom({ form: manualForm.trim(), title: manualTitle.trim(), url: manualUrl.trim() || null });
                          setManualForm(''); setManualTitle(''); setManualUrl('');
                        }}>
                        + Add to Solicitation
                      </button>
                    </details>

                    {/* Custom attachments list */}
                    {customAtts.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Custom Additions</div>
                        {customAtts.map((att, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--teal-200)', background: 'var(--teal-50)' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: 'var(--teal-700)' }}>{att.form}</span>
                            <span style={{ fontSize: 12, color: 'var(--slate-600)', flex: 1 }}>{att.title}</span>
                            {att.url && <a href={att.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--navy-600)', textDecoration: 'underline' }}>↓ Form</a>}
                            <button onClick={() => removeCustom(att.form)} style={{ background: 'none', border: 'none', color: 'var(--red-400)', cursor: 'pointer', fontSize: 13, padding: 0 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Upload Custom Solicitation Attachments ── */}
                    {(() => {
                      const solFiles = contract.pennyDrafts?.solAttachFiles || [];
                      const [uploadingSolFile, setUploadingSolFile] = React.useState(false);
                      const [solFileLabel, setSolFileLabel] = React.useState('');

                      async function uploadSolFile(file) {
                        if (!file) return;
                        const { data: { user } } = await supabase.auth.getUser();
                        if (!user || !contract?.id) { toast.error('Not authenticated'); return; }
                        setUploadingSolFile(true);
                        try {
                          const safeName = file.name.replace(/\s+/g, '_');
                          const path = user.id + '/' + contract.id + '/solicitation/attachments/' + safeName;
                          const { error } = await supabase.storage
                            .from('exhibit-files')
                            .upload(path, file, { upsert: true });
                          if (error) throw error;
                          const label = solFileLabel.trim() || file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');
                          const entry = {
                            id: Date.now().toString(),
                            label: label,
                            fileName: file.name,
                            storagePath: path,
                            size: file.size,
                            type: file.type || 'application/octet-stream',
                            uploadedAt: new Date().toISOString(),
                          };
                          updateC({ pennyDrafts: { solAttachFiles: [...solFiles, entry] } });
                          setSolFileLabel('');
                          toast.success('📎 ' + file.name + ' uploaded as solicitation attachment');
                        } catch (e) {
                          console.error('Sol file upload error:', e);
                          toast.error('Upload failed: ' + e.message);
                        } finally {
                          setUploadingSolFile(false);
                        }
                      }

                      async function downloadSolFile(entry) {
                        try {
                          const { data, error } = await supabase.storage
                            .from('exhibit-files')
                            .download(entry.storagePath);
                          if (error) throw error;
                          const url = URL.createObjectURL(data);
                          const a = document.createElement('a');
                          a.href = url; a.download = entry.fileName; a.click();
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          toast.error('Download failed: ' + e.message);
                        }
                      }

                      async function removeSolFile(id) {
                        const entry = solFiles.find(f => f.id === id);
                        if (entry) {
                          try {
                            await supabase.storage.from('exhibit-files').remove([entry.storagePath]);
                          } catch (e) { console.warn('Storage delete failed:', e.message); }
                        }
                        updateC({ pennyDrafts: { solAttachFiles: solFiles.filter(f => f.id !== id) } });
                      }

                      return (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
                            📎 Custom File Attachments
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--slate-400)', marginBottom: 10, lineHeight: 1.5 }}>
                            Upload price sheets, evaluation criteria, technical requirements, or any other documents to include in the solicitation package. Vendors will receive these with the solicitation.
                          </div>

                          {/* Upload row */}
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                            <input value={solFileLabel} onChange={e => setSolFileLabel(e.target.value)}
                              placeholder="Label (e.g. Exhibit B Att. 1 — Price Sheet)"
                              style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'inherit' }} />
                            <label className="btn btn-gold btn-sm" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {uploadingSolFile ? <><div className="spin spin-sm" /> Uploading…</> : '↑ Upload File'}
                              <input type="file" accept=".pdf,.docx,.xlsx,.xls,.doc,.csv,.txt" hidden
                                disabled={uploadingSolFile}
                                onChange={e => { if (e.target.files?.[0]) { uploadSolFile(e.target.files[0]); e.target.value = ''; } }} />
                            </label>
                          </div>

                          {/* Uploaded files list */}
                          {solFiles.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {solFiles.map(f => {
                                const ext = (f.fileName || '').split('.').pop().toUpperCase();
                                const sizeStr = f.size > 1048576 ? (f.size / 1048576).toFixed(1) + ' MB' : Math.round(f.size / 1024) + ' KB';
                                return (
                                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--gold-200)', background: 'var(--gold-50)' }}>
                                    <span style={{
                                      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                                      background: ext === 'PDF' ? '#fecaca' : ext === 'XLSX' || ext === 'XLS' ? '#bbf7d0' : '#dbeafe',
                                      color: ext === 'PDF' ? '#991b1b' : ext === 'XLSX' || ext === 'XLS' ? '#166534' : '#1e40af',
                                    }}>{ext}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</div>
                                      <div style={{ fontSize: 10, color: 'var(--slate-400)' }}>{f.fileName} · {sizeStr} · {new Date(f.uploadedAt).toLocaleDateString()}</div>
                                    </div>
                                    <button onClick={() => downloadSolFile(f)} className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '3px 8px' }}>↓ Download</button>
                                    <button onClick={() => { if (window.confirm('Remove "' + f.label + '" from this solicitation?')) removeSolFile(f.id); }}
                                      style={{ background: 'none', border: 'none', color: 'var(--red-400)', cursor: 'pointer', fontSize: 13, padding: 0 }}>✕</button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

            </div>
          );
        })()}

              {renderDraftItem(DRAFT_ITEMS[1])}
            </>
          );
        })()}

        {/* ── Solicitation Status ── */}
        {(() => {
          const hasDraft = !!(draftResults['sol']);
          const hasReview = !!(contract.pennyDrafts?.exhibitReviewSOL);
          const status = solStatus;
          const s = EXHIBIT_STATUSES[status];
          return (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1px solid ' + s.border, background: s.bg, display: 'flex', alignItems: 'center', gap: 12 }}>
              <ExhibitStatusBadge status={status} />
              <span style={{ fontSize: 12, color: 'var(--slate-600)', flex: 1 }}>
                {status === 'draft' && (hasDraft ? 'Draft ready — run SCM review then mark ready for review' : 'No solicitation draft yet')}
                {status === 'ready_for_review' && 'Marked ready — include in a review package on the Sharing tab'}
                {status === 'in_review' && 'Currently under review via Sharing tab'}
                {status === 'approved' && (contract.pennyDrafts?.solApprovedBy ? 'Approved by ' + contract.pennyDrafts.solApprovedBy + (contract.pennyDrafts?.solApprovedAt ? ' on ' + new Date(contract.pennyDrafts.solApprovedAt).toLocaleDateString() : '') : 'Approved')}
                {status === 'needs_revision' && 'Reviewer requested revisions — update and re-mark when ready'}
              </span>
              {status === 'draft' && hasDraft && (
                <button className="btn btn-gold btn-sm" onClick={() => { setExStatus('solStatus', 'ready_for_review'); toast.success('Solicitation marked Ready for Review'); }}>
                  {!hasReview ? '⚠ Mark Ready (no SCM review)' : '✓ Mark Ready for Review'}
                </button>
              )}
              {status === 'ready_for_review' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setExStatus('solStatus', 'draft')}>↩ Back to Draft</button>
              )}
              {(status === 'approved' || status === 'needs_revision') && (
                <button className="btn btn-ghost btn-xs" style={{ color: 'var(--slate-400)' }} onClick={() => setExStatus('solStatus', 'draft')}>Reset</button>
              )}
            </div>
          );
        })()}

        {/* ── Inline SCM Compliance Review — Solicitation ── */}
        {(() => {
          const solDraft = draftResults['sol'];
          const reviewResultSOL = contract.pennyDrafts?.['exhibitReviewSOL'] || '';
          const isLoadingSOL = !!exhibitReviewLoading['SOL'];
          const docSourceSOL = solDraft ? { type: 'text', content: solDraft } : null;
          const statusMatchSOL = reviewResultSOL.match(/OVERALL STATUS:\s*([^\n]+)/);
          const statusSOL = statusMatchSOL ? statusMatchSOL[1].trim() : null;
          const statusColorSOL = statusSOL?.includes('✅') ? 'var(--green-600)' : statusSOL?.includes('⚠') ? 'var(--orange-600)' : statusSOL?.includes('❌') ? 'var(--red-600)' : 'var(--slate-400)';
          return (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>✦ SCM Compliance Review — Solicitation</span>
                  {statusSOL && <span style={{ fontSize: 11, fontWeight: 700, color: statusColorSOL }}>{statusSOL}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {!solDraft && !reviewResultSOL && (
                    <span style={{ fontSize: 10, color: 'var(--slate-400)', fontStyle: 'italic' }}>Draft the solicitation above first</span>
                  )}
                  <button className="btn btn-gold btn-sm" disabled={isLoadingSOL || !solDraft}
                    onClick={() => runExhibitReview('SOL', 'Solicitation Document', docSourceSOL)}>
                    {isLoadingSOL ? <><div className="spin spin-sm" /> Reviewing…</> : reviewResultSOL ? '↺ Re-review' : '✦ Review'}
                  </button>
                  {reviewResultSOL && <button className="btn btn-ghost btn-xs" onClick={() => updateC({ pennyDrafts: { ['exhibitReviewSOL']: '' } })}>Clear</button>}
                </div>
              </div>
              {isLoadingSOL && <LoadRow msg="Penny is reviewing the solicitation document against SCM requirements…" />}
              {reviewResultSOL && !isLoadingSOL && (
                <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (statusSOL?.includes('✅') ? 'var(--green-200)' : statusSOL?.includes('❌') ? 'var(--red-200)' : 'var(--orange-200)'), background: statusSOL?.includes('✅') ? 'var(--green-50)' : statusSOL?.includes('❌') ? 'var(--red-50)' : 'var(--orange-50)', fontSize: 12.5, lineHeight: 1.7, color: 'var(--slate-700)', fontFamily: 'inherit' }} dangerouslySetInnerHTML={{ __html: renderMd(reviewResultSOL) }}>
                </div>
              )}
            </div>
          );
        })()}
      </CollapsibleCard>

      {/* ── CMAS Quote Comparison — below Solicitation Studio ── */}
      {isCMAS && (
        <CollapsibleCard
          id="cmasQuotes"
          title="✦ CMAS Quote Comparison · Penny"
          subtitle="Document quotes from at least 3 vendors under this agreement. Required for price reasonableness determination."
          collapsed={collapsed.cmasQuotes}
          onToggle={() => toggleCard('cmasQuotes')}
          style={{ borderLeft: '3px solid var(--gold-400)' }}
        >
            {(() => {
              const quotes = contract.pennyDrafts?.cmasQuotes || [
                { vendor: '', cmasNum: '', amount: '', sb: false, dvbe: false, notes: '', selected: false },
                { vendor: '', cmasNum: '', amount: '', sb: false, dvbe: false, notes: '', selected: false },
                { vendor: '', cmasNum: '', amount: '', sb: false, dvbe: false, notes: '', selected: false },
              ];
              const setQuotes = (v) => updateC({ pennyDrafts: { cmasQuotes: v } });
              const updQuote = (idx, field, val) => setQuotes(quotes.map((q, i) => i === idx ? { ...q, [field]: val } : q));
              const addQuote = () => setQuotes([...quotes, { vendor: '', cmasNum: '', amount: '', sb: false, dvbe: false, notes: '', selected: false }]);
              const removeQuote = (idx) => setQuotes(quotes.filter((_, i) => i !== idx));
              const priceRationale = contract.pennyDrafts?.cmasPriceRationale || '';
              const setPriceRationale = (v) => updateC({ pennyDrafts: { cmasPriceRationale: v } });
              const selectedCount = quotes.filter(q => q.selected).length;
              const filledCount = quotes.filter(q => q.vendor.trim()).length;

              return (
                <div>
                  {/* Quote Document Upload */}
                  <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 'var(--r-md)', background: 'var(--slate-50)', border: '1.5px solid var(--slate-200)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 2 }}>✒ Upload Quote Documents</div>
                        <div style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>Upload vendor PDFs or exported email chains — Penny will extract vendor name, CMAS number, and quote amount into the table below</div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <input ref={quoteFileRef} type="file" accept=".pdf" multiple style={{ display: 'none' }}
                          onChange={e => { if (e.target.files?.length) { analyzeQuoteDocs(Array.from(e.target.files)); e.target.value = ''; }}} />
                        <button
                          className="btn btn-gold btn-sm"
                          disabled={quoteAnalyzing}
                          onClick={() => quoteFileRef.current?.click()}
                        >
                          {quoteAnalyzing ? <><div className="spin spin-sm" /> Analyzing…</> : '✦ Upload & Analyze'}
                        </button>
                      </div>
                    </div>
                    {quoteAnalyzing && (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--navy-600)', fontStyle: 'italic' }}>
                        Penny is reading your documents and extracting quote data — about 15 seconds…
                      </div>
                    )}
                  </div>

                  {filledCount < 3 && (
                    <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--orange-50)', border: '1px solid var(--orange-200)', fontSize: 12.5, color: 'var(--orange-700)' }}>
                      ⚠ At least 3 vendor quotes are required. {filledCount}/3 vendors entered so far.
                    </div>
                  )}
                  {filledCount >= 3 && selectedCount === 0 && (
                    <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--blue-50)', border: '1px solid var(--blue-200)', fontSize: 12.5, color: 'var(--blue-700)' }}>
                      ℹ Mark the selected vendor in the table below and document price reasonableness.
                    </div>
                  )}
                  {selectedCount === 1 && filledCount >= 3 && (
                    <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', fontSize: 12.5, color: 'var(--teal-700)', fontWeight: 600 }}>
                      ✓ Quote comparison complete — {quotes.find(q => q.selected)?.vendor || 'selected vendor'} selected
                    </div>
                  )}

                  {/* Quote table */}
                  <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: 'var(--navy-50)', borderBottom: '2px solid var(--slate-200)' }}>
                          {['Vendor Name', 'CMAS #', 'Quote Amount', 'SB', 'DVBE', 'Notes', 'Selected', ''].map(h => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--slate-600)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {quotes.map((q, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--slate-100)', background: q.selected ? 'var(--teal-50)' : idx % 2 === 0 ? 'white' : 'var(--slate-50)' }}>
                            <td style={{ padding: '6px 8px' }}>
                              <input value={q.vendor} onChange={e => updQuote(idx, 'vendor', e.target.value)} placeholder="Vendor name"
                                style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--slate-200)', fontSize: 12, boxSizing: 'border-box' }} />
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <input value={q.cmasNum} onChange={e => updQuote(idx, 'cmasNum', e.target.value)} placeholder="e.g., 4-14-06-0001A"
                                style={{ width: '130px', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--slate-200)', fontSize: 12 }} />
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <input value={q.amount} onChange={e => updQuote(idx, 'amount', e.target.value)} placeholder="$0.00"
                                style={{ width: '100px', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--slate-200)', fontSize: 12 }} />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <input type="checkbox" checked={q.sb} onChange={e => updQuote(idx, 'sb', e.target.checked)} />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <input type="checkbox" checked={q.dvbe} onChange={e => updQuote(idx, 'dvbe', e.target.checked)} />
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <input value={q.notes} onChange={e => updQuote(idx, 'notes', e.target.value)} placeholder="Notes"
                                style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--slate-200)', fontSize: 12 }} />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <input type="radio" name="selectedVendor" checked={q.selected}
                                onChange={() => setQuotes(quotes.map((qq, i) => ({ ...qq, selected: i === idx })))} />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              {quotes.length > 3 && (
                                <button onClick={() => removeQuote(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-400)', fontSize: 14 }}>×</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                    <button className="btn btn-ghost btn-sm" onClick={addQuote}>+ Add Vendor</button>
                  </div>

                  {/* Price reasonableness */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', marginBottom: 6 }}>Price Reasonableness Determination</div>
                    <textarea
                      value={priceRationale}
                      onChange={e => setPriceRationale(e.target.value)}
                      rows={3}
                      placeholder="Document why the selected vendor's price is fair and reasonable — e.g., lowest responsive quote, verified against CMAS price list, within published GSA schedule rates…"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', fontSize: 12.5, border: '1.5px solid var(--slate-200)', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              );
            })()}
          </CollapsibleCard>
      )}

      {/* ── Exhibit B Studio ─────────────────────────────────────── */}
      <CollapsibleCard id="exhibitB" title="✦ Exhibit B — Budget Detail & Payment Provisions Studio · Penny" subtitle={isCMAS ? "Not typically required for " + lpaLabel + " orders — the vendor's price list under the master agreement serves this function." : "Penny reads your sample contracts and extracts budget and payment clauses. You select which to include."} collapsed={collapsed.exhibitB} onToggle={() => !isCMAS && toggleCard('exhibitB')} titleStyle={{ color: 'var(--navy-700)' }} style={{ opacity: isCMAS ? 0.4 : 1, pointerEvents: isCMAS ? 'none' : 'auto', transition: 'opacity .2s' }}>

        <div style={{ background: 'var(--teal-50)', border: '1px solid var(--teal-200)', borderRadius: 'var(--r-md)', padding: '10px 16px', marginBottom: 14, fontSize: 12, color: 'var(--teal-700)' }}>
          Already have a completed Exhibit B from outside GoldenScope? Skip ahead to <strong>"Upload an Existing Exhibit B"</strong> below to use it as your working document instead of having Penny draft one.
        </div>
        {(() => {
          // Branch on whether the agency has a saved Exhibit B baseline.
          // When present, the user skips the analyze-templates and select-
          // clauses steps and just clicks Draft — Penny customizes the
          // baseline for this contract.
          const exhibitBBaseline = agencyDocTemplate?.exhibit_b_template?.sections;
          const hasBaseline = Array.isArray(exhibitBBaseline) && exhibitBBaseline.filter(s => s.active !== false).length > 0;

          if (hasBaseline) {
            const activeCount = exhibitBBaseline.filter(s => s.active !== false).length;
            return (
              <>
                <Callout type="teal" style={{ marginBottom: 14 }}>
                  Your agency has a saved Exhibit B baseline with <strong>{activeCount} section{activeCount === 1 ? '' : 's'}</strong>.
                  Penny will customize it for this contract — substituting placeholders, expanding the payment schedule based on the contract budget, and adapting any per-contract specifics.
                  To edit the baseline, go to <strong>Agency Admin → Doc Profile</strong>.
                </Callout>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button className="btn btn-gold" disabled={exBDraftLoading} onClick={runExBDraft}>
                    {exBDraftLoading ? <><div className="spin spin-sm" /> Drafting from baseline…</> : '📄 Draft Exhibit B'}
                  </button>
                  {exBDraftLoading && <span style={{ fontSize: 12, color: 'var(--slate-400)' }}>Customizing the baseline for this contract…</span>}
                </div>
              </>
            );
          }

          // Legacy path — no agency baseline. Keep the existing analyze+select+draft flow.
          if (getMatchingTemplates('B').length === 0) {
            return (
              <Callout type="orange">
                No templates with Exhibit B (Budget Detail & Payment Provisions) found for <strong>{contract.category || 'this contract type'}</strong>.
                Go to <strong>Template Library</strong> in the sidebar and upload a sample contract that includes Exhibit B.
              </Callout>
            );
          }
          return (
          <>
            <Callout type="teal" style={{ marginBottom: 14 }}>
              <strong>{getMatchingTemplates('B').length} template{getMatchingTemplates('B').length > 1 ? 's' : ''}</strong> found
              for <strong>{contract.category || 'Professional Services'}</strong>.
              {getMatchingTemplates('B').length > 1
                ? ' Penny will synthesize across all of them — clauses that appear in multiple templates are pre-selected.'
                : ' Add more templates of this type to improve synthesis accuracy.'}
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--slate-500)' }}>
                💡 Tip: in <strong>Agency Admin → Doc Profile</strong>, extract a reusable Exhibit B baseline so future contracts skip these per-draft analysis steps.
              </div>
            </Callout>

            {exBFindings.length === 0 ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn btn-primary" disabled={exBLoading} onClick={runExBAnalysis}>
                  {exBLoading ? <><div className="spin spin-sm" /> Analyzing templates…</> : 'Step 1 — Analyze Templates'}
                </button>
                {exBLoading && <span style={{ fontSize: 12, color: 'var(--slate-400)' }}>Reading your sample contracts…</span>}
              </div>
            ) : (
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                  Step 2 — Select Payment Clauses to Include
                </div>
                <p style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 14 }}>
                  Penny found these clauses in your sample contracts. Pre-selected items are standard for this contract type.
                </p>

                {/* Group by category */}
                {['schedule', 'invoicing', 'rates', 'withholding', 'dispute', 'other'].map(cat => {
                  const items = exBFindings.filter(f => f.category === cat);
                  if (items.length === 0) return null;
                  const catLabels = { schedule: 'Payment Schedule', invoicing: 'Invoicing Requirements', rates: 'Rates & Not-to-Exceed', withholding: 'Withholding Provisions', dispute: 'Invoice Disputes', other: 'Other Provisions' };
                  return (
                    <div key={cat} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--slate-400)', marginBottom: 6 }}>{catLabels[cat]}</div>
                      {items.map(f => (
                        <div key={f.id} style={{
                          display: 'flex', gap: 10, padding: '9px 12px', marginBottom: 4,
                          background: exBChecked[f.id] ? 'var(--blue-50)' : 'var(--slate-50)',
                          border: '1px solid ' + (exBChecked[f.id] ? 'var(--blue-100)' : 'var(--slate-200)'),
                          borderRadius: 'var(--r-md)', alignItems: 'flex-start',
                        }}>
                          <input type="checkbox" checked={!!exBChecked[f.id]}
                            onChange={e => setExBChecked(p => ({ ...p, [f.id]: e.target.checked }))}
                            style={{ marginTop: 3, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 12.5 }}>{f.section}</div>
                            <div style={{ fontSize: 12, color: 'var(--slate-600)', marginTop: 2 }}>{f.clause}</div>
                            {f.note && (
                              <div style={{ fontSize: 11, marginTop: 3,
                                color: f.note.toLowerCase().includes('found in') ? 'var(--teal-600)' : 'var(--slate-400)',
                                fontStyle: 'italic',
                              }}>{f.note}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 16 }}>
                  <button className="btn btn-gold" disabled={exBDraftLoading || Object.values(exBChecked).filter(Boolean).length === 0} onClick={runExBDraft}>
                    {exBDraftLoading ? <><div className="spin spin-sm" /> Drafting Exhibit B…</> : 'Step 3 — Draft Exhibit B — Budget Detail & Payment Provisions (.docx)'}
                  </button>
                  <span style={{ fontSize: 11.5, color: 'var(--slate-400)' }}>
                    {Object.values(exBChecked).filter(Boolean).length} of {exBFindings.length} clauses selected
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setExBFindings([]); setExBChecked({}); }}>
                    Reset
                  </button>
                </div>
                {exBDraftLoading && <div className="loading-row" style={{ marginTop: 10 }}><div className="spin" /><span>Drafting Exhibit B — Budget Detail & Payment Provisions…</span></div>}
              </div>
            )}
          </>
          );
        })()}

        {/* ── Working Draft — Exhibit B ── */}
        <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--navy-700)', marginBottom: 8 }}>
            📋 Working Draft — Exhibit B
          </div>
          {exBDraftText ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11.5, color: 'var(--green-600)' }}>
                ✓ Draft saved{exBDraftAt ? ' · ' + new Date(exBDraftAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                <span style={{ color: 'var(--slate-400)', marginLeft: 6 }}>({Math.round(exBDraftText.length / 1000)}K chars)</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost btn-sm" onClick={async () => {
                  try {
                    await buildAndDownloadDocx('Exhibit B — Budget Detail and Payment Provisions', exBDraftText, profile, s215, contract, assignedCm, 'B', resolveProfile('B'));
                    toast.success('Exhibit B working draft downloaded');
                  } catch (e) { toast.error('Download failed: ' + e.message); }
                }}>↓ Re-download (.docx)</button>
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                  ↑ Upload revised (.docx)
                  <input type="file" accept=".docx" hidden onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = '';
                    setExBUploadLoading(true);
                    try {
                      const text = await extractDocxText(file);
                      if (!text || text.length < 50) throw new Error('Extracted text is too short — is the document empty?');
                      updateC({ pennyDrafts: { exBDraft: text, exBDraftAt: new Date().toISOString() } });
                      toast.success('Working draft updated from ' + file.name + ' (' + Math.round(text.length / 1000) + 'K chars)');
                    } catch (err) { toast.error('Upload failed: ' + err.message); }
                    finally { setExBUploadLoading(false); }
                  }} />
                </label>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-500)' }}
                  onClick={() => { if (window.confirm('Clear the Exhibit B working draft? This cannot be undone.')) updateC({ pennyDrafts: { exBDraft: '', exBDraftAt: null } }); }}>
                  ✕ Clear
                </button>
              </div>
              {exBUploadLoading && <div className="loading-row"><div className="spin" /><span>Extracting text from docx…</span></div>}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--slate-400)', lineHeight: 1.5 }}>
              No working draft saved. Draft one above, or upload your own:
              <label style={{ marginLeft: 8, cursor: 'pointer', color: 'var(--gold-600)', fontWeight: 600, textDecoration: 'underline' }}>
                Upload .docx
                <input type="file" accept=".docx" hidden onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = '';
                  setExBUploadLoading(true);
                  try {
                    const text = await extractDocxText(file);
                    if (!text || text.length < 50) throw new Error('Extracted text is too short — is the document empty?');
                    updateC({ pennyDrafts: { exBDraft: text, exBDraftAt: new Date().toISOString() } });
                    toast.success('Working draft saved from ' + file.name);
                  } catch (err) { toast.error('Upload failed: ' + err.message); }
                  finally { setExBUploadLoading(false); }
                }} />
              </label>
              {exBUploadLoading && <div className="loading-row" style={{ marginTop: 6 }}><div className="spin" /><span>Extracting text from docx…</span></div>}
            </div>
          )}
        </div>

        {/* ── Upload revised Exhibit B + structured review ── */}
        <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--navy-700)', marginBottom: 8 }}>
            📝 Upload an Existing Exhibit B (or a Revision to Review)
          </div>
          <div style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 10, lineHeight: 1.5 }}>
            Already have a completed Exhibit B, or made manual edits to Penny's draft? Save as PDF and upload it here — Penny will compare it against the agency baseline and flag issues, or you can accept it as-is below.
          </div>
          <UploadZone
            file={exBReviewFile}
            onFile={handleExBReviewUpload}
            title="Upload Exhibit B (PDF)"
            subtitle="Penny will review against the agency baseline and SCM requirements"
          />
          {exBReviewFile && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-gold btn-sm" disabled={exBReviewLoading} onClick={runExBReview}>
                {exBReviewLoading ? <><div className="spin spin-sm" /> Analyzing…</> : '✦ Review Exhibit B'}
              </button>
              <button className="btn btn-teal btn-sm" onClick={() => {
                updateC({ pennyDrafts: { exBFinal: {
                  source: 'user_uploaded', fileName: exBReviewFile.name, size: exBReviewFile.size,
                  base64: exBReviewFile.base64, uploadedAt: new Date().toISOString(),
                } } });
                toast.success('"' + exBReviewFile.name + '" set as the Final Version for Exhibit B — visible in Current Working Versions.');
              }} title="Use this document as-is, without running or waiting on Penny's review">
                ✓ Use As-Is — No Revision Needed
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setExBReviewFile(null); setExBReviewFindings([]); setExBReviewChecked({}); }}>
                Clear
              </button>
            </div>
          )}
          {exBReviewLoading && <LoadRow msg="Reading Exhibit B and identifying issues — usually 20-30 seconds, longer for large documents…" />}
          {exBReviewFindings.length > 0 && !exBReviewLoading && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-700)' }}>
                {exBReviewFindings.length} finding{exBReviewFindings.length === 1 ? '' : 's'} — select fixes to apply:
              </div>
              {exBReviewFindings.map(f => (
                <label key={f.id} style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 12px', borderRadius: 8,
                  border: '1px solid ' + (f.priority === 'high' ? 'var(--red-200)' : f.priority === 'medium' ? 'var(--orange-200)' : 'var(--slate-200)'),
                  background: f.priority === 'high' ? 'var(--red-50)' : f.priority === 'medium' ? 'var(--orange-50)' : 'var(--slate-50)',
                  cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={!!exBReviewChecked[f.id]}
                    onChange={() => setExBReviewChecked(c => ({ ...c, [f.id]: !c[f.id] }))}
                    style={{ marginTop: 2, width: 16, height: 16 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '1px 6px', borderRadius: 4,
                        background: f.priority === 'high' ? '#fca5a5' : f.priority === 'medium' ? '#fed7aa' : '#e2e8f0',
                        color: f.priority === 'high' ? '#991b1b' : f.priority === 'medium' ? '#9a3412' : '#475569',
                      }}>{f.priority}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--slate-400)' }}>{f.category}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--navy-800)', fontWeight: 600 }}>{f.issue}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--slate-600)', marginTop: 2 }}>Fix: {f.fix}</div>
                  </div>
                </label>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button className="btn btn-gold btn-sm" disabled={exBRewriteLoading || Object.values(exBReviewChecked).filter(Boolean).length === 0} onClick={runExBRewrite}>
                  {exBRewriteLoading ? <><div className="spin spin-sm" /> Rewriting…</> : '📄 Apply Fixes & Download Revised Exhibit B'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Inline SCM Compliance Review ── */}
        {(() => {
          const pennyFinalB = contract.pennyDrafts?.['exBFinal'];
          const reviewResultB = contract.pennyDrafts?.['exhibitReviewB'] || '';
          const isLoadingB = !!exhibitReviewLoading['B'];
          const hasPdfB = pennyFinalB?.source === 'user_uploaded' || pennyFinalB?.source === 'legal_revised' || pennyFinalB?.source === 'exec_approved';
          const hasDraftB = pennyFinalB?.source === 'penny_draft' && pennyFinalB?.content;
          let docSourceB = null;
          if (hasPdfB && (pennyFinalB.storagePath || pennyFinalB.base64)) docSourceB = { type: 'pdf', base64: pennyFinalB.base64 };
          else if (hasDraftB) docSourceB = { type: 'text', content: pennyFinalB.content };
          const statusMatchB = reviewResultB.match(/OVERALL STATUS:\s*([^\n]+)/);
          const statusB = statusMatchB ? statusMatchB[1].trim() : null;
          const statusColorB = statusB?.includes('✅') ? 'var(--green-600)' : statusB?.includes('⚠') ? 'var(--orange-600)' : statusB?.includes('❌') ? 'var(--red-600)' : 'var(--slate-400)';
          return (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>✦ SCM Compliance Review — Exhibit B</span>
                  {statusB && <span style={{ fontSize: 11, fontWeight: 700, color: statusColorB }}>{statusB}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-gold btn-sm" disabled={isLoadingB}
                    onClick={() => runExhibitReview('B', 'Budget Detail & Payment Provisions', docSourceB)}>
                    {isLoadingB ? <><div className="spin spin-sm" /> Reviewing…</> : reviewResultB ? '↺ Re-review' : '✦ Review'}
                  </button>
                  {reviewResultB && <button className="btn btn-ghost btn-xs" onClick={() => updateC({ pennyDrafts: { ['exhibitReviewB']: '' } })}>Clear</button>}
                </div>
              </div>
              {isLoadingB && <LoadRow msg="Penny is reviewing Exhibit B against SCM requirements…" />}
              {reviewResultB && !isLoadingB && (
                <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (statusB?.includes('✅') ? 'var(--green-200)' : statusB?.includes('❌') ? 'var(--red-200)' : 'var(--orange-200)'), background: statusB?.includes('✅') ? 'var(--green-50)' : statusB?.includes('❌') ? 'var(--red-50)' : 'var(--orange-50)', fontSize: 12.5, lineHeight: 1.7, color: 'var(--slate-700)', fontFamily: 'inherit' }} dangerouslySetInnerHTML={{ __html: renderMd(reviewResultB) }}>
                </div>
              )}
            </div>
          );
        })()}
        {/* ── Exhibit B Status ── */}
        {(() => {
          const hasDraft = !!exBFinal;
          const hasReview = !!contract.pennyDrafts?.exhibitReviewB;
          const status = exBStatus;
          const s = EXHIBIT_STATUSES[status];
          return (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1px solid ' + s.border, background: s.bg, display: 'flex', alignItems: 'center', gap: 12 }}>
              <ExhibitStatusBadge status={status} />
              <span style={{ fontSize: 12, color: 'var(--slate-600)', flex: 1 }}>
                {status === 'draft' && (hasDraft ? 'Final Version set — mark ready for review' : 'No Final Version set yet')}
                {status === 'ready_for_review' && 'Marked ready — include in a review package on the Sharing tab'}
                {status === 'in_review' && 'Currently under review via Sharing tab'}
                {status === 'approved' && 'Approved'}
                {status === 'needs_revision' && 'Reviewer requested revisions — update and re-mark when ready'}
              </span>
              {status === 'draft' && hasDraft && (
                <button className="btn btn-gold btn-sm" onClick={() => { setExStatus('exBStatus', 'ready_for_review'); toast.success('Exhibit B marked Ready for Review'); }}>
                  {!hasReview ? '⚠ Mark Ready (no SCM review)' : '✓ Mark Ready for Review'}
                </button>
              )}
              {status === 'ready_for_review' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setExStatus('exBStatus', 'draft')}>↩ Back to Draft</button>
              )}
            </div>
          );
        })()}
      </CollapsibleCard>
      <CollapsibleCard id="exhibitD" title="✦ Exhibit D — Special Terms and Conditions Studio · Penny" subtitle={isCMAS ? "Not typically required for " + lpaLabel + " orders — the master agreement General Terms and Conditions cover this." : "Penny extracts special terms from your sample contracts — conflict of interest, confidentiality, DVBE, data ownership, and more."} collapsed={collapsed.exhibitD} onToggle={() => !isCMAS && toggleCard('exhibitD')} titleStyle={{ color: 'var(--navy-700)' }} style={{ opacity: isCMAS ? 0.4 : 1, pointerEvents: isCMAS ? 'none' : 'auto', transition: 'opacity .2s' }}>

        {(() => {
          // Branch on whether the agency has a saved Exhibit D baseline.
          const exhibitDBaseline = agencyDocTemplate?.exhibit_d_template?.sections;
          const hasBaseline = Array.isArray(exhibitDBaseline) && exhibitDBaseline.filter(s => s.active !== false).length > 0;

          if (hasBaseline) {
            const activeCount = exhibitDBaseline.filter(s => s.active !== false).length;
            return (
              <>
                <Callout type="teal" style={{ marginBottom: 14 }}>
                  Your agency has a saved Exhibit D baseline with <strong>{activeCount} section{activeCount === 1 ? '' : 's'}</strong>.
                  Penny will customize it for this contract — substituting placeholders and adapting per-contract specifics.
                  To edit the baseline, go to <strong>Agency Admin → Doc Profile</strong>.
                </Callout>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button className="btn btn-gold" disabled={exCDraftLoading} onClick={runExCDraft}>
                    {exCDraftLoading ? <><div className="spin spin-sm" /> Drafting from baseline…</> : '📄 Draft Exhibit D'}
                  </button>
                  {exCDraftLoading && <span style={{ fontSize: 12, color: 'var(--slate-400)' }}>Customizing the baseline for this contract…</span>}
                </div>
              </>
            );
          }

          // Legacy path
          if (getMatchingTemplates('D').length === 0) {
            return (
              <Callout type="orange">
                No templates with Exhibit D (Special Terms and Conditions) found for <strong>{contract.category || 'this contract type'}</strong>.
                Go to <strong>Template Library</strong> in the sidebar and upload a sample contract that includes Exhibit D.
              </Callout>
            );
          }
          return (
          <>
            <Callout type="teal" style={{ marginBottom: 14 }}>
              <strong>{getMatchingTemplates('D').length} template{getMatchingTemplates('D').length > 1 ? 's' : ''}</strong> found
              for <strong>{contract.category || 'Professional Services'}</strong>.
              {getMatchingTemplates('D').length > 1
                ? ' Penny will synthesize across all of them — provisions found in multiple templates are pre-selected.'
                : ' Add more templates of this type to improve synthesis accuracy.'}
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--slate-500)' }}>
                💡 Tip: in <strong>Agency Admin → Doc Profile</strong>, extract a reusable Exhibit D baseline so future contracts skip these per-draft analysis steps.
              </div>
            </Callout>

            {exCFindings.length === 0 ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn btn-primary" disabled={exCLoading} onClick={runExCAnalysis}>
                  {exCLoading ? <><div className="spin spin-sm" /> Analyzing templates…</> : 'Step 1 — Analyze Templates'}
                </button>
                {exCLoading && <span style={{ fontSize: 12, color: 'var(--slate-400)' }}>Reading your sample contracts for standard provisions…</span>}
              </div>
            ) : (
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                  Step 2 — Select Provisions to Include
                </div>
                <p style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 14 }}>
                  Penny found these provisions in your sample contracts. Pre-selected items are standard for state contracts.
                </p>

                {/* Group by category */}
                {['conflict', 'confidentiality', 'dvbe', 'data', 'sanctions', 'liability', 'termination', 'other'].map(cat => {
                  const items = exCFindings.filter(f => f.category === cat);
                  if (items.length === 0) return null;
                  const catLabels = { conflict: 'Conflict of Interest / Form 700', confidentiality: 'Confidentiality & Nondisclosure', dvbe: 'DVBE / SB Preference Program', data: 'Data Ownership & Records', sanctions: 'Sanctions & Compliance', liability: 'Liability & Indemnification', termination: 'Termination Provisions', other: 'Other Special Terms' };
                  return (
                    <div key={cat} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--slate-400)', marginBottom: 6 }}>{catLabels[cat]}</div>
                      {items.map(f => (
                        <div key={f.id} style={{
                          display: 'flex', gap: 10, padding: '9px 12px', marginBottom: 4,
                          background: exCChecked[f.id] ? 'var(--blue-50)' : 'var(--slate-50)',
                          border: '1px solid ' + (exCChecked[f.id] ? 'var(--blue-100)' : 'var(--slate-200)'),
                          borderRadius: 'var(--r-md)', alignItems: 'flex-start',
                        }}>
                          <input type="checkbox" checked={!!exCChecked[f.id]}
                            onChange={e => setExCChecked(p => ({ ...p, [f.id]: e.target.checked }))}
                            style={{ marginTop: 3, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 12.5 }}>{f.section}</div>
                            <div style={{ fontSize: 12, color: 'var(--slate-600)', marginTop: 2 }}>{f.clause}</div>
                            {f.note && (
                              <div style={{ fontSize: 11, marginTop: 3,
                                color: f.note.toLowerCase().includes('found in') ? 'var(--teal-600)' : 'var(--slate-400)',
                                fontStyle: 'italic',
                              }}>{f.note}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 16 }}>
                  <button className="btn btn-gold" disabled={exCDraftLoading || Object.values(exCChecked).filter(Boolean).length === 0} onClick={runExCDraft}>
                    {exCDraftLoading ? <><div className="spin spin-sm" /> Drafting Exhibit C…</> : 'Step 3 — Draft Exhibit C — Special Terms and Conditions (.docx)'}
                  </button>
                  <span style={{ fontSize: 11.5, color: 'var(--slate-400)' }}>
                    {Object.values(exCChecked).filter(Boolean).length} of {exCFindings.length} provisions selected
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setExCFindings([]); setExCChecked({}); }}>
                    Reset
                  </button>
                </div>
                {exCDraftLoading && <div className="loading-row" style={{ marginTop: 10 }}><div className="spin" /><span>Drafting Exhibit C — Special Terms and Conditions…</span></div>}
              </div>
            )}
          </>
          );
        })()}

        {/* ── Working Draft — Exhibit D ── */}
        <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--navy-700)', marginBottom: 8 }}>
            📋 Working Draft — Exhibit D
          </div>
          {exDDraftText ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11.5, color: 'var(--green-600)' }}>
                ✓ Draft saved{exDDraftAt ? ' · ' + new Date(exDDraftAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                <span style={{ color: 'var(--slate-400)', marginLeft: 6 }}>({Math.round(exDDraftText.length / 1000)}K chars)</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost btn-sm" onClick={async () => {
                  try {
                    await buildAndDownloadDocx('Exhibit D — Special Terms and Conditions', exDDraftText, profile, s215, contract, assignedCm, 'D', resolveProfile('D'));
                    toast.success('Exhibit D working draft downloaded');
                  } catch (e) { toast.error('Download failed: ' + e.message); }
                }}>↓ Re-download (.docx)</button>
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                  ↑ Upload revised (.docx)
                  <input type="file" accept=".docx" hidden onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = '';
                    setExDUploadLoading(true);
                    try {
                      const text = await extractDocxText(file);
                      if (!text || text.length < 50) throw new Error('Extracted text is too short — is the document empty?');
                      updateC({ pennyDrafts: { exDDraft: text, exDDraftAt: new Date().toISOString() } });
                      toast.success('Working draft updated from ' + file.name + ' (' + Math.round(text.length / 1000) + 'K chars)');
                    } catch (err) { toast.error('Upload failed: ' + err.message); }
                    finally { setExDUploadLoading(false); }
                  }} />
                </label>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-500)' }}
                  onClick={() => { if (window.confirm('Clear the Exhibit D working draft? This cannot be undone.')) updateC({ pennyDrafts: { exDDraft: '', exDDraftAt: null } }); }}>
                  ✕ Clear
                </button>
              </div>
              {exDUploadLoading && <div className="loading-row"><div className="spin" /><span>Extracting text from docx…</span></div>}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--slate-400)', lineHeight: 1.5 }}>
              No working draft saved. Draft one above, or upload your own:
              <label style={{ marginLeft: 8, cursor: 'pointer', color: 'var(--gold-600)', fontWeight: 600, textDecoration: 'underline' }}>
                Upload .docx
                <input type="file" accept=".docx" hidden onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = '';
                  setExDUploadLoading(true);
                  try {
                    const text = await extractDocxText(file);
                    if (!text || text.length < 50) throw new Error('Extracted text is too short — is the document empty?');
                    updateC({ pennyDrafts: { exDDraft: text, exDDraftAt: new Date().toISOString() } });
                    toast.success('Working draft saved from ' + file.name);
                  } catch (err) { toast.error('Upload failed: ' + err.message); }
                  finally { setExDUploadLoading(false); }
                }} />
              </label>
              {exDUploadLoading && <div className="loading-row" style={{ marginTop: 6 }}><div className="spin" /><span>Extracting text from docx…</span></div>}
            </div>
          )}
        </div>

        {/* ── Upload Exhibit D + structured review ── */}
        <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--navy-700)', marginBottom: 8 }}>
            📝 Upload Exhibit D
          </div>
          <div style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 10, lineHeight: 1.5 }}>
            Upload a completed Exhibit D (from outside GoldenScope or with manual edits) — Penny will compare it against the agency baseline and flag issues, or you can accept it as-is below.
          </div>
          <UploadZone
            file={exDReviewFile}
            onFile={handleExDReviewUpload}
            title="Upload Exhibit D (PDF)"
            subtitle="Penny will review against the agency baseline and SCM requirements"
          />
          {exDReviewFile && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-gold btn-sm" disabled={exDReviewLoading} onClick={runExDReview}>
                {exDReviewLoading ? <><div className="spin spin-sm" /> Analyzing…</> : '✦ Review Exhibit D'}
              </button>
              <button className="btn btn-teal btn-sm" onClick={() => {
                updateC({ pennyDrafts: { exDFinal: {
                  source: 'user_uploaded', fileName: exDReviewFile.name, size: exDReviewFile.size,
                  base64: exDReviewFile.base64, uploadedAt: new Date().toISOString(),
                } } });
                toast.success('"' + exDReviewFile.name + '" set as the Final Version for Exhibit D — visible in Current Working Versions.');
              }} title="Use this document as-is, without running or waiting on Penny's review">
                ✓ Use As-Is — No Revision Needed
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setExDReviewFile(null); setExDReviewFindings([]); setExDReviewChecked({}); }}>
                Clear
              </button>
            </div>
          )}
          {exDReviewLoading && <LoadRow msg="Reading Exhibit D and identifying issues — usually 20-30 seconds, longer for large documents…" />}
          {exDReviewFindings.length > 0 && !exDReviewLoading && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-700)' }}>
                {exDReviewFindings.length} finding{exDReviewFindings.length === 1 ? '' : 's'} — select fixes to apply:
              </div>
              {exDReviewFindings.map(f => (
                <label key={f.id} style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 12px', borderRadius: 8,
                  border: '1px solid ' + (f.priority === 'high' ? 'var(--red-200)' : f.priority === 'medium' ? 'var(--orange-200)' : 'var(--slate-200)'),
                  background: f.priority === 'high' ? 'var(--red-50)' : f.priority === 'medium' ? 'var(--orange-50)' : 'var(--slate-50)',
                  cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={!!exDReviewChecked[f.id]}
                    onChange={() => setExDReviewChecked(c => ({ ...c, [f.id]: !c[f.id] }))}
                    style={{ marginTop: 2, width: 16, height: 16 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '1px 6px', borderRadius: 4,
                        background: f.priority === 'high' ? '#fca5a5' : f.priority === 'medium' ? '#fed7aa' : '#e2e8f0',
                        color: f.priority === 'high' ? '#991b1b' : f.priority === 'medium' ? '#9a3412' : '#475569',
                      }}>{f.priority}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--slate-400)' }}>{f.category}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--navy-800)', fontWeight: 600 }}>{f.issue}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--slate-600)', marginTop: 2 }}>Fix: {f.fix}</div>
                  </div>
                </label>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button className="btn btn-gold btn-sm" disabled={exDRewriteLoading || Object.values(exDReviewChecked).filter(Boolean).length === 0} onClick={runExDRewrite}>
                  {exDRewriteLoading ? <><div className="spin spin-sm" /> Rewriting…</> : '📄 Apply Fixes & Download Revised Exhibit D'}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* ── Exhibit D Status ── */}
        {(() => {
          const hasDraft = !!exDFinal;
          const hasReview = !!contract.pennyDrafts?.exhibitReviewD;
          const status = exDStatus;
          const s = EXHIBIT_STATUSES[status];
          return (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1px solid ' + s.border, background: s.bg, display: 'flex', alignItems: 'center', gap: 12 }}>
              <ExhibitStatusBadge status={status} />
              <span style={{ fontSize: 12, color: 'var(--slate-600)', flex: 1 }}>
                {status === 'draft' && (hasDraft ? 'Final Version set — mark ready for review' : 'No Final Version set yet')}
                {status === 'ready_for_review' && 'Marked ready — include in a review package on the Sharing tab'}
                {status === 'in_review' && 'Currently under review via Sharing tab'}
                {status === 'approved' && 'Approved'}
                {status === 'needs_revision' && 'Reviewer requested revisions — update and re-mark when ready'}
              </span>
              {status === 'draft' && hasDraft && (
                <button className="btn btn-gold btn-sm" onClick={() => { setExStatus('exDStatus', 'ready_for_review'); toast.success('Exhibit D marked Ready for Review'); }}>
                  {!hasReview ? '⚠ Mark Ready (no SCM review)' : '✓ Mark Ready for Review'}
                </button>
              )}
              {status === 'ready_for_review' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setExStatus('exDStatus', 'draft')}>↩ Back to Draft</button>
              )}
            </div>
          );
        })()}
      </CollapsibleCard>

      <CollapsibleCard
        id="finalVersions"
        title="📄 Current Working Versions"
        subtitle="The most current version of each document — from Penny's first draft to approved final. Status shows where each document is in the review lifecycle."
        collapsed={collapsed.finalVersions}
        onToggle={() => toggleCard('finalVersions')}
        badge={(() => {
          const statuses = ['A','B','D'].map(l => contract.pennyDrafts?.['ex' + l + 'Status'] || 'draft');
          const approved = statuses.filter(s => s === 'approved').length;
          const inReview = statuses.filter(s => s === 'in_review' || s === 'ready_for_review').length;
          if (approved > 0) return approved + ' approved';
          if (inReview > 0) return inReview + ' in review';
          const hasDraft = ['A','B','D'].filter(l => contract.pennyDrafts?.['ex' + l + 'Final']?.source).length;
          return hasDraft + '/3 drafted';
        })()}
      >
        {/* Fixed exhibit slots — A, B, D. Penny tab is source of truth, Assembly reads from here */}

        {/* ── Contractor Finalization ── */}
        <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 'var(--r-lg)', border: '1px solid var(--slate-200)', background: 'var(--slate-50)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: s215?.contractorName ? 8 : 0 }}>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)' }}>🏆 Finalize with Contractor Info</div>
              <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginTop: 2 }}>
                Regenerate all drafted exhibits with the awarded contractor's information — headers, representative table, and all placeholder text.
              </div>
            </div>
            <button className="btn btn-gold btn-sm" disabled={finalizing || !s215?.contractorName?.trim()}
              onClick={runContractorFinalize}
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              {finalizing ? <><div className="spin spin-sm" /> Finalizing…</> : '🏆 Finalize & Download'}
            </button>
          </div>
          {s215?.contractorName ? (
            <div style={{ fontSize: 11, color: 'var(--slate-500)', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <span><strong>Contractor:</strong> {s215.contractorName}</span>
              {s215.contractorContactName && <span><strong>Contact:</strong> {s215.contractorContactName}{s215.contractorContactTitle ? ' (' + s215.contractorContactTitle + ')' : ''}</span>}
              {s215.contractorAddress && <span><strong>Address:</strong> {s215.contractorAddress}{s215.contractorCity ? ', ' + s215.contractorCity : ''}</span>}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--orange-600)', marginTop: 6 }}>
              ⚠ Fill in the contractor's information on the <strong>STD 215 tab</strong> before finalizing.
            </div>
          )}

          {/* Results after running */}
          {finalizeResults && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {finalizeResults.map(r => (
                <div key={r.label} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 6,
                  background: r.status === 'downloaded' ? 'var(--green-50)' : r.status === 'flagged' ? 'var(--orange-50)' : 'var(--slate-100)',
                  border: '1px solid ' + (r.status === 'downloaded' ? 'var(--green-200)' : r.status === 'flagged' ? 'var(--orange-200)' : 'var(--slate-200)'),
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: r.status === 'downloaded' ? 'var(--green-700)' : r.status === 'flagged' ? 'var(--orange-700)' : 'var(--slate-500)', minWidth: 65 }}>
                    {r.status === 'downloaded' ? '✅' : r.status === 'flagged' ? '⚠️' : r.status === 'error' ? '❌' : '—'} Ex. {r.label}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--slate-600)', flex: 1 }}>{r.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: 'A', name: 'Scope of Work', pennyDraftText: sowDraftText, pennyReview: exAPennyReview, setPennyReview: setExAPennyReview },
            { label: 'B', name: 'Budget Detail & Payment Provisions', pennyDraftText: exBDraftText, pennyReview: exBPennyReview, setPennyReview: setExBPennyReview },
            { label: 'D', name: 'Special Terms and Conditions', pennyDraftText: exDDraftText, pennyReview: exDPennyReview, setPennyReview: setExDPennyReview },
          ].map(ex => {
            const pennyFinal = contract.pennyDrafts?.['ex' + ex.label + 'Final'];
            const hasFinal = !!pennyFinal?.source;
            const hasPennyDraft = !!ex.pennyDraftText;
            const sourceLabels = { penny_draft: 'Penny Draft', user_uploaded: 'User Uploaded', legal_revised: 'Legal Revised', exec_approved: 'Executive Approved' };
            const exStatus = contract.pennyDrafts?.['ex' + ex.label + 'Status'] || 'draft';
            const SLOT_STYLES = {
              draft:            { border: 'var(--slate-200)',  bg: 'var(--slate-50)'   },
              ready_for_review: { border: 'var(--blue-200)',   bg: 'var(--blue-50)'    },
              in_review:        { border: 'var(--purple-200)', bg: 'var(--purple-50)'  },
              approved:         { border: 'var(--green-200)',  bg: 'var(--green-50)'   },
              needs_revision:   { border: 'var(--orange-200)', bg: 'var(--orange-50)'  },
            };
            const slotStyle = SLOT_STYLES[exStatus] || SLOT_STYLES.draft;
            return (
              <div key={ex.label} style={{ padding: '12px 14px', borderRadius: 'var(--r-lg)', border: '1.5px solid ' + slotStyle.border, background: slotStyle.bg }}>
                {/* Row header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasFinal ? 8 : 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: hasFinal ? 'var(--green-700)' : 'var(--slate-600)', minWidth: 72 }}>Exhibit {ex.label}</span>
                    <span style={{ fontSize: 12, color: 'var(--slate-500)' }}>{ex.name}</span>
                    {hasFinal && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--green-100)', color: 'var(--green-700)', border: '1px solid var(--green-200)', whiteSpace: 'nowrap' }}>
                        {sourceLabels[pennyFinal.source] || pennyFinal.source}
                      </span>
                    )}
                    <ExhibitStatusBadge status={contract.pennyDrafts?.['ex' + ex.label + 'Status'] || 'draft'} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--slate-400)', cursor: 'pointer', flexShrink: 0 }}>
                    <input type="checkbox" checked={ex.pennyReview} onChange={e => ex.setPennyReview(e.target.checked)} />
                    Penny Review
                  </label>
                </div>
                {/* File info */}
                {hasFinal && (
                  <div style={{ fontSize: 12, color: 'var(--green-600)', marginBottom: 8 }}>
                    {pennyFinal.fileName || 'Penny Draft'}{pennyFinal.uploadedAt ? ' · ' + new Date(pennyFinal.uploadedAt).toLocaleDateString() : ''}
                    {pennyFinal.storagePath && <span style={{ fontSize: 10, color: 'var(--teal-600)', marginLeft: 8 }}>☁ Stored</span>}
                  </div>
                )}
                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {hasPennyDraft && !hasFinal && (
                    <button className="btn btn-gold btn-sm"
                      onClick={() => updateC({ pennyDrafts: { ['ex' + ex.label + 'Final']: { source: 'penny_draft', content: ex.pennyDraftText, uploadedAt: new Date().toISOString() } } })}>
                      ✒ Use Penny Draft
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    const inp = document.createElement('input');
                    inp.type = 'file'; inp.accept = '.pdf';
                    inp.onchange = e => { if (e.target.files[0]) uploadExhibitFinal(e.target.files[0], (v) => updateC({ pennyDrafts: { ['ex' + ex.label + 'Final']: v } }), 'user_uploaded'); };
                    inp.click();
                  }}>
                    {hasFinal ? '↑ Replace' : '↑ Upload Final PDF'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDocPickerFor(docPickerFor === ex.label ? null : ex.label)}>
                    📎 Select from Documents
                  </button>
                  {hasFinal && (pennyFinal.storagePath || pennyFinal.base64) && (
                    <button className="btn btn-ghost btn-sm" onClick={() => downloadExhibitFinal(pennyFinal)}>↓ Download</button>
                  )}
                  {hasFinal && (
                    <select value={pennyFinal.source || ''}
                      onChange={e => updateC({ pennyDrafts: { ['ex' + ex.label + 'Final']: { ...pennyFinal, source: e.target.value } } })}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--slate-200)', fontSize: 11, fontFamily: 'inherit', color: 'var(--slate-600)', background: '#fff' }}>
                      <option value="penny_draft">Penny Draft</option>
                      <option value="user_uploaded">User Uploaded</option>
                      <option value="legal_revised">Legal Revised</option>
                      <option value="exec_approved">Executive Approved</option>
                    </select>
                  )}
                  {hasFinal && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-400)' }}
                      onClick={() => updateC({ pennyDrafts: { ['ex' + ex.label + 'Final']: null } })}>
                      ✕ Clear
                    </button>
                  )}
                </div>
                {docPickerFor === ex.label && (() => {
                  const pickable = getPickableDocuments();
                  return (
                    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)', background: '#fff' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', marginBottom: 8 }}>
                        Select a document already on this contract to use as Exhibit {ex.label}'s Final Version:
                      </div>
                      {pickable.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--slate-400)' }}>No documents with readable content found in Documents or Reference Docs yet.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                          {pickable.map(doc => (
                            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 6, fontSize: 12 }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--slate-50)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <span style={{ color: 'var(--slate-700)' }}>{doc.name} <span style={{ color: 'var(--slate-400)', fontSize: 10.5 }}>({doc.source === 'referenceDocs' ? 'Reference Docs' : 'Documents'})</span></span>
                              <button className="btn btn-gold btn-sm" onClick={() => promotePickedDocument(ex.label, doc)}>Use This</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* ── Inline SCM Compliance Review ── */}
        {(() => {
          const pennyFinalD = contract.pennyDrafts?.['exDFinal'];
          const reviewResultD = contract.pennyDrafts?.['exhibitReviewD'] || '';
          const isLoadingD = !!exhibitReviewLoading['D'];
          const hasPdfD = pennyFinalD?.source === 'user_uploaded' || pennyFinalD?.source === 'legal_revised' || pennyFinalD?.source === 'exec_approved';
          const hasDraftD = pennyFinalD?.source === 'penny_draft' && pennyFinalD?.content;
          let docSourceD = null;
          if (hasPdfD && (pennyFinalD.storagePath || pennyFinalD.base64)) docSourceD = { type: 'pdf', base64: pennyFinalD.base64 };
          else if (hasDraftD) docSourceD = { type: 'text', content: pennyFinalD.content };
          
          const statusMatchD = reviewResultD.match(/OVERALL STATUS:\s*([^\n]+)/);
          const statusD = statusMatchD ? statusMatchD[1].trim() : null;
          const statusColorD = statusD?.includes('✅') ? 'var(--green-600)' : statusD?.includes('⚠') ? 'var(--orange-600)' : statusD?.includes('❌') ? 'var(--red-600)' : 'var(--slate-400)';
          return (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-700)' }}>✦ SCM Compliance Review — Exhibit D</span>
                  {statusD && <span style={{ fontSize: 11, fontWeight: 700, color: statusColorD }}>{statusD}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-gold btn-sm" disabled={isLoadingD}
                    onClick={() => runExhibitReview('D', 'Special Terms and Conditions', docSourceD)}>
                    {isLoadingD ? <><div className="spin spin-sm" /> Reviewing…</> : reviewResultD ? '↺ Re-review' : '✦ Review'}
                  </button>
                  {reviewResultD && <button className="btn btn-ghost btn-xs" onClick={() => updateC({ pennyDrafts: { ['exhibitReviewD']: '' } })}>Clear</button>}
                </div>
              </div>
              {isLoadingD && <LoadRow msg="Penny is reviewing Exhibit D against SCM requirements…" />}
              {reviewResultD && !isLoadingD && (
                <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (statusD?.includes('✅') ? 'var(--green-200)' : statusD?.includes('❌') ? 'var(--red-200)' : 'var(--orange-200)'), background: statusD?.includes('✅') ? 'var(--green-50)' : statusD?.includes('❌') ? 'var(--red-50)' : 'var(--orange-50)', fontSize: 12.5, lineHeight: 1.7, color: 'var(--slate-700)', fontFamily: 'inherit' }} dangerouslySetInnerHTML={{ __html: renderMd(reviewResultD) }}>
                </div>
              )}
            </div>
          );
        })()}
      </CollapsibleCard>

      {/* ── CalEProcure Posting Summary ─────────────────────────── */}
      {contract.showCalEProcure && (
        <CalEProcurePanel contract={contract} updateC={updateC} profile={profile} s215={s215} apiKey={apiKey} callAI={callAI} toast={toast} fmtMoney={fmtMoney} />
      )}




      {/* ── Complete Contract Review ─────────────────────────── */}
      {(() => {
        const preReviewLocked = !exAFinal && !exBFinal && !exDFinal;
        return (
      <CollapsibleCard id="completeReview" title="✦ Pre-Review Package Check · Penny" subtitle="Run before submitting to Legal or Supervisor. Penny checks completeness, compliance gaps, and readiness." collapsed={collapsed.completeReview} onToggle={() => !preReviewLocked && toggleCard('completeReview')} style={{ opacity: preReviewLocked ? 0.45 : 1, pointerEvents: preReviewLocked ? 'none' : 'auto', borderLeft: '3px solid var(--gold-400)', transition: 'opacity .2s' }} titleStyle={{}}>
        {preReviewLocked && (
          <div style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--slate-50)', border: '1px solid var(--slate-200)', fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 12 }}>
            Set a Final Version on at least one exhibit to enable the Pre-Review Package Check.
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h3 className="card-title" style={{ color: 'var(--gold-400)', marginBottom: 4 }}>✦ Complete Contract Review · Penny</h3>
            <p style={{ fontSize: 12.5, color: 'var(--navy-300)', lineHeight: 1.6 }}>
              Penny reads the current working versions of all exhibits together and checks readiness before you submit to Legal or Supervisor.
            </p>
          </div>
        </div>

        {/* Full package status summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Exhibit A — SOW', final: exAFinal },
            { label: 'Exhibit B — Budget', final: exBFinal },
            { label: 'Exhibit D — Special Terms', final: exDFinal },
          ].map(({ label, final }) => (
            <div key={label} style={{ padding: '8px 12px', borderRadius: 8, background: final ? 'rgba(34,197,94,.1)' : 'rgba(255,255,255,.05)', border: '1px solid ' + (final ? 'rgba(34,197,94,.3)' : 'rgba(255,255,255,.1)') }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: final ? 'var(--green-400)' : 'var(--navy-400)' }}>
                {final ? '✅' : '⬜'} {label}
              </div>
              {final && <div style={{ fontSize: 10, color: 'var(--navy-400)', marginTop: 2 }}>{final.fileName || 'Penny Draft'}</div>}
            </div>
          ))}
        </div>
        {/* Attachment summary */}
        {(() => {
          const nestedCount = (contract.exhibits || []).reduce((sum, e) => sum + (e.attachments || []).length, 0);
          const generalCount = (contract.general_attachments || []).length;
          const uploadedNested = (contract.exhibits || []).reduce((sum, e) => sum + (e.attachments || []).filter(a => a.base64 || a.storagePath).length, 0);
          const uploadedGeneral = (contract.general_attachments || []).filter(a => a.base64 || a.storagePath).length;
          if (nestedCount === 0 && generalCount === 0) return null;
          return (
            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', fontSize: 12, color: 'var(--navy-300)' }}>
              <div style={{ fontWeight: 700, color: 'var(--navy-200)', marginBottom: 4 }}>📎 Attachments</div>
              {nestedCount > 0 && <div>{uploadedNested}/{nestedCount} nested exhibit attachments uploaded</div>}
              {generalCount > 0 && <div>{uploadedGeneral}/{generalCount} general attachments uploaded</div>}
            </div>
          );
        })()}

        {(exAFinal || exBFinal || exDFinal) && (
          <div>
            <button
              className="btn btn-gold"
              disabled={contractReviewLoading}
              onClick={async () => {
                setContractReviewLoading(true);
                try {
                  const scmBrief = await getScmContext();

                  // Build full package summary — exhibits, nested attachments, general attachments
                  const exhibits = contract.exhibits || [];
                  const generalAtts = contract.general_attachments || [];

                  const packageParts = [];

                  // Exhibits with their content and nested attachments
                  for (const ex of exhibits.filter(e => !e.referenceOnly)) {
                    const final = contract.pennyDrafts?.['ex' + ex.label + 'Final'];
                    const draftTxt = ex.label === 'A' ? sowDraftText : null;
                    let content_text = '';
                    if (final?.content) content_text = final.content.slice(0, 2000);
                    else if (draftTxt) content_text = draftTxt.slice(0, 2000);
                    else if (final?.fileName) content_text = '[Uploaded PDF: ' + final.fileName + ']';
                    else content_text = '[No content set]';
                    packageParts.push('EXHIBIT ' + ex.label + ' — ' + ex.name + ':\n' + content_text);
                    // Nested attachments
                    for (const att of (ex.attachments || [])) {
                      if (att.name || att.base64) {
                        packageParts.push('  → Attachment: ' + (att.name || 'Unnamed') + (att.base64 ? ' [PDF uploaded]' : ' [no file]'));
                      }
                    }
                  }

                  // General attachments
                  if (generalAtts.length > 0) {
                    packageParts.push('GENERAL ATTACHMENTS:');
                    generalAtts.forEach((a, i) => {
                      packageParts.push('  ' + (i+1) + '. ' + (a.name || 'Unnamed') + (a.base64 ? ' [PDF uploaded]' : ' [no file]'));
                    });
                  }

                  const packageSummary = packageParts.join('\n\n---\n\n');

                  const system = [
                    'You are Penny doing a PRE-REVIEW readiness check for a California state contract package before it is submitted to Legal or Supervisor review.',
                    'Your job: identify everything that a legal reviewer or supervisor is likely to flag so the analyst can fix it BEFORE sending.',
                    'Check for:',
                    '1. Cross-exhibit consistency — do exhibits reference each other correctly?',
                    '2. Conflicting language — contradictions between exhibits?',
                    '3. Missing cross-references — terms referenced but not defined?',
                    '4. Attachment completeness — all referenced attachments present?',
                    '5. SCM compliance as a complete package',
                    '6. Scope clarity — is the SOW specific enough for a reviewer to evaluate?',
                    '7. Budget alignment — does Exhibit B match the stated contract maximum?',
                    '',
                    'Structure your review:',
                    'PRE-REVIEW READINESS REPORT',
                    '',
                    'OVERALL READINESS: [Ready to Submit / Needs Work / Not Ready]',
                    '',
                    'LIKELY REVIEWER FLAGS (what Legal or Supervisor will catch):',
                    'Number each. Be specific.',
                    '',
                    'COMPLIANCE GAPS:',
                    'Number each. Cite SCM sections.',
                    '',
                    'CROSS-EXHIBIT ISSUES:',
                    'Number each.',
                    '',
                    'RECOMMENDED FIXES before submitting:',
                    'Prioritized action list.',
                    '',
                    'SCM Brief: ' + (scmBrief || 'Apply standard California SCM requirements.'),
                    'Contract: ' + contract.title + ' | Method: ' + (contract.procMethod || 'to be determined') + ' | Budget: $' + (contract.budget || 'not set') + ' | ' + (contract.isIT === 'yes' ? 'IT — SCM Vol 2' : 'Non-IT — SCM Vol 1'),
                  ].join('\n');

                  const text = await callAI(apiKey, {
                    system,
                    messages: [{ role: 'user', content: [...getContractRefDocs(), ...getGeneralAttachmentDocs(), { type: 'text', text: 'Please review this complete contract package:\n\n' + packageSummary }] }],
                    tag: 'pre_review_check',
                    contract_id: contract.id,
                    max_tokens: 8000,
                  });
                  setContractReview(text);
                  toast.success('Contract Health Report complete');
                } catch (e) { toast.error('Review failed: ' + e.message); }
                finally { setContractReviewLoading(false); }
              }}
            >
              {contractReviewLoading ? <><div className="spin spin-sm" /> Running Complete Review…</> : '✦ Run Pre-Review Check'}
            </button>
            {contractReviewLoading && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--navy-300)', fontStyle: 'italic' }}>Penny is checking readiness — about 30 seconds…</div>}

            {contractReview && !contractReviewLoading && (
              <div style={{ marginTop: 16, padding: '20px 24px', background: 'var(--white)', borderRadius: 'var(--r-lg)', border: '1px solid var(--slate-200)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold-600)', marginBottom: 12 }}>✦ Contract Health Report</div>
                <div style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--navy-800)' }} dangerouslySetInnerHTML={{ __html: renderMd(contractReview) }} />
                <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                  <button className="btn btn-gold btn-sm" onClick={() => { navigator.clipboard.writeText(contractReview); toast.success('Copied to clipboard'); }}>📋 Copy Report</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => saveDoc('contractReview', contractReview, 'draft')}>💾 Save to Vault</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--slate-400)' }} onClick={() => setContractReview('')}>Clear</button>
                </div>
              </div>
            )}
          </div>
        )}
      </CollapsibleCard>
        );
      })()}

      {/* ── Post-Review Final Package Check ─────────────────────── */}
      {(() => {
        const hasAnyApproval = [exAStatus, exBStatus, exDStatus, solStatus].some(s => s === 'approved');
        return (
          <CollapsibleCard
            id="postReview"
            title="✦ Post-Review Final Package Check · Penny"
            subtitle="Run after approvals and revisions are complete. Penny verifies the final package is consistent and nothing was broken during the review cycle."
            collapsed={collapsed.postReview !== false}
            onToggle={() => !(!hasAnyApproval) && toggleCard('postReview')}
            style={{ opacity: !hasAnyApproval ? 0.45 : 1, pointerEvents: !hasAnyApproval ? 'none' : 'auto', borderLeft: '3px solid var(--gold-400)', transition: 'opacity .2s' }}
            titleStyle={{}}
          >
            {!hasAnyApproval && (
              <div style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--slate-50)', border: '1px solid var(--slate-200)', fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 12 }}>
                At least one exhibit must be marked Approved before running the Post-Review Final Package Check.
              </div>
            )}
            {hasAnyApproval && (
              <div>
                {/* Status summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'Exhibit A', status: exAStatus },
                    { label: 'Exhibit B', status: exBStatus },
                    { label: 'Exhibit D', status: exDStatus },
                    { label: 'Solicitation', status: solStatus },
                  ].map(({ label, status }) => {
                    const s = EXHIBIT_STATUSES[status];
                    return (
                      <div key={label} style={{ padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.1)' }}>
                        <div style={{ fontSize: 10, color: 'var(--navy-400)', marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: status === 'approved' ? 'var(--green-400)' : status === 'needs_revision' ? 'var(--orange-400)' : 'var(--navy-300)' }}>
                          {s.icon} {s.label}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  className="btn btn-gold"
                  disabled={postReviewLoading}
                  onClick={async () => {
                    setPostReviewLoading(true);
                    try {
                      const scmBrief = await getScmContext();
                      const exhibits = contract.exhibits || [];
                      const generalAtts = contract.general_attachments || [];
                      const packageParts = [];
                      for (const ex of exhibits.filter(e => !e.referenceOnly)) {
                        const final = contract.pennyDrafts?.['ex' + ex.label + 'Final'];
                        const statusKey = 'ex' + ex.label + 'Status';
                        const exStatus = contract.pennyDrafts?.[statusKey] || 'draft';
                        let contentText = '';
                        if (final?.content) contentText = final.content.slice(0, 2000);
                        else if (final?.fileName) contentText = '[Uploaded PDF: ' + final.fileName + ']';
                        else contentText = '[No content set]';
                        packageParts.push('EXHIBIT ' + ex.label + ' — ' + ex.name + ' [STATUS: ' + (EXHIBIT_STATUSES[exStatus]?.label || exStatus) + ']:\n' + contentText);
                        for (const att of (ex.attachments || [])) {
                          if (att.name) packageParts.push('  Attachment: ' + att.name + (att.base64 ? ' [uploaded]' : ' [no file]'));
                        }
                      }
                      if (generalAtts.length > 0) {
                        packageParts.push('GENERAL ATTACHMENTS: ' + generalAtts.map((a, i) => (i+1) + '. ' + (a.name || 'Unnamed')).join(', '));
                      }
                      const packageSummary = packageParts.join('\n\n---\n\n');
                      const system = [
                        'You are Penny doing a POST-REVIEW final package check for a California state contract.',
                        'The contract has gone through Legal/Supervisor review. Revisions may have been made.',
                        'Your job: verify the final package is internally consistent and nothing was broken during revisions.',
                        'Check for:',
                        '1. Internal consistency — do all approved exhibits still align with each other after revisions?',
                        '2. New contradictions introduced during revision cycle',
                        '3. Budget figures consistent across Exhibit A scope and Exhibit B?',
                        '4. Special terms in Exhibit D consistent with scope in Exhibit A?',
                        '5. Any exhibits still in Needs Revision status that must be resolved',
                        '6. Final clearance — is this package ready for execution?',
                        '',
                        'Structure your review:',
                        'POST-REVIEW FINAL PACKAGE CHECK',
                        '',
                        'FINAL CLEARANCE: [✅ Clear for Execution / ⚠ Issues Found / ❌ Not Ready]',
                        '',
                        'POST-REVISION CONSISTENCY CHECK:',
                        'Did revisions introduce any new issues?',
                        '',
                        'REMAINING ISSUES (if any):',
                        'List anything still unresolved.',
                        '',
                        'FINAL VERDICT:',
                        'Clear summary of whether the package is ready for signature and execution.',
                        '',
                        'SCM Brief: ' + (scmBrief || 'Apply standard California SCM requirements.'),
                        'Contract: ' + contract.title + ' | Method: ' + (contract.procMethod || 'to be determined') + ' | Budget: $' + (contract.budget || 'not set'),
                      ].join('\n');
                      const text = await callAI(apiKey, {
                        system,
                        messages: [{ role: 'user', content: [...getContractRefDocs(), ...getGeneralAttachmentDocs(), { type: 'text', text: 'Please run the post-review final package check:\n\n' + packageSummary }] }],
                        tag: 'post_review_check',
                        contract_id: contract.id,
                        max_tokens: 8000,
                      });
                      setPostReview(text);
                      toast.success('Post-Review Check complete');
                    } catch (e) { toast.error('Check failed: ' + e.message); }
                    finally { setPostReviewLoading(false); }
                  }}
                >
                  {postReviewLoading ? <><div className="spin spin-sm" /> Running Post-Review Check…</> : postReview ? '↺ Re-run Post-Review Check' : '✦ Run Post-Review Final Check'}
                </button>
                {postReviewLoading && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--navy-300)', fontStyle: 'italic' }}>Penny is verifying the final package — about 30 seconds…</div>}

                {postReview && !postReviewLoading && (
                  <div style={{ marginTop: 16, padding: '20px 24px', background: 'var(--white)', borderRadius: 'var(--r-lg)', border: '1px solid var(--slate-200)' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--green-600)', marginBottom: 12 }}>✦ Post-Review Final Package Check</div>
                    <div style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--navy-800)' }} dangerouslySetInnerHTML={{ __html: renderMd(postReview) }} />
                    <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                      <button className="btn btn-gold btn-sm" onClick={() => { navigator.clipboard.writeText(postReview); toast.success('Copied'); }}>📋 Copy Report</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--slate-400)' }} onClick={() => setPostReview('')}>Clear</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CollapsibleCard>
        );
      })()}
    </div>
  );
}

// ── FinalVersionZone component ────────────────────────────────────────────────
function FinalVersionZone({ label, final, setFinal, pennyReview, setPennyReview, pennyDraftText, uploadFn, downloadExhibitFinal, toast, updateC, contract }) {
  const fileRef = React.useRef(null);
  const hasFinal = !!final?.source;

  const sourceLabels = {
    penny_draft: 'Penny Draft', user_uploaded: 'User Uploaded',
    legal_revised: 'Legal Revised', exec_approved: 'Executive Approved',
  };

  return (
    <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 'var(--r-lg)', border: '1.5px solid ' + (hasFinal ? 'var(--green-200)' : 'var(--slate-200)'), background: hasFinal ? 'var(--green-50)' : 'var(--slate-50)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: hasFinal ? 10 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: hasFinal ? 'var(--green-700)' : 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            {hasFinal ? '✅ Final Version Set' : '📄 Final Version — Not Set'}
          </div>
          {hasFinal && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--green-100)', color: 'var(--green-700)', border: '1px solid var(--green-200)' }}>
              {sourceLabels[final.source] || final.source}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--slate-500)', cursor: 'pointer' }}>
            <input type="checkbox" checked={pennyReview} onChange={e => setPennyReview(e.target.checked)} />
            Penny Review
          </label>
        </div>
      </div>

      {hasFinal ? (
        <div>
          <div style={{ fontSize: 12, color: 'var(--green-600)', marginBottom: 10 }}>
            {final.fileName || 'Penny Draft'} {final.uploadedAt ? '· ' + new Date(final.uploadedAt).toLocaleDateString() : ''}
            {final.storagePath && <span style={{ fontSize: 10, color: 'var(--teal-600)', marginLeft: 8 }}>☁ Stored</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(final.storagePath || final.base64) && (
              <button className="btn btn-ghost btn-xs" onClick={() => downloadExhibitFinal && downloadExhibitFinal(final)}>↓ Download</button>
            )}
            <button className="btn btn-ghost btn-xs" onClick={() => fileRef.current?.click()}>↑ Replace</button>
            <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red-400)' }} onClick={() => setFinal(null)}>✕ Clear Final</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {pennyDraftText && (
            <button className="btn btn-gold btn-sm"
              onClick={() => { setFinal({ source: 'penny_draft', content: pennyDraftText, uploadedAt: new Date().toISOString() }); toast.success('Exhibit ' + label + ' Penny draft set as Final'); }}>
              ✒ Use Penny Draft as Final
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>
            ↑ Upload Final Version
          </button>
          <select
            onChange={e => { if (e.target.value && final) setFinal({ ...final, source: e.target.value }); }}
            defaultValue=""
            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--slate-200)', fontSize: 12, fontFamily: 'inherit', color: 'var(--slate-600)' }}>
            <option value="" disabled>Tag source…</option>
            <option value="user_uploaded">User Uploaded</option>
            <option value="legal_revised">Legal Revised</option>
            <option value="exec_approved">Executive Approved</option>
          </select>
        </div>
      )}
      <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) uploadFn(e.target.files[0], setFinal, 'user_uploaded'); e.target.value = ''; }} />
    </div>
  );
}
